import React from 'react';
import ReactMarkdown from 'react-markdown';
import format from 'date-fns/format';
import { defaultRenderHrefLink, defaultRenderDatabaseLink } from './routing';
import { renderFieldWithLinks } from 'utils/linkifyText';
import { formatEntityLensCode } from 'utils/entityLensBadge';
import type { RenderHrefLink, RenderDatabaseLink } from './routing';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GlobalContext } from '../../providers/GlobalProvider';
import {
  Home,
  ChevronRight,
  ChevronDown,
  Package,
  Globe2,
  ShieldAlert,
  Flag,
  Percent,
  Building2,
  Factory,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  RotateCw,
  X,
  Sparkles,
  Search,
  Upload,
  Download,
  History,
  ExternalLink,
  MessageSquare,
  PlayCircle,
  PencilLine,
  Filter,
  RefreshCw,
  ArrowUp,
  Layers,
  Scale,
  DollarSign,
  AlertCircle,
  Info,
  Award,
  Plus,
  Truck,
  Trash2,
  Lock,
} from './icons';
import {
  IMPORT_PRODUCTS,
  IMPORT_DATABASES,
  IMPORT_ENTITIES,
  getImportEntity,
} from './lib/import-data';
import { getEntriesForProduct } from './lib/import-data';
import { EntryStatusPill } from './primitives';
import type { ImportProduct } from './lib/import-types';
import {
  buildProductDetail,
  type ProductDetail,
  type BiteLensGroup,
  type Severity,
} from './lib/import-detail';
import {
  fetchImportProductById,
  fetchImportProductScreening,
  fetchImportProductScreeningHistory,
  fetchImportCommodityScreening,
  fetchImportPgaFlags,
  upsertImportPgaFlag,
  deleteImportPgaFlag,
  runImportProductScreening,
  pollImportScreeningStatus,
  applyRealProductDetail,
  toImportProduct,
  fetchImportProductDocuments,
  uploadImportProductDocument,
  downloadImportProductDocument,
  fetchImportProductComments,
  addImportProductComment,
  deleteImportProductComment,
  fetchImportProductTariffPreview,
  fetchImportProductTariffHistory,
  fetchImportProductHtsClassification,
  feeFreeTotalCost,
  fetchImportProductHtsRecommendations,
  fetchImportProductCrossRulings,
  fetchImportProductCategories,
  createImportProductCategory,
  fetchImportDatabaseScreeningStatus,
  fetchImportHtsOptions,
  saveImportProductDetails,
  updateImportProductComplianceFlag,
  IMPORT_HIERARCHY_PARENT_TYPES,
  buildAdcvdDetail,
} from './lib/import-service';
import { isHiddenAdditionalColumn } from './lib/additional-columns';
import type {
  ImportServiceProduct,
  ImportServiceScreeningResult,
  ImportServiceScreeningHistory,
  ImportServiceScreeningHistoryEntry,
  ImportServiceAuditEvent,
  CommodityLensGroups,
  ImportServiceDocument,
  ImportServiceComment,
  ImportTariffExplanationContext,
  ImportTariffHistoryEntry,
  ImportHtsClassification,
  ImportProductCategory,
  ImportHtsOption,
  ImportProductDetailsUpdate,
  ImportProductSource,
  ImportParentCandidate,
} from './lib/import-service';
import {
  ImportParentProductPicker,
  formatParentCandidate,
} from './ImportParentProductPicker';
import { runAIExplanationOnly } from '../../api/PyAPIConnect';
import {
  importProductTicketPDF,
  type IImportProductTicketReportData,
} from '../../components/Common/EntityTicketPdf';
import { toast } from './lib/toast';
import { useImportHubReadOnly } from './lib/access';
import { formatCurrency, formatDate } from './lib/store';
import { Button, cx as cn } from './ui';
import { ClassificationLink } from './ClassificationLink';
import {
  IMPORT_CATEGORY_DEFAULTS,
  ImportCategorySelect,
} from './ImportCategorySelect';
import {
  getAppliedFlags,
  addAppliedFlag,
  updateAppliedFlag,
  removeAppliedFlag,
  addFlagDocument,
  removeFlagDocument,
  setAppliedFlags,
  type AppliedPgaFlag,
} from './lib/pga-applied';
import {
  resolvePgaCode,
  PGA_FLAG_SCHEMAS,
  type PgaCode,
} from './lib/pga-schema';
import { AppliedFlagsSection } from './pga/AppliedFlagsSection';
import { PgaFlagEditorDrawer } from './pga/PgaFlagEditorDrawer';
import {
  PgaFlagCreateDialog,
  type PgaHitOption,
} from './pga/PgaFlagCreateDialog';
import { buildPgaHitId } from './lib/pga-hit-prefill';

import type { ImportEntity } from './lib/import-types';
import { countries } from '../../utils/countriesWithCodes';

export type ImportProductDetailView =
  | 'overview'
  | 'documents'
  | 'comments'
  | 'history';

export interface ImportProductDetailPageProps {
  productId: string;
  initialView?: ImportProductDetailView;
  onViewChange?: (view: ImportProductDetailView) => void;
  /** How to render the "Import Hub" link (breadcrumb + not-found fallback). */
  renderHubLink?: RenderHrefLink;
  /** How to render the database breadcrumb link. */
  renderDatabaseLink?: RenderDatabaseLink;
  /** Href used by the default hub link. Defaults to "/import". */
  hubHref?: string;
}

interface ProductEditDraft {
  name: string;
  sku: string;
  hierarchyType: string;
  parentProductId: string;
  parentSku: string;
  parentName: string;
  parentHierarchyType: string;
  category: string;
  manufacturer: string;
  vendor: string;
  htsCode: string;
  countryOfOrigin: string;
  quantity: string;
  unitOfMeasure: string;
  description: string;
  traces: string;
  specifications: string;
  additionalHtsCodes: AdditionalHtsCodeDraft[];
  additionalColumns: AdditionalColumnDraft[];
  declaredValue: string;
  smeltCountry: string;
  transMethod: string;
  copperPct: string;
  copperValue: string;
  aluminumPct: string;
  aluminumValue: string;
  steelPct: string;
  steelValue: string;
  entryDate: string;
  loadingDate: string;
}

interface AdditionalHtsCodeDraft {
  id?: string;
  draftKey: string;
  htsCode: string;
  countryOfOrigin: string;
  validatedHtsCode: string;
}

interface AdditionalColumnDraft {
  id: string;
  key: string;
  value: string;
}

type ProductEditStringKey = {
  [Key in keyof ProductEditDraft]: ProductEditDraft[Key] extends string
    ? Key
    : never;
}[keyof ProductEditDraft];

const PRODUCT_DETAIL_DRAFT_KEYS: Array<keyof ProductEditDraft> = [
  'name',
  'sku',
  'hierarchyType',
  'parentProductId',
  'parentSku',
  'parentName',
  'parentHierarchyType',
  'category',
  'manufacturer',
  'vendor',
  'htsCode',
  'countryOfOrigin',
  'quantity',
  'unitOfMeasure',
  'description',
  'traces',
  'specifications',
  'additionalHtsCodes',
  'additionalColumns',
];

const TARIFF_INPUT_DRAFT_KEYS: Array<keyof ProductEditDraft> = [
  'declaredValue',
  'smeltCountry',
  'transMethod',
  'copperPct',
  'copperValue',
  'aluminumPct',
  'aluminumValue',
  'steelPct',
  'steelValue',
  'entryDate',
  'loadingDate',
];

// Sticky .Top-Header is 64px; 80px leaves a little breathing room above the target.
const scrollToAnchor = (anchor?: string): void => {
  if (!anchor) return;
  const el = document.getElementById(anchor);
  if (!el) return;
  window.scrollTo({
    top: el.getBoundingClientRect().top + window.scrollY - 80,
    behavior: 'smooth',
  });
};

const toDateInputValue = (value: unknown): string => {
  if (!value) return '';
  const normalized = String(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : '';
};

// YYYY-MM-DD in the user's local timezone. `toISOString()` is UTC, which
// rolls over to tomorrow during US evenings.
// A missing loading date defaults to today, but never later than the entry
// date: loading after entry is rejected on save and flagged by the tariff
// calculation. Both are YYYY-MM-DD, so string comparison is chronological.
const defaultLoadingDate = (entryDate: string, today: string): string =>
  entryDate && entryDate < today ? entryDate : today;

const localDateInputValue = (date: Date = new Date()): string =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');

const normalizeCountryCode = (value: string | null | undefined): string => {
  const normalized = (value ?? '').trim();
  const match = countries.find(
    ({ iso2, name }) =>
      iso2.toLowerCase() === normalized.toLowerCase() ||
      name.toLowerCase() === normalized.toLowerCase(),
  );
  return match?.iso2 ?? '';
};

const formatHtsPretty = (value: string | null | undefined): string => {
  const digits = String(value ?? '')
    .replace(/\D/g, '')
    .slice(0, 10);
  return [
    digits.slice(0, 4),
    digits.slice(4, 6),
    digits.slice(6, 8),
    digits.slice(8, 10),
  ]
    .filter(Boolean)
    .join('.');
};

// Legacy EditProduct renders the persisted material values directly rather
// than passing them through a whole-dollar currency formatter. Preserve every
// stored decimal digit here and retain its empty-value behavior.
const formatMaterialValue = (value: number): string =>
  value ? String(value) : 'N/A';

// The tariff calculator treats Ocean specially (HMF, "on the water" rules);
// a blank method is screened as Ocean.
const TRANSPORT_METHOD_OPTIONS = ['Ocean', 'Air', 'Truck', 'Rail'].map(
  (value) => ({ value, label: value }),
);

const EDIT_INPUT_CLASS =
  'w-full h-8 rounded-md border border-input bg-background px-2 text-xs';


const productEditDraftFrom = (
  product: ImportProduct,
  detail: ProductDetail,
  row: ImportServiceProduct | null,
): ProductEditDraft => ({
  name: product.name,
  sku: product.sku,
  hierarchyType: row?.hierarchy_type ?? detail.tariffInputs.hierarchyType ?? '',
  parentProductId: row?.parent_product_id ?? '',
  parentSku: row?.parent_model_number ?? '',
  parentName: row?.parent_name ?? '',
  parentHierarchyType: row?.parent_hierarchy_type ?? '',
  category: row?.category ?? detail.tariffInputs.category ?? '',
  manufacturer: row?.manufacturer ?? detail.tariffInputs.manufacturer ?? '',
  vendor: row?.vendor ?? detail.tariffInputs.vendor ?? '',
  htsCode: String(row?.hts_code ?? product.htsCode ?? '').replace(/\D/g, ''),
  countryOfOrigin: normalizeCountryCode(
    row?.country_of_origin ?? product.countryOfOrigin,
  ),
  quantity: String(row?.quantity ?? ''),
  unitOfMeasure:
    row?.unit_of_measure ??
    row?.additional_columns?.unit_of_measure ??
    product.unitOfMeasure ??
    '',
  description: row?.description ?? product.description ?? '',
  traces: row?.traces ?? '',
  specifications: row?.specifications ?? '',
  additionalHtsCodes: Array.isArray(row?.additional_hts_codes)
    ? row.additional_hts_codes.map((code, index) => {
        const htsCode = String(code?.hts_code ?? '')
          .replace(/\D/g, '')
          .slice(0, 10);
        return {
          id: typeof code?.id === 'string' ? code.id : undefined,
          draftKey:
            typeof code?.id === 'string'
              ? code.id
              : `additional-hts-${index}-${htsCode}`,
          htsCode,
          countryOfOrigin: normalizeCountryCode(code?.country_of_origin),
          validatedHtsCode: htsCode.length === 10 ? htsCode : '',
        };
      })
    : [],
  additionalColumns:
    row?.additional_columns &&
    typeof row.additional_columns === 'object' &&
    !Array.isArray(row.additional_columns)
      ? Object.entries(row.additional_columns)
          .filter(([key]) => key !== 'unit_of_measure')
          .map(([key, value], index) => ({
            id: `${key}-${index}`,
            key,
            value:
              typeof value === 'string'
                ? value
                : value === null || value === undefined
                ? ''
                : typeof value === 'object'
                ? JSON.stringify(value)
                : String(value),
          }))
      : [],
  declaredValue: String(row?.product_value ?? product.declaredValue ?? 0),
  smeltCountry: normalizeCountryCode(
    row?.country_of_smelt_pour ?? detail.tariffInputs.smeltCountry,
  ),
  transMethod: row?.trans_method ?? '',
  copperPct: String(row?.copper_percent ?? detail.tariffInputs.copperPct ?? 0),
  copperValue: String(
    row?.copper_value ?? detail.tariffInputs.copperValue ?? 0,
  ),
  aluminumPct: String(
    row?.aluminum_percent ?? detail.tariffInputs.aluminumPct ?? 0,
  ),
  aluminumValue: String(
    row?.aluminum_value ?? detail.tariffInputs.aluminumValue ?? 0,
  ),
  steelPct: String(row?.steel_percent ?? detail.tariffInputs.steelPct ?? 0),
  steelValue: String(row?.steel_value ?? detail.tariffInputs.steelValue ?? 0),
  entryDate: toDateInputValue(row?.entry_date),
  loadingDate: toDateInputValue(row?.loading_date),
});

/* ─── HTS validity / recommendations (mock catalog) ─── */

interface HtsRecommendation {
  code: string;
  description: string;
  confidence: number; // 0–100
  rationale?: string;
  recommended?: boolean;
}

const EXPIRED_HTS: Record<
  string,
  { reason: string; recommendations: HtsRecommendation[] }
> = {
  '3818.00.00': {
    reason: 'Expired HTS code — replaced in the current USHTS schedule',
    recommendations: [
      {
        code: '3818.00.0010',
        description:
          'Chemical elements doped for use in electronics, in the form of discs, wafers — of silicon',
        confidence: 96,
        recommended: true,
        rationale: 'Matches product description: logic-grade silicon wafer.',
      },
      {
        code: '3818.00.0090',
        description:
          'Chemical elements doped for use in electronics — other (non-silicon wafers, discs)',
        confidence: 72,
        rationale: 'Fallback if wafer substrate is not silicon.',
      },
      {
        code: '8541.90.0000',
        description:
          'Parts of semiconductor devices — includes wafers already cut into chips',
        confidence: 41,
        rationale: 'Only if the wafer has been diced into individual dies.',
      },
    ],
  },
  '8504.40.95': {
    reason:
      'Invalid HTS code for country of origin — statistical suffix required',
    recommendations: [
      {
        code: '8504.40.9540',
        description:
          'Static converters — power supplies with a power output not exceeding 50 W',
        confidence: 62,
        rationale: 'Sub-50W tier — verify against nameplate wattage.',
      },
      {
        code: '8504.40.9550',
        description:
          'Static converters — power supplies with a power output exceeding 50 W but not exceeding 150 W',
        confidence: 91,
        recommended: true,
        rationale: 'Correct tier for a 65 W USB-C adapter.',
      },
      {
        code: '8504.40.8500',
        description: 'Static converters — inverters',
        confidence: 18,
        rationale: 'Only if the unit is an inverter, not a rectifier.',
      },
    ],
  },
};

function getHtsIssue(code: string | undefined) {
  if (!code) return null;
  const issue = EXPIRED_HTS[code];
  return issue ? { ...issue, severity: 'danger' as const } : null;
}

const SEV_TEXT: Record<Severity, string> = {
  success: 'text-[var(--success)]',
  info: 'text-[var(--action-color)]',
  warning: 'text-[var(--warning)]',
  danger: 'text-[var(--danger)]',
};
const SEV_BG: Record<Severity, string> = {
  success: 'bg-[color-mix(in_oklch,var(--success)_10%,transparent)]',
  info: 'bg-[color-mix(in_oklch,var(--action-color)_10%,transparent)]',
  warning: 'bg-[color-mix(in_oklch,var(--warning)_12%,transparent)]',
  danger: 'bg-[color-mix(in_oklch,var(--danger)_12%,transparent)]',
};
const SEV_BADGE: Record<Severity, string> = {
  success: 'badge-success',
  info: 'badge-info',
  warning: 'badge-warning',
  danger: 'badge-danger',
};

type CheckType =
  | 'HTS Classification'
  | 'PGA Review'
  | 'Tariff Exposure'
  | 'Forced-Labor Screening'
  | 'FTA Eligibility'
  | 'AD/CVD'
  | 'Risk Summary'
  | 'Customs Flags'
  | 'Tariff & Duty Summary'
  | 'Cross Rulings'
  | 'Entity Risk Screening';

export const ImportProductDetailPage: React.FC<
  ImportProductDetailPageProps
> = ({
  productId,
  initialView = 'overview',
  onViewChange,
  renderHubLink = defaultRenderHrefLink,
  renderDatabaseLink = defaultRenderDatabaseLink,
  hubHref = '/import',
}) => {
  const { cur_user } = useContext(GlobalContext);
  const readOnly = useImportHubReadOnly();
  const id = productId;
  const mockProduct = IMPORT_PRODUCTS.find((p) => p.id === id);

  // Real products (created via the product-service-backed database page) aren't in
  // the mock array. Fetch by id, and adapt the row to the mock ImportProduct shape
  // via toImportProduct — see the Import Hub plan, Slice 4a.
  const [realProductRow, setRealProductRow] =
    useState<ImportServiceProduct | null>(null);
  // Starts true (unless a mock hit means no fetch will ever run) so the first
  // render shows the loading state instead of a "not found" flash before the
  // fetch effect below has had a chance to run.
  const [realProductLoading, setRealProductLoading] = useState(!mockProduct);

  useEffect(() => {
    if (mockProduct) return;
    let cancelled = false;
    setRealProductRow(null);
    setRealProductLoading(true);
    fetchImportProductById(id).then(({ success, product: fetched, error }) => {
      if (cancelled) return;
      setRealProductLoading(false);
      if (success && fetched) {
        setRealProductRow(fetched);
      } else {
        toast.error(error ?? 'Failed to load product');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [id, mockProduct]);

  // Screening result (Slice 4c-ii): needs database_product_id, which is only
  // known once the product fetch above resolves, so this runs sequenced
  // after it rather than in parallel with the other real-data fetches. No
  // screening result yet is an expected state for an unscreened product, not
  // an error — no toast on failure here.
  const [realScreening, setRealScreening] =
    useState<ImportServiceScreeningResult | null>(null);
  const [realScreeningLoading, setRealScreeningLoading] = useState(false);
  const [realScreeningHistory, setRealScreeningHistory] =
    useState<ImportServiceScreeningHistory | null>(null);
  const [activityLimit, setActivityLimit] = useState(50);
  const [screeningHistoryLoading, setScreeningHistoryLoading] = useState(false);
  const [screeningHistoryError, setScreeningHistoryError] = useState<
    string | null
  >(null);
  const [tariffHistoryVersion, setTariffHistoryVersion] = useState(0);
  const [realHtsClassification, setRealHtsClassification] =
    useState<ImportHtsClassification | null>(null);
  const [htsClassificationLoading, setHtsClassificationLoading] =
    useState(false);
  const [htsClassificationError, setHtsClassificationError] = useState<
    string | null
  >(null);
  const [realHtsRecommendations, setRealHtsRecommendations] = useState<
    HtsRecommendation[]
  >([]);
  const [htsRecommendationsLoading, setHtsRecommendationsLoading] =
    useState(false);
  const [htsRecommendationsError, setHtsRecommendationsError] = useState<
    string | null
  >(null);
  const [htsRecommendationsRequested, setHtsRecommendationsRequested] =
    useState(false);
  const htsRecommendationsRequestRef = useRef(0);

  useEffect(() => setActivityLimit(50), [id]);

  useEffect(() => {
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseProductId) return;
    let cancelled = false;
    setRealScreening(null);
    setRealScreeningHistory(null);
    setRealScreeningLoading(true);
    setScreeningHistoryLoading(true);
    setScreeningHistoryError(null);
    const loadScreening = async () => {
      try {
        const [latest, history] = await Promise.all([
          fetchImportProductScreening(databaseProductId),
          fetchImportProductScreeningHistory(databaseProductId, activityLimit),
        ]);
        if (cancelled) return;
        if (latest.success) {
          setRealScreening(latest.screening);
        }
        if (history.success) {
          setRealScreeningHistory(history.result);
        } else {
          setScreeningHistoryError(
            history.error ?? 'Failed to load product activity',
          );
        }
      } catch {
        if (cancelled) return;
        setScreeningHistoryError('Failed to load product screening data');
      } finally {
        if (!cancelled) {
          setRealScreeningLoading(false);
          setScreeningHistoryLoading(false);
        }
      }
    };
    void loadScreening();
    return () => {
      cancelled = true;
    };
  }, [activityLimit, realProductRow?.database_product_id]);

  useEffect(() => {
    const databaseProductId = realProductRow?.database_product_id;
    let cancelled = false;
    setRealHtsClassification(null);
    if (!databaseProductId) {
      setHtsClassificationLoading(false);
      return;
    }
    setHtsClassificationLoading(true);
    setHtsClassificationError(null);
    setRealHtsRecommendations([]);
    setHtsRecommendationsLoading(false);
    setHtsRecommendationsError(null);
    setHtsRecommendationsRequested(false);
    htsRecommendationsRequestRef.current += 1;
    const loadClassification = async () => {
      try {
        const result = await fetchImportProductHtsClassification(
          databaseProductId,
        );
        if (cancelled) return;
        if (result.success) {
          setRealHtsClassification(result.classification);
          setHtsClassificationError(null);
        } else {
          setRealHtsClassification(null);
          setHtsClassificationError(
            result.error ?? 'Failed to load HTS classification',
          );
        }
      } catch {
        if (cancelled) return;
        setRealHtsClassification(null);
        setHtsClassificationError('Failed to load HTS classification');
      } finally {
        if (!cancelled) setHtsClassificationLoading(false);
      }
    };
    void loadClassification();
    return () => {
      cancelled = true;
    };
  }, [realProductRow?.database_product_id, tariffHistoryVersion]);

  // Program Codes assistant table (Slice 9) — live commodity/regulatory-list
  // search so the detail view can show the latest index contents without
  // waiting for the database's next persisted screening run.
  const [realCommodity, setRealCommodity] =
    useState<CommodityLensGroups | null>(null);
  const [commodityLoading, setCommodityLoading] = useState(false);
  const [commodityError, setCommodityError] = useState<string | null>(null);
  const commodityRequestRef = useRef(0);
  const [crossRulingsLoading, setCrossRulingsLoading] = useState(false);

  // Documents + Comments — real products only, both reuse the legacy
  // main-server /bite-db/uploads and /bite-db/comment backends (see the
  // plan notes: same shared Postgres, same database_product_id).
  const [realDocuments, setRealDocuments] = useState<
    ImportServiceDocument[] | null
  >(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [realComments, setRealComments] = useState<
    ImportServiceComment[] | null
  >(null);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const today = localDateInputValue();
  const [entryDate, setEntryDate] = useState<string>(today);
  const [loadingDate, setLoadingDate] = useState<string>(today);
  const [productDetailsEditing, setProductDetailsEditing] = useState(false);
  const [productDetailsSaving, setProductDetailsSaving] = useState(false);
  const [productDetailsError, setProductDetailsError] = useState<string | null>(
    null,
  );
  const [tariffInputsEditing, setTariffInputsEditing] = useState(false);
  const [tariffInputsSaving, setTariffInputsSaving] = useState(false);
  const [tariffInputsError, setTariffInputsError] = useState<string | null>(
    null,
  );
  const [detailsDraft, setDetailsDraft] = useState<ProductEditDraft | null>(
    null,
  );
  const [productCategories, setProductCategories] = useState<
    ImportProductCategory[]
  >([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [validatedHtsCode, setValidatedHtsCode] = useState('');
  const productDetailsSaveRequestRef = useRef(0);
  const tariffInputsSaveRequestRef = useRef(0);
  const complianceFlagRequestRef = useRef(0);
  const [complianceFlagSaving, setComplianceFlagSaving] = useState<
    string | null
  >(null);
  const [ticketDownloading, setTicketDownloading] = useState(false);

  // Navigating directly from one real product to another (e.g. via the
  // products table) doesn't unmount this component — react-router v5 reuses
  // the instance since only :productId changes. Without this, the real-*
  // states above would keep the PREVIOUS product's data on screen for one
  // render (Effects run after paint) or, for realScreening/realCommodity,
  // until the new product's own id/HTS code resolves. Resetting them here,
  // during render, is React's documented "adjust state when a prop changes"
  // pattern: React discards this render and re-renders immediately with the
  // reset values before anything paints, so the stale product is never shown
  // — versus an Effect-based reset, which is always at least one paint late.
  const [prevId, setPrevId] = useState(id);
  if (id !== prevId) {
    setPrevId(id);
    setRealProductRow(null);
    setRealProductLoading(!mockProduct);
    setRealScreening(null);
    setRealScreeningLoading(false);
    setRealScreeningHistory(null);
    setScreeningHistoryLoading(false);
    setScreeningHistoryError(null);
    setRealHtsClassification(null);
    setHtsClassificationLoading(false);
    setHtsClassificationError(null);
    setRealHtsRecommendations([]);
    setHtsRecommendationsLoading(false);
    setHtsRecommendationsError(null);
    setHtsRecommendationsRequested(false);
    htsRecommendationsRequestRef.current += 1;
    setRealCommodity(null);
    setCommodityLoading(false);
    setCommodityError(null);
    commodityRequestRef.current += 1;
    setCrossRulingsLoading(false);
    setRealDocuments(null);
    setDocumentsLoading(false);
    setRealComments(null);
    setCommentsLoading(false);
    productDetailsSaveRequestRef.current += 1;
    tariffInputsSaveRequestRef.current += 1;
    complianceFlagRequestRef.current += 1;
    setProductDetailsEditing(false);
    setProductDetailsSaving(false);
    setProductDetailsError(null);
    setTariffInputsEditing(false);
    setTariffInputsSaving(false);
    setTariffInputsError(null);
    setComplianceFlagSaving(null);
    setDetailsDraft(null);
    setProductCategories([]);
    setCategoriesLoading(false);
    setValidatedHtsCode('');
    setEntryDate(today);
    setLoadingDate(today);
  }

  // "Run Screening" (Slice 8) — real products only. A ref (not a `cancelled`
  // closure var like the effects above) tracks unmount, since this chain is
  // triggered from a click handler, not an effect, so there's no natural
  // cleanup function to tie a local flag to.
  const unmountedRef = useRef(false);
  const screeningRunRequestRef = useRef(0);
  useEffect(() => {
    // React 18 Strict Mode replays effect setup/cleanup in development while
    // preserving refs. Reset this during setup so valid async actions (notably
    // System Flags → Clear) do not mistake the replay cleanup for an unmount.
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      screeningRunRequestRef.current += 1;
    };
  }, []);

  const [screeningRunState, setScreeningRunState] = useState<
    'idle' | 'running' | 'done' | 'error'
  >('idle');
  useEffect(() => {
    screeningRunRequestRef.current += 1;
    setScreeningRunState('idle');
    setTariffHistoryVersion(0);
  }, [id]);

  useEffect(() => {
    const databaseId = realProductRow?.database_id;
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseId || !databaseProductId) return;

    let cancelled = false;
    fetchImportDatabaseScreeningStatus(databaseId).then(
      async ({ success, status }) => {
        if (cancelled || !success || !status) return;
        const active =
          status.inProgress ||
          status.aiInProgress ||
          status.verificationInProgress;
        const affectsProduct =
          status.databaseProductIds.length === 0 ||
          status.databaseProductIds.includes(databaseProductId);
        if (!active || !affectsProduct) return;

        const requestId = ++screeningRunRequestRef.current;
        const isStale = () =>
          cancelled ||
          unmountedRef.current ||
          screeningRunRequestRef.current !== requestId;
        setScreeningRunState('running');

        const { completed } = await pollImportScreeningStatus(databaseId, {
          screeningResultId: status.screeningResultId ?? undefined,
          isCancelled: isStale,
        });
        if (!completed || isStale()) return;

        setScreeningRunState('done');
        setTariffHistoryVersion((version) => version + 1);
        const [latest, history, freshProduct, classification] =
          await Promise.all([
            fetchImportProductScreening(databaseProductId),
            fetchImportProductScreeningHistory(
              databaseProductId,
              activityLimit,
            ),
            fetchImportProductById(id),
            fetchImportProductHtsClassification(databaseProductId),
          ]);
        if (isStale()) return;
        if (latest.success) setRealScreening(latest.screening);
        if (history.success) setRealScreeningHistory(history.result);
        if (freshProduct.success && freshProduct.product) {
          setRealProductRow(freshProduct.product);
        }
        if (classification.success) {
          setRealHtsClassification(classification.classification);
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [
    activityLimit,
    id,
    realProductRow?.database_id,
    realProductRow?.database_product_id,
  ]);

  const handleRunScreening = async () => {
    const databaseId = realProductRow?.database_id;
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseId || !databaseProductId) return;

    const requestId = ++screeningRunRequestRef.current;
    const isStale = () =>
      unmountedRef.current || screeningRunRequestRef.current !== requestId;

    setScreeningRunState('running');

    try {
      const { success, screeningResultId, error } =
        await runImportProductScreening(databaseId, databaseProductId);
      if (isStale()) return;

      if (!success) {
        setScreeningRunState('error');
        toast.error(error ?? 'Failed to start screening');
        return;
      }

      toast.info('Screening started — this can take a moment.');

      const { completed, timedOut } = await pollImportScreeningStatus(
        databaseId,
        {
          screeningResultId,
          isCancelled: isStale,
        },
      );
      if (isStale()) return;

      if (!completed) {
        if (timedOut) {
          setScreeningRunState('error');
          toast.error(
            'Screening is taking longer than expected — check back shortly.',
          );
        }
        return;
      }

      // The worker run is the terminal condition for the button. Refreshing
      // the larger result/history payloads is follow-up work and must not leave
      // the action stuck in "Analyzing…" when either request is slow.
      setScreeningRunState('done');
      setTariffHistoryVersion((version) => version + 1);
      toast.success('Screening complete.');

      const [latest, history, freshProduct, classification] = await Promise.all(
        [
          fetchImportProductScreening(databaseProductId),
          fetchImportProductScreeningHistory(databaseProductId, activityLimit),
          fetchImportProductById(id),
          fetchImportProductHtsClassification(databaseProductId),
        ],
      );
      if (isStale()) return;

      if (latest.success) {
        setRealScreening(latest.screening);
      }
      if (freshProduct.success && freshProduct.product) {
        setRealProductRow(freshProduct.product);
      }
      if (classification.success) {
        setRealHtsClassification(classification.classification);
        setHtsClassificationError(null);
      } else {
        setHtsClassificationError(
          classification.error ?? 'Failed to refresh HTS classification',
        );
      }

      if (history.success) {
        setRealScreeningHistory(history.result);
        setScreeningHistoryError(null);
      } else {
        setScreeningHistoryError(
          history.error ?? 'Failed to refresh product activity',
        );
      }
    } catch {
      if (isStale()) return;
      setScreeningRunState('error');
      toast.error('Analyze Product failed. Please try again.');
    }
  };

  const handleSetComplianceFlagCleared = async (
    flag: string,
    cleared: boolean,
  ) => {
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseProductId || complianceFlagSaving) return;

    const requestId = ++complianceFlagRequestRef.current;
    const isStale = () =>
      unmountedRef.current || complianceFlagRequestRef.current !== requestId;
    setComplianceFlagSaving(flag);
    try {
      const { success, update, error } =
        await updateImportProductComplianceFlag(
          databaseProductId,
          flag,
          cleared,
        );
      if (isStale()) return;

      if (!success || !update) {
        toast.error(error ?? 'Failed to update compliance flag');
        return;
      }

      // Apply the authoritative response immediately so the button visibly
      // changes state, then refresh the product and Activity Log to keep every
      // consumer synchronized with the persisted override.
      setRealProductRow((current) =>
        current?.database_product_id === databaseProductId
          ? {
              ...current,
              compliance_score: update.compliance_score,
              issues: update.issues,
              clear_flags: update.clear_flags,
            }
          : current,
      );
      const [freshProduct, history] = await Promise.all([
        fetchImportProductById(id),
        fetchImportProductScreeningHistory(databaseProductId, activityLimit),
      ]);
      if (isStale()) return;
      if (freshProduct.success && freshProduct.product) {
        setRealProductRow((current) =>
          current?.database_product_id === databaseProductId
            ? freshProduct.product ?? current
            : current,
        );
      }
      if (history.success) {
        setRealScreeningHistory(history.result);
        setScreeningHistoryError(null);
      } else {
        setScreeningHistoryError(
          history.error ?? 'Failed to refresh product activity',
        );
      }
      toast.success(
        cleared ? 'Compliance flag cleared.' : 'Compliance flag restored.',
      );
    } catch {
      if (isStale()) return;
      toast.error('Failed to update compliance flag');
    } finally {
      if (!isStale()) setComplianceFlagSaving(null);
    }
  };

  const product = useMemo(
    () =>
      mockProduct ??
      (realProductRow
        ? toImportProduct(realProductRow, realProductRow.database_id ?? '')
        : undefined),
    [mockProduct, realProductRow],
  );

  const [openCheck, setOpenCheck] = useState<CheckType | null>(null);
  const [openRecord, setOpenRecord] = useState<Record<
    string,
    string | number
  > | null>(null);
  const [entriesOpen, setEntriesOpen] = useState(false);
  const [issuesOpen, setIssuesOpen] = useState(false);
  const [scoreOpen, setScoreOpen] = useState(false);
  const [view, setViewState] = useState<ImportProductDetailView>(initialView);
  const setView = (next: ImportProductDetailView) => {
    setViewState(next);
    onViewChange?.(next);
  };

  const [riskState, setRiskState] = useState<'idle' | 'running' | 'done'>(
    'idle',
  );
  const [riskRanAt, setRiskRanAt] = useState<string | null>(null);
  const [riskDetailOpen, setRiskDetailOpen] = useState(false);
  // HTS recommendation flow
  const [htsOverride, setHtsOverride] = useState<string | null>(null);
  const [htsPrevious, setHtsPrevious] = useState<string | null>(null);
  const [htsChangedTo, setHtsChangedTo] = useState<string | null>(null);
  const [showAnalyzePrompt, setShowAnalyzePrompt] = useState(false);
  const [htsToast, setHtsToast] = useState<string | null>(null);
  const [htsActivity, setHtsActivity] = useState<{
    from: string;
    to: string;
    at: string;
  } | null>(null);

  const loadHtsRecommendations = async () => {
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseProductId || htsRecommendationsLoading) return;
    const requestId = ++htsRecommendationsRequestRef.current;
    setHtsRecommendationsRequested(true);
    setHtsRecommendationsLoading(true);
    setHtsRecommendationsError(null);
    try {
      const result = await fetchImportProductHtsRecommendations(
        databaseProductId,
      );
      if (
        unmountedRef.current ||
        requestId !== htsRecommendationsRequestRef.current
      )
        return;
      if (!result.success) {
        setRealHtsRecommendations([]);
        setHtsRecommendationsError(
          result.error ?? 'Failed to generate HTS recommendations',
        );
        return;
      }
      if (result.classification) {
        setRealHtsClassification(result.classification);
      }
      setRealHtsRecommendations(result.recommendations);
    } catch {
      if (
        unmountedRef.current ||
        requestId !== htsRecommendationsRequestRef.current
      )
        return;
      setRealHtsRecommendations([]);
      setHtsRecommendationsError('Failed to generate HTS recommendations');
    } finally {
      if (
        !unmountedRef.current &&
        requestId === htsRecommendationsRequestRef.current
      ) {
        setHtsRecommendationsLoading(false);
      }
    }
  };

  useEffect(() => {
    setHtsOverride(null);
    setHtsPrevious(null);
    setHtsChangedTo(null);
    setShowAnalyzePrompt(false);
    setHtsToast(null);
    setHtsActivity(null);
  }, [id]);
  const [refreshing, setRefreshing] = useState<Record<string, boolean>>({});
  const refreshSection = (key: string) => {
    setRefreshing((s) => ({ ...s, [key]: true }));
    setTimeout(() => setRefreshing((s) => ({ ...s, [key]: false })), 800);
  };
  const refreshCrossRulings = () => {
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseProductId || crossRulingsLoading) return;

    setCrossRulingsLoading(true);
    fetchImportProductCrossRulings(databaseProductId).then(
      ({ success, crossRulings, error }) => {
        if (unmountedRef.current) return;
        setCrossRulingsLoading(false);
        if (!success) {
          toast.error(error ?? 'Failed to load CROSS rulings');
          return;
        }

        setRealProductRow((current) =>
          current?.database_product_id === databaseProductId
            ? { ...current, cross_rulings: crossRulings }
            : current,
        );
        if (crossRulings.length === 0) {
          toast.info('No matching CROSS rulings were found.');
        }
      },
    );
  };
  const runRisk = () => {
    setRiskState('running');
    setTimeout(() => {
      setRiskRanAt(new Date().toLocaleTimeString());
      setRiskState('done');
    }, 900);
  };
  const [pgaVersion, setPgaVersion] = useState(0);
  const [editorCode, setEditorCode] = useState<PgaCode | null>(null);
  const [draftPgaFlag, setDraftPgaFlag] = useState<AppliedPgaFlag | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createInitialHit, setCreateInitialHit] = useState<PgaHitOption | null>(
    null,
  );
  const bumpPga = () => setPgaVersion((v) => v + 1);
  const appliedFlags = useMemo<AppliedPgaFlag[]>(
    () => (product ? getAppliedFlags(product.id) : []),
    [product?.id, pgaVersion],
  );

  // Program Codes / PGA flags (Slice 7) — real products only. The store is
  // keyed by product.id regardless of mock/real (unchanged), so hydrating a
  // real product's flags here doesn't disturb the mock demo data at all.
  useEffect(() => {
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseProductId || !product) return;
    let cancelled = false;
    fetchImportPgaFlags(databaseProductId).then(({ success, flags, error }) => {
      if (cancelled) return;
      if (success) {
        setAppliedFlags(product.id, flags);
        bumpPga();
      } else {
        toast.error(error ?? 'Failed to load PGA flags');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [product?.id, realProductRow?.database_product_id]);

  // Re-fetches and re-hydrates the store from the backend — used to correct
  // local/server divergence after a failed write (the local store mutation
  // that triggered the write has already run optimistically by that point).
  const reconcilePgaFlags = () => {
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseProductId || !product) return;
    fetchImportPgaFlags(databaseProductId).then(({ success, flags }) => {
      if (success) {
        setAppliedFlags(product.id, flags);
        bumpPga();
      }
    });
  };

  // Persists a flag's full current state (upsert). Real products only — a
  // no-op for mock products, which stay purely in-memory.
  const persistPgaFlagState = async (
    flag: AppliedPgaFlag,
  ): Promise<boolean> => {
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseProductId) return true;
    const { success, error } = await upsertImportPgaFlag(
      databaseProductId,
      flag,
    );
    if (!success) {
      toast.error(error ?? 'Failed to save PGA flag');
      return false;
    }
    return true;
  };

  const persistPgaFlag = (code: PgaCode) => {
    if (!product) return;
    const flag = getAppliedFlags(product.id).find((item) => item.code === code);
    if (!flag) return;
    persistPgaFlagState(flag).then((success) => {
      if (!success) reconcilePgaFlags();
    });
  };

  const persistPgaFlagRemoval = (code: PgaCode) => {
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseProductId) return;
    deleteImportPgaFlag(databaseProductId, code).then(({ success, error }) => {
      if (!success) {
        toast.error(error ?? 'Failed to remove PGA flag');
        reconcilePgaFlags();
      }
    });
  };

  // Documents — real products only.
  const reloadDocuments = () => {
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseProductId) return;
    setDocumentsLoading(true);
    fetchImportProductDocuments(databaseProductId).then(
      ({ success, documents, error }) => {
        setDocumentsLoading(false);
        if (success) {
          setRealDocuments(documents);
        } else {
          toast.error(error ?? 'Failed to load documents');
        }
      },
    );
  };

  useEffect(() => {
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseProductId) return;
    let cancelled = false;
    setDocumentsLoading(true);
    fetchImportProductDocuments(databaseProductId).then(
      ({ success, documents, error }) => {
        if (cancelled) return;
        setDocumentsLoading(false);
        if (success) {
          setRealDocuments(documents);
        } else {
          toast.error(error ?? 'Failed to load documents');
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [realProductRow?.database_product_id]);

  const [uploadingDocument, setUploadingDocument] = useState(false);
  const documentInputRef = useRef<HTMLInputElement>(null);

  const handleUploadDocument = (file: File) => {
    const databaseId = realProductRow?.database_id;
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseId || !databaseProductId) return;
    setUploadingDocument(true);
    uploadImportProductDocument(file, databaseId, databaseProductId).then(
      ({ success, error }) => {
        setUploadingDocument(false);
        if (!success) {
          toast.error(error ?? 'Failed to upload document');
          return;
        }
        toast.success('Document uploaded.');
        reloadDocuments();
      },
    );
  };

  const handleDownloadDocument = (doc: ImportServiceDocument) => {
    downloadImportProductDocument(doc.id, doc.file_name).then(
      ({ success, error }) => {
        if (!success) {
          toast.error(error ?? 'Failed to download document');
        }
      },
    );
  };

  // Comments — real products only. Delete is only offered on the current
  // user's own comments (matches legacy EditProduct.tsx's client-side gate;
  // the shared backend has no server-side ownership check either).
  useEffect(() => {
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseProductId) return;
    let cancelled = false;
    setCommentsLoading(true);
    fetchImportProductComments(databaseProductId).then(
      ({ success, comments }) => {
        if (cancelled) return;
        setCommentsLoading(false);
        if (success) {
          setRealComments(comments);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [realProductRow?.database_product_id]);

  const [postingComment, setPostingComment] = useState(false);

  const handlePostComment = (content: string) => {
    const databaseProductId = realProductRow?.database_product_id;
    if (!databaseProductId || !content.trim()) return;
    const authorName =
      [cur_user?.basics?.firstName, cur_user?.basics?.lastName]
        .filter(Boolean)
        .join(' ') ||
      cur_user?.basics?.email ||
      'Anonymous';
    setPostingComment(true);
    addImportProductComment({
      databaseProductId,
      content: content.trim(),
      authorId: cur_user?.basics?.id,
      authorName,
    }).then(({ success, comment, error }) => {
      setPostingComment(false);
      if (!success) {
        toast.error(error ?? 'Failed to add comment');
        return;
      }
      if (comment) {
        setRealComments((prev) => [...(prev ?? []), comment]);
      }
    });
  };

  const handleDeleteComment = (commentId: string) => {
    setRealComments((prev) => prev && prev.filter((c) => c.id !== commentId));
    deleteImportProductComment(commentId).then(({ success, error }) => {
      if (!success) {
        toast.error(error ?? 'Failed to delete comment');
        const databaseProductId = realProductRow?.database_product_id;
        if (databaseProductId) {
          fetchImportProductComments(databaseProductId).then(
            ({ success: refreshed, comments }) => {
              if (refreshed) setRealComments(comments);
            },
          );
        }
      }
    });
  };

  const openEditorFor = (code: PgaCode) => setEditorCode(code);

  const activeProduct = useMemo<ImportProduct | undefined>(
    () =>
      product && htsOverride ? { ...product, htsCode: htsOverride } : product,
    [product, htsOverride],
  );

  const commodityHtsCode = realProductRow ? activeProduct?.htsCode : undefined;

  useEffect(() => {
    // A changed product/code returns the Program Codes view to its persisted
    // screening snapshot. A live OpenSearch lookup only replaces it after the
    // user explicitly clicks "Get latest results".
    setRealCommodity(null);
    setCommodityError(null);
    setCommodityLoading(false);
    commodityRequestRef.current += 1;
  }, [commodityHtsCode]);

  // "Get latest results" (Program Codes box) — real products only, re-runs
  // the same live search on demand rather than waiting for the HTS code to
  // change.
  const refreshCommodity = async () => {
    if (!commodityHtsCode) return;
    const requestId = ++commodityRequestRef.current;
    const isStale = () => commodityRequestRef.current !== requestId;
    setCommodityLoading(true);
    setCommodityError(null);
    try {
      const { success, groups, error } = await fetchImportCommodityScreening(
        commodityHtsCode,
      );
      if (isStale()) return;
      if (success) {
        setRealCommodity(groups ?? null);
        setCommodityError(null);
      } else {
        const message = error ?? 'Failed to load commodity screening results';
        setRealCommodity(null);
        setCommodityError(message);
        toast.error(message);
      }
    } catch {
      if (isStale()) return;
      const message = 'Failed to load commodity screening results';
      setRealCommodity(null);
      setCommodityError(message);
      toast.error(message);
    } finally {
      if (!isStale()) setCommodityLoading(false);
    }
  };

  const detail = useMemo(() => {
    if (!activeProduct) return null;
    if (!realProductRow) return buildProductDetail(activeProduct);
    const base = applyRealProductDetail(
      activeProduct.htsCode,
      realProductRow,
      undefined,
      realScreening,
    );
    // Program Codes assistant table sources from the live commodity search
    // (Slice 9), not the stored screening result applyRealProductDetail
    // otherwise carries — see fetchImportCommodityScreening's doc comment.
    return realCommodity
      ? {
          ...base,
          biteLensGroups: realCommodity.pga,
          forcedLabor: realCommodity.forcedLabor,
          // The live search doesn't know the product's origin; apply the
          // orders screening found for it.
          adcvd: buildAdcvdDetail(
            realCommodity.adcvdDocs,
            realScreening?.adcvd_cases,
          ),
          euImports: realCommodity.euImports,
          other: realCommodity.other,
        }
      : base;
  }, [activeProduct, realProductRow, realScreening, realCommodity]);

  useEffect(() => {
    if (!product || !detail || productDetailsEditing || tariffInputsEditing)
      return;
    const nextDraft = productEditDraftFrom(product, detail, realProductRow);
    setDetailsDraft(nextDraft);
    setValidatedHtsCode(
      nextDraft.htsCode.length === 10 ? nextDraft.htsCode : '',
    );
    setEntryDate(nextDraft.entryDate || today);
    setLoadingDate(
      nextDraft.loadingDate ||
        defaultLoadingDate(nextDraft.entryDate || today, today),
    );
  }, [
    product,
    detail,
    realProductRow,
    productDetailsEditing,
    tariffInputsEditing,
    today,
  ]);

  useEffect(() => {
    const groupId = cur_user?.selected_team?.id;
    if (!productDetailsEditing || !groupId || !realProductRow) return;
    let cancelled = false;
    setCategoriesLoading(true);
    fetchImportProductCategories(groupId).then(
      ({ success, categories, error }) => {
        if (cancelled) return;
        setCategoriesLoading(false);
        if (success) {
          setProductCategories(categories);
        } else {
          toast.error(error ?? 'Failed to load product categories');
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [
    productDetailsEditing,
    realProductRow?.database_product_id,
    cur_user?.selected_team?.id,
  ]);

  const setDraftValue = (key: ProductEditStringKey, value: string) => {
    setDetailsDraft((current) =>
      current ? { ...current, [key]: value } : current,
    );
    if (TARIFF_INPUT_DRAFT_KEYS.includes(key)) {
      setTariffInputsError(null);
    } else {
      setProductDetailsError(null);
    }
  };

  const restoreDraftFields = (keys: Array<keyof ProductEditDraft>): void => {
    if (!product || !detail) return;
    const fresh = productEditDraftFrom(product, detail, realProductRow);
    setDetailsDraft((current) => {
      const next = { ...(current ?? fresh) };
      keys.forEach((key) => {
        Object.assign(next, { [key]: fresh[key] });
      });
      return next;
    });
  };

  const beginProductDetailsEdit = () => {
    if (!product || !detail) return;
    const nextDraft = productEditDraftFrom(product, detail, realProductRow);
    if (tariffInputsEditing && detailsDraft) {
      TARIFF_INPUT_DRAFT_KEYS.forEach((key) => {
        Object.assign(nextDraft, { [key]: detailsDraft[key] });
      });
    }
    setDetailsDraft(nextDraft);
    setValidatedHtsCode(
      nextDraft.htsCode.length === 10 ? nextDraft.htsCode : '',
    );
    setProductDetailsError(null);
    setProductDetailsEditing(true);
  };

  const cancelProductDetailsEdit = () => {
    productDetailsSaveRequestRef.current += 1;
    restoreDraftFields(PRODUCT_DETAIL_DRAFT_KEYS);
    setProductDetailsSaving(false);
    setProductDetailsError(null);
    setProductDetailsEditing(false);
  };

  const beginTariffInputsEdit = () => {
    if (!product || !detail) return;
    const nextDraft = productEditDraftFrom(product, detail, realProductRow);
    if (!nextDraft.entryDate) nextDraft.entryDate = today;
    if (!nextDraft.loadingDate)
      nextDraft.loadingDate = defaultLoadingDate(nextDraft.entryDate, today);
    if (productDetailsEditing && detailsDraft) {
      PRODUCT_DETAIL_DRAFT_KEYS.forEach((key) => {
        Object.assign(nextDraft, { [key]: detailsDraft[key] });
      });
    }
    setDetailsDraft(nextDraft);
    setTariffInputsError(null);
    setTariffInputsEditing(true);
  };

  const cancelTariffInputsEdit = () => {
    tariffInputsSaveRequestRef.current += 1;
    restoreDraftFields(TARIFF_INPUT_DRAFT_KEYS);
    setTariffInputsSaving(false);
    setTariffInputsError(null);
    setTariffInputsEditing(false);
  };

  const saveProductDetails = async () => {
    if (!detailsDraft || !product || !detail) return;
    if (!realProductRow?.database_product_id) {
      setProductDetailsEditing(false);
      return;
    }

    const currentHts = String(realProductRow.hts_code ?? '').replace(/\D/g, '');
    const originalDraft = productEditDraftFrom(product, detail, realProductRow);
    let requestId = 0;

    try {
      if (!detailsDraft.name.trim()) {
        throw new Error('Product name is required.');
      }
      const normalizedHts = detailsDraft.htsCode.replace(/\D/g, '');
      const htsCodeChanged = normalizedHts !== currentHts;
      if (
        htsCodeChanged &&
        (normalizedHts.length !== 10 || normalizedHts !== validatedHtsCode)
      ) {
        throw new Error(
          'Select a valid ten-digit HTS code from the search results.',
        );
      }
      const additionalHtsCodes = detailsDraft.additionalHtsCodes.map(
        (code) => ({
          ...code,
          htsCode: code.htsCode.replace(/\D/g, '').slice(0, 10),
        }),
      );
      const originalAdditionalHtsCodes = originalDraft.additionalHtsCodes.map(
        ({ id, htsCode, countryOfOrigin }) => ({
          id,
          hts_code: htsCode.replace(/\D/g, '').slice(0, 10),
          country_of_origin: countryOfOrigin || null,
        }),
      );
      const normalizedAdditionalHtsCodes = additionalHtsCodes.map(
        ({ id, htsCode, countryOfOrigin }) => ({
          id,
          hts_code: htsCode,
          country_of_origin: countryOfOrigin || null,
        }),
      );
      const additionalHtsCodesChanged =
        JSON.stringify(normalizedAdditionalHtsCodes) !==
        JSON.stringify(originalAdditionalHtsCodes);
      if (
        additionalHtsCodesChanged &&
        additionalHtsCodes.some(
          (code) =>
            code.htsCode.length !== 10 ||
            code.validatedHtsCode !== code.htsCode,
        )
      ) {
        throw new Error(
          'Select every additional HTS code from the current search results.',
        );
      }
      if (
        additionalHtsCodesChanged &&
        new Set(additionalHtsCodes.map(({ htsCode }) => htsCode)).size !==
          additionalHtsCodes.length
      ) {
        throw new Error('Additional HTS codes cannot contain duplicates.');
      }
      const additionalColumnKeys = detailsDraft.additionalColumns.map(
        ({ key }) => key.trim(),
      );
      const additionalColumns = Object.fromEntries(
        detailsDraft.additionalColumns.map(({ key, value }) => [
          key.trim(),
          value,
        ]),
      );
      const originalAdditionalColumns = Object.fromEntries(
        originalDraft.additionalColumns.map(({ key, value }) => [
          key.trim(),
          value,
        ]),
      );
      const additionalColumnsChanged =
        JSON.stringify(additionalColumns) !==
        JSON.stringify(originalAdditionalColumns);
      if (
        additionalColumnsChanged &&
        additionalColumnKeys.some((key) => !key)
      ) {
        throw new Error('Every additional column needs a name.');
      }
      if (
        additionalColumnsChanged &&
        additionalColumnKeys.some(
          (key) => key.toLowerCase() === 'unit_of_measure',
        )
      ) {
        throw new Error(
          'Use the Unit of measure field instead of an additional column with that name.',
        );
      }
      if (
        additionalColumnsChanged &&
        new Set(additionalColumnKeys.map((key) => key.toLowerCase())).size !==
          additionalColumnKeys.length
      ) {
        throw new Error('Additional column names must be unique.');
      }
      const quantity = detailsDraft.quantity.trim()
        ? Number(detailsDraft.quantity)
        : null;
      if (quantity !== null && (!Number.isFinite(quantity) || quantity < 0)) {
        throw new Error('Quantity must be zero or a positive number.');
      }
      requestId = ++productDetailsSaveRequestRef.current;
      const databaseProductId = realProductRow.database_product_id;
      setProductDetailsSaving(true);
      setProductDetailsError(null);

      let category = detailsDraft.category.trim();
      const originalCategory = originalDraft.category.trim();
      if (category && category !== originalCategory) {
        const existingCategory = productCategories.find(
          ({ name }) => name.toLowerCase() === category.toLowerCase(),
        );
        if (existingCategory) {
          category = existingCategory.name;
        } else {
          const groupId = cur_user?.selected_team?.id;
          if (!groupId) {
            throw new Error(
              'Select a team before creating a product category.',
            );
          }
          const categoryResult = await createImportProductCategory(
            groupId,
            category,
          );
          if (
            requestId !== productDetailsSaveRequestRef.current ||
            unmountedRef.current
          ) {
            return;
          }
          if (!categoryResult.success || !categoryResult.category) {
            throw new Error(
              categoryResult.error ?? 'Failed to save product category.',
            );
          }
          category = categoryResult.category.name;
          const savedCategory = categoryResult.category;
          setProductCategories((current) =>
            current.some(
              ({ name }) =>
                name.toLowerCase() === savedCategory.name.toLowerCase(),
            )
              ? current
              : [...current, savedCategory],
          );
        }
      }

      const update: ImportProductDetailsUpdate = {
        expected_updated_at: realProductRow.updated_at ?? undefined,
      };
      const addChangedField = (
        key: keyof ImportProductDetailsUpdate,
        nextValue: unknown,
        currentValue: unknown,
      ) => {
        if ((nextValue ?? '') !== (currentValue ?? '')) {
          Object.assign(update, { [key]: nextValue });
        }
      };
      addChangedField('name', detailsDraft.name.trim(), originalDraft.name);
      addChangedField(
        'description',
        detailsDraft.description.trim() || null,
        originalDraft.description.trim() || null,
      );
      addChangedField(
        'traces',
        detailsDraft.traces.trim() || null,
        originalDraft.traces.trim() || null,
      );
      addChangedField(
        'specifications',
        detailsDraft.specifications.trim() || null,
        originalDraft.specifications.trim() || null,
      );
      addChangedField(
        'category',
        category || null,
        originalDraft.category || null,
      );
      addChangedField(
        'hierarchy_type',
        detailsDraft.hierarchyType || null,
        originalDraft.hierarchyType || null,
      );
      if ((realProductRow.database_structure ?? 'hierarchy') === 'hierarchy') {
        addChangedField(
          'parent_product_id',
          detailsDraft.parentProductId || null,
          originalDraft.parentProductId || null,
        );
      }
      addChangedField(
        'manufacturer',
        detailsDraft.manufacturer.trim() || null,
        originalDraft.manufacturer.trim() || null,
      );
      addChangedField(
        'vendor',
        detailsDraft.vendor.trim() || null,
        originalDraft.vendor.trim() || null,
      );
      addChangedField(
        'model_number',
        detailsDraft.sku.trim() || null,
        originalDraft.sku.trim() || null,
      );
      addChangedField(
        'country_of_origin',
        detailsDraft.countryOfOrigin || null,
        originalDraft.countryOfOrigin || null,
      );
      if (htsCodeChanged) update.hts_code = normalizedHts;
      addChangedField(
        'quantity',
        quantity,
        originalDraft.quantity.trim() ? Number(originalDraft.quantity) : null,
      );
      addChangedField(
        'unit_of_measure',
        detailsDraft.unitOfMeasure.trim() || null,
        originalDraft.unitOfMeasure.trim() || null,
      );
      if (additionalColumnsChanged) {
        update.additional_columns = additionalColumns;
      }
      if (additionalHtsCodesChanged) {
        update.additional_hts_codes = normalizedAdditionalHtsCodes;
      }

      const result = await saveImportProductDetails(databaseProductId, update);

      if (
        requestId !== productDetailsSaveRequestRef.current ||
        unmountedRef.current
      ) {
        return;
      }
      setProductDetailsSaving(false);
      if (!result.success || !result.product) {
        const message = result.error ?? 'Failed to save product details';
        setProductDetailsError(message);
        toast.error(message);
        return;
      }

      const savedHts = String(result.product.hts_code ?? '').replace(/\D/g, '');
      const savedHtsChanged = savedHts !== currentHts;
      setRealProductRow(result.product);
      setHtsOverride(null);
      if (savedHtsChanged) {
        setHtsPrevious(currentHts);
        setHtsChangedTo(savedHts);
        setHtsToast(`HTS code updated to ${formatHtsPretty(savedHts)}`);
        setShowAnalyzePrompt(true);
        window.setTimeout(() => setHtsToast(null), 3200);
      }
      fetchImportProductScreeningHistory(databaseProductId, activityLimit).then(
        (history) => {
          if (unmountedRef.current || !history.success) return;
          setRealScreeningHistory(history.result);
          setScreeningHistoryError(null);
        },
      );
      setProductDetailsEditing(false);
      if (result.recalculated) {
        setTariffHistoryVersion((version) => version + 1);
        toast.success('Product details and tariff summary updated.');
      } else {
        toast.success('Product details updated.');
      }
    } catch (error) {
      if (
        requestId &&
        (requestId !== productDetailsSaveRequestRef.current ||
          unmountedRef.current)
      ) {
        return;
      }
      const message =
        error instanceof Error ? error.message : 'Invalid product details';
      setProductDetailsError(message);
      setProductDetailsSaving(false);
    }
  };

  const saveTariffInputs = async () => {
    if (!detailsDraft || !product || !detail) return;
    if (!realProductRow?.database_product_id) {
      setTariffInputsEditing(false);
      return;
    }

    const numberValue = (label: string, value: string, max?: number) => {
      if (value.trim() === '') return 0;
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed < 0) {
        throw new Error(`${label} must be zero or a positive number.`);
      }
      if (max !== undefined && parsed > max) {
        throw new Error(`${label} cannot be greater than ${max}.`);
      }
      return parsed;
    };

    try {
      if (
        detailsDraft.entryDate &&
        detailsDraft.loadingDate &&
        detailsDraft.loadingDate > detailsDraft.entryDate
      ) {
        throw new Error('Loading date cannot be later than entry date.');
      }

      const requestId = ++tariffInputsSaveRequestRef.current;
      const databaseProductId = realProductRow.database_product_id;
      setTariffInputsSaving(true);
      setTariffInputsError(null);

      const result = await saveImportProductDetails(databaseProductId, {
        country_of_smelt_pour: detailsDraft.smeltCountry || null,
        trans_method: detailsDraft.transMethod || null,
        entry_date: detailsDraft.entryDate || null,
        loading_date: detailsDraft.loadingDate || null,
        product_value: numberValue(
          'Declared value',
          detailsDraft.declaredValue,
        ),
        copper_percent: numberValue(
          'Copper percentage',
          detailsDraft.copperPct,
          100,
        ),
        copper_value: numberValue('Copper value', detailsDraft.copperValue),
        aluminum_percent: numberValue(
          'Aluminum percentage',
          detailsDraft.aluminumPct,
          100,
        ),
        aluminum_value: numberValue(
          'Aluminum value',
          detailsDraft.aluminumValue,
        ),
        steel_percent: numberValue(
          'Steel percentage',
          detailsDraft.steelPct,
          100,
        ),
        steel_value: numberValue('Steel value', detailsDraft.steelValue),
        expected_updated_at: realProductRow.updated_at ?? undefined,
      });

      if (
        requestId !== tariffInputsSaveRequestRef.current ||
        unmountedRef.current
      ) {
        return;
      }
      setTariffInputsSaving(false);
      if (!result.success || !result.product) {
        const message = result.error ?? 'Failed to save tariff inputs';
        setTariffInputsError(message);
        toast.error(message);
        return;
      }

      setRealProductRow(result.product);
      setEntryDate(detailsDraft.entryDate || today);
      setLoadingDate(
        detailsDraft.loadingDate ||
          defaultLoadingDate(detailsDraft.entryDate || today, today),
      );
      setTariffInputsEditing(false);
      if (result.recalculated) {
        setTariffHistoryVersion((version) => version + 1);
        toast.success('Tariff inputs and tariff summary updated.');
      } else {
        toast.success('Tariff inputs saved; no tariff values changed.');
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Invalid tariff inputs';
      setTariffInputsError(message);
      setTariffInputsSaving(false);
    }
  };

  if (!product && realProductLoading) {
    return (
      <div className='p-12 text-center text-sm text-muted-foreground'>
        <span className='inline-flex items-center gap-2'>
          <span
            className='ih-spinner'
            style={{ width: 16, height: 16, borderWidth: 2 }}
          />
          Loading product…
        </span>
      </div>
    );
  }

  if (!product || !activeProduct || !detail) {
    return (
      <div className='p-12 text-center text-sm text-muted-foreground'>
        Product not found.{' '}
        {renderHubLink({
          href: hubHref,
          className: 'text-primary hover:underline',
          children: 'Back to Import Hub',
        })}
      </div>
    );
  }

  const db =
    IMPORT_DATABASES.find((d) => d.id === product.importDatabaseId) ??
    (realProductRow
      ? {
          ...IMPORT_DATABASES[0],
          id: realProductRow.database_id ?? '',
          name: realProductRow.database_name ?? IMPORT_DATABASES[0].name,
        }
      : undefined);
  const supplier = getImportEntity(product.supplierId);
  const manufacturer = getImportEntity(product.manufacturerId);
  const consignee = getImportEntity('ie-15');

  const pgaHits: PgaHitOption[] = flattenMatches(detail.biteLensGroups)
    .filter((m) => m.lensCode === 'PGA')
    .map(toPgaHitOption)
    .filter((x): x is PgaHitOption => x !== null);

  const annualShipments = 12;
  const annualTariff = detail.cost.totalDutyCost * annualShipments;
  const entries = getEntriesForProduct(product.id);

  const totalFlags =
    detail.riskFlags.length +
    product.pgaFlags.length +
    (product.uflpaRisk !== 'none' ? 1 : 0) +
    (detail.adcvd.exposure ? 1 : 0);

  const activeHts = activeProduct.htsCode;
  const savedHtsClassificationCandidate =
    realProductRow?.tariff_result?.insights?.htsClassification;
  const savedHtsClassification =
    savedHtsClassificationCandidate &&
    typeof savedHtsClassificationCandidate === 'object'
      ? (savedHtsClassificationCandidate as ImportHtsClassification)
      : null;
  const effectiveHtsClassification =
    realHtsClassification ?? savedHtsClassification;
  const hasStoredInvalidHtsIssue = (realProductRow?.issues ?? []).some(
    (issue) => issue === 'Invalid HTS Code',
  );
  const hasStoredMissingHtsIssue = (realProductRow?.issues ?? []).some(
    (issue) => issue === 'Missing HTS Code',
  );
  const htsIssue = realProductRow
    ? effectiveHtsClassification?.requiresAttention ||
      effectiveHtsClassification?.valid === false
      ? {
          reason: effectiveHtsClassification.reason,
          recommendations: realHtsRecommendations,
          status: effectiveHtsClassification.status,
          changedDate: effectiveHtsClassification.changedDate,
          severity:
            effectiveHtsClassification.status === 'expiring_soon'
              ? ('warning' as const)
              : ('danger' as const),
        }
      : !effectiveHtsClassification && hasStoredInvalidHtsIssue
      ? {
          reason:
            'The latest screening marked this HTS code for classification review.',
          recommendations: realHtsRecommendations,
          severity: 'danger' as const,
        }
      : activeHts && activeHts.replace(/\D/g, '').length !== 10
      ? {
          reason: 'A complete ten-digit HTS code is required.',
          recommendations: realHtsRecommendations,
          status: 'not_found' as const,
          severity: 'danger' as const,
        }
      : !activeHts
      ? {
          reason: hasStoredMissingHtsIssue
            ? 'A complete ten-digit HTS code is required.'
            : 'No HTS code is on file. A complete ten-digit HTS code is required.',
          recommendations: realHtsRecommendations,
          status: 'not_found' as const,
          severity: 'danger' as const,
        }
      : null
    : htsOverride
    ? null
    : getHtsIssue(product.htsCode);
  const htsCondensed = (activeHts || '').replace(/\D/g, '');
  const hasInvalidHtsFormat = !!activeHts && htsCondensed.length !== 10;
  const missingEntryDate = Boolean(
    realProductRow && !toDateInputValue(realProductRow.entry_date),
  );
  const missingLoadingDate = Boolean(
    realProductRow && !toDateInputValue(realProductRow.loading_date),
  );
  const loadingDefaultsToEntry =
    !missingEntryDate &&
    defaultLoadingDate(toDateInputValue(realProductRow?.entry_date), today) !==
      today;
  const missingDatesNotice =
    missingEntryDate && missingLoadingDate
      ? 'Entry date and loading date were missing from the original import. Both default to today for tariff calculations and will be persisted when you save Tariff Inputs.'
      : missingEntryDate
      ? 'Entry date was missing from the original import and defaults to today for tariff calculations. It will be persisted when you save Tariff Inputs. Loading date was taken from the import.'
      : `Loading date was missing from the original import and defaults to ${
          loadingDefaultsToEntry ? 'the entry date' : 'today'
        } for tariff calculations. It will be persisted when you save Tariff Inputs. Entry date was taken from the import.`;
  const dataIssues: string[] = [
    !activeHts ? 'HTS code missing' : '',
    activeHts && htsIssue ? `HTS Code Invalid — ${htsIssue.reason}` : '',
    activeHts && !htsIssue && hasInvalidHtsFormat
      ? 'HTS code invalid (must be 10 digits)'
      : '',
    !product.countryOfOrigin ? 'Country of origin missing' : '',
    realProductRow
      ? !realProductRow.vendor
        ? 'Supplier not assigned'
        : ''
      : !product.supplierId
      ? 'Supplier not assigned'
      : '',
    realProductRow
      ? !realProductRow.manufacturer
        ? 'Manufacturer not assigned'
        : ''
      : !product.manufacturerId
      ? 'Manufacturer not assigned'
      : '',
    !product.declaredValue ? 'Declared value missing' : '',
  ].filter(Boolean);

  const currentHtsComplianceIssue = !activeHts
    ? 'Missing HTS Code'
    : htsIssue?.severity === 'danger'
    ? 'Invalid HTS Code'
    : null;
  const realComplianceIssues = realProductRow
    ? [
        ...(realProductRow.issues ?? []).filter(
          (issue) =>
            issue !== 'Missing HTS Code' && issue !== 'Invalid HTS Code',
        ),
        ...(currentHtsComplianceIssue ? [currentHtsComplianceIssue] : []),
      ]
    : [];

  // Compliance score breakdown (deduction rules)
  const scoreDeductions = realProductRow
    ? buildRealScoreDeductions(
        realComplianceIssues,
        realProductRow.clear_flags ?? [],
      )
    : buildScoreDeductions(activeProduct, detail, htsCondensed);
  const scoreFlags = buildScoreFlags(activeProduct, detail);
  // Real products use persisted issues plus the current authoritative HTS
  // classification. Mock products retain the demo-only synthesized flags.
  const systemFlags = realProductRow
    ? []
    : buildSystemFlags(activeProduct, detail, htsCondensed, !!htsIssue);
  const realIssueFlags: SystemFlagItem[] = realProductRow
    ? realComplianceIssues.map((issue) => ({
        label: issue,
        severity: complianceIssueSeverity(issue),
        anchor: complianceIssueAnchor(issue),
        cleared: (realProductRow.clear_flags ?? []).includes(issue),
        sourceIssue: issue,
      }))
    : [];
  const allSystemFlags = [...systemFlags, ...realIssueFlags];

  // Real products only show AI output persisted by an actual screening run.
  // The demo setTimeout flow remains available exclusively to mock products.
  const hasRealAi = detail.ai.status === 'available';
  const effectiveRiskState = realProductRow
    ? hasRealAi
      ? 'done'
      : 'idle'
    : riskState;
  // Prefer the ScreeningResult row's own timestamp: it's always a real ISO
  // instant, whereas detail.ai.screenedAt can be a date-only string on a
  // cache-hit or no-result AI evaluation (server-side toLocaleDateString()),
  // which renders as a bogus midnight time here.
  const effectiveRiskRanAt = hasRealAi
    ? realScreening?.created_at
      ? new Date(realScreening.created_at).toLocaleString()
      : detail.ai.screenedAt
      ? new Date(detail.ai.screenedAt).toLocaleString()
      : riskRanAt
    : riskRanAt;

  const applyRecommendedHts = (rec: HtsRecommendation) => {
    const from = product.htsCode || '—';
    if (realProductRow && detail) {
      const nextDraft = productEditDraftFrom(product, detail, realProductRow);
      nextDraft.htsCode = rec.code.replace(/\D/g, '').slice(0, 10);
      setDetailsDraft(nextDraft);
      setValidatedHtsCode(nextDraft.htsCode);
      setProductDetailsError(null);
      setProductDetailsEditing(true);
      toast.info(
        `${formatHtsPretty(
          nextDraft.htsCode,
        )} is ready for review. Save Product Details to apply it.`,
      );
      // Editing mode replaces the recommendations panel with the edit form
      // in this same commit, so wait a frame before scrolling to the
      // Classification section's Save button.
      requestAnimationFrame(() => scrollToAnchor('classification'));
      return;
    }
    setHtsPrevious(from);
    setHtsOverride(rec.code);
    setHtsChangedTo(rec.code);
    setHtsToast(`HTS code updated to ${rec.code}`);
    setHtsActivity({ from, to: rec.code, at: new Date().toISOString() });
    setShowAnalyzePrompt(true);
    setTimeout(() => setHtsToast(null), 3200);
  };
  const analyzeAfterHtsChange = () => {
    setShowAnalyzePrompt(false);
    // Scroll up first so the user watches the header's Analyze Product
    // button flip into its Analyzing… / spinner state.
    window.scrollTo({ top: 0, behavior: 'smooth' });
    refreshSection('flags');
    if (realProductRow) {
      handleRunScreening();
    } else {
      runRisk();
    }
  };
  const scoreBand =
    product.complianceScore >= 90
      ? 'success'
      : product.complianceScore >= 70
      ? 'warning'
      : 'danger';
  const scoreBandLabel =
    product.complianceScore >= 90
      ? 'Good'
      : product.complianceScore >= 70
      ? 'Fair'
      : 'Low';

  const handleDownloadTicket = async () => {
    if (ticketDownloading) return;

    const fileName = `${(product.name || 'product')
      .toString()
      .trim()
      .replace(/\s+/g, '-')}-${format(new Date(), 'yyyy-MM-dd-hh-mm-ss')}.pdf`;

    try {
      setTicketDownloading(true);

      const databaseProductId = realProductRow?.database_product_id;
      let changelog: IImportProductTicketReportData['changelog'] = [];
      if (databaseProductId) {
        const historyResult = await fetchImportProductTariffHistory(
          databaseProductId,
        );
        if (historyResult.success) {
          changelog = [...historyResult.history]
            .sort(
              (left, right) =>
                new Date(right.changedAt).getTime() -
                new Date(left.changedAt).getTime(),
            )
            .map((entry) => ({
              date: formatDate(entry.changedAt),
              heading: entry.heading,
              previousValue: entry.previousValue,
              currentValue: entry.currentValue,
            }));
        }
      }

      const activeDeductions = scoreDeductions.filter((d) => !d.passed);
      const preparedByName = [
        cur_user?.basics?.firstName,
        cur_user?.basics?.lastName,
      ]
        .filter(Boolean)
        .join(' ')
        .trim();
      const lensGroups: BiteLensGroup[] = [
        ...detail.biteLensGroups,
        ...(detail.forcedLabor.groups ?? []),
        ...(detail.adcvd.groups ?? []),
        ...detail.euImports,
        ...detail.other,
      ];
      const matches = flattenMatches(lensGroups);
      const declaredValueText = product.declaredValue
        ? formatTariffCurrency(product.declaredValue)
        : undefined;

      const materialInputs: IImportProductTicketReportData['tariff']['materialInputs'] =
        [];
      if (detail.tariffInputs.smeltCountry) {
        materialInputs.push({
          label: 'Smelt Country',
          value: detail.tariffInputs.smeltCountry,
        });
      }
      materialInputs.push(
        { label: 'Copper %', value: `${detail.tariffInputs.copperPct}%` },
        {
          label: 'Copper Value',
          value: formatMaterialValue(detail.tariffInputs.copperValue),
        },
        { label: 'Aluminum %', value: `${detail.tariffInputs.aluminumPct}%` },
        {
          label: 'Aluminum Value',
          value: formatMaterialValue(detail.tariffInputs.aluminumValue),
        },
        { label: 'Steel %', value: `${detail.tariffInputs.steelPct}%` },
        {
          label: 'Steel Value',
          value: formatMaterialValue(detail.tariffInputs.steelValue),
        },
        { label: 'Entry Date', value: entryDate || '—' },
        { label: 'Loading Date', value: loadingDate || '—' },
      );

      const riskFlags: IImportProductTicketReportData['tariff']['riskFlags'] =
        [];
      if (detail.adcvd.exposure) {
        riskFlags.push({
          label: 'AD/CVD Exposure',
          detail: detail.adcvd.scopeDetermination || detail.adcvd.reasoning,
        });
      }
      if (detail.forcedLabor.severity !== 'success') {
        riskFlags.push({
          label: 'Forced Labor',
          detail: detail.forcedLabor.result,
        });
      }
      const totalPgaHits = detail.biteLensGroups.reduce(
        (sum, g) => sum + (g.total || 0),
        0,
      );
      if (totalPgaHits > 0) {
        riskFlags.push({
          label: 'PGA Requirement',
          detail:
            'Partner Government Agency filing may be required for this entry.',
        });
      }

      const reportData: IImportProductTicketReportData = {
        productName: product.name,
        sku: activeProduct.sku,
        htsCode: activeProduct.htsCode,
        countryOfOrigin: activeProduct.countryOfOrigin,
        databaseName: db?.name,
        declaredValue: declaredValueText,
        preparedBy: preparedByName || cur_user?.basics?.email,
        compliance: {
          assessed: product.complianceAssessed !== false,
          score: product.complianceScore,
          deductions: activeDeductions.map((d) => ({
            reason: d.reason,
            points: d.points,
            category: d.category,
          })),
        },
        systemFlags: allSystemFlags.map((f) => ({
          label: f.label,
          detail: f.detail,
          severity: f.severity,
          cleared: f.cleared,
        })),
        risk: {
          status: detail.ai.status || 'unavailable',
          level: detail.ai.risk,
          confidence:
            formatAiConfidence(detail.ai.matchProbability) || undefined,
          narrative: detail.ai.narrative,
          keyFactors: detail.ai.keyFactors,
        },
        classification: {
          name: activeProduct.name,
          sku: activeProduct.sku,
          hierarchyType: activeProduct.hierarchyType,
          category: activeProduct.category,
          manufacturer: activeProduct.manufacturer,
          vendor: activeProduct.vendor,
          htsCode: activeProduct.htsCode,
          countryOfOrigin: activeProduct.countryOfOrigin,
          unitOfMeasure: activeProduct.unitOfMeasure,
          database: db?.name,
          description: activeProduct.description,
          traces: detailsDraft?.traces,
          specifications: detailsDraft?.specifications,
          additionalHtsCodes: (detailsDraft?.additionalHtsCodes ?? []).map(
            (h) => ({
              htsCode: h.htsCode,
              countryOfOrigin: h.countryOfOrigin,
            }),
          ),
          additionalColumns: (detailsDraft?.additionalColumns ?? [])
            .filter((col) => !isHiddenAdditionalColumn(col.key))
            .map((col) => ({ key: col.key, value: col.value })),
        },
        tariff: {
          declaredValue: declaredValueText,
          materialInputs,
          riskFlags,
          baseDutyRate: `${detail.cost.baseRatePct}%`,
          baseDutyCost: formatTariffCurrency(detail.cost.baseCost),
          additionalDutyRate: `${detail.cost.additionalRatePct}%`,
          additionalDutyCost: formatTariffCurrency(detail.cost.additionalCost),
          totalDutyCost: formatTariffCurrency(detail.cost.totalDutyCost),
          totalCost: formatTariffCurrency(detail.cost.totalCost),
          dutyLines: detail.dutyLines.map((l) => ({
            subCode: l.subCode,
            label: l.label,
            ratePct: `${l.ratePct}%`,
            cost: formatTariffCurrency(l.cost),
          })),
          ftas: detail.ftas.map((f) => ({
            program: f.program,
            status: f.status,
            note: f.note,
          })),
          exclusions: detail.exclusions.map((e) => ({
            subCode: e.subCode,
            label: e.label,
            ratePct: `${e.ratePct}%`,
            applicability: e.applicability,
          })),
        },
        programCodes: {
          appliedFlagCodes: appliedFlags.map((f) => f.code),
          lensSummary: lensGroups.map((g) => ({
            name: g.name,
            total: g.total,
            newCount: g.newCount,
          })),
          matches: matches.map((m) => ({
            lens: m.lensName,
            entity: m.entity,
            type: m.type,
            country: m.country,
            list: m.list,
            score: m.score,
          })),
        },
        crossRulings: detail.crossRulings.map((r) => ({
          title: r.title,
          url: r.url,
        })),
        changelog,
      };

      const pdf = await importProductTicketPDF(reportData, fileName, {
        shouldDownload: true,
      });
      if (!pdf) {
        throw new Error('The product report could not be rendered.');
      }
      toast.success('Product compliance report downloaded');
    } catch (error) {
      console.error('Failed to download product compliance report PDF:', error);
      toast.error('Unable to download product compliance report');
    } finally {
      setTicketDownloading(false);
    }
  };

  return (
    <div className='p-6 space-y-5 mx-auto'>
      {htsToast && (
        <div className='fixed bottom-6 right-6 z-50 elms-card border border-[var(--success)]/40 bg-[color-mix(in_oklch,var(--success)_10%,transparent)] flex items-center gap-2 shadow-lg'>
          <CheckCircle2 className='size-4 text-[var(--success)]' />
          <span className='text-sm'>{htsToast}</span>
        </div>
      )}
      {/* Breadcrumb */}
      <nav className='flex items-center gap-1.5 text-xs text-muted-foreground'>
        {renderHubLink({
          href: hubHref,
          className: 'inline-flex items-center gap-1 hover:text-foreground',
          children: (
            <>
              <Home className='size-3.5' /> Import Hub
            </>
          ),
        })}
        <ChevronRight className='size-3' />
        {db && (
          <>
            {renderDatabaseLink({
              dbId: db.id,
              className: 'hover:text-foreground',
              children: db.name,
            })}
            <ChevronRight className='size-3' />
          </>
        )}
        <span className='text-foreground font-medium'>{product.name}</span>
      </nav>

      {/* Header */}
      <div className='elms-card'>
        <div className='flex items-start justify-between gap-4 flex-wrap'>
          <div className='space-y-1.5 min-w-0'>
            <h1 className='text-xl font-semibold tracking-tight'>
              {product.name}
            </h1>
            <div className='flex items-center gap-2 text-xs text-muted-foreground flex-wrap'>
              <span className='font-mono'>{product.sku}</span>
              <span>·</span>
              <span className='inline-flex items-center gap-1'>
                <Globe2 className='size-3' />
                {product.countryOfOrigin || 'Origin n/a'}
              </span>
              <span>·</span>
              <span>
                HTS{' '}
                <span
                  className={cn(
                    'font-mono text-foreground',
                    htsIssue && 'text-[var(--danger)]',
                  )}
                >
                  {activeHts || '—'}
                </span>
              </span>
              <span>·</span>
              <span>
                Declared{' '}
                <span className='tabular-nums text-foreground'>
                  {formatCurrency(product.declaredValue)}
                </span>
              </span>
            </div>
          </div>
          <div className='flex items-center gap-2 shrink-0 flex-wrap justify-end'>
            <HeaderBtn
              icon={ticketDownloading ? RotateCw : Download}
              onClick={handleDownloadTicket}
              disabled={ticketDownloading}
              spinning={ticketDownloading}
            >
              {ticketDownloading ? 'Downloading…' : 'Download Ticket'}
            </HeaderBtn>
            <HeaderBtn
              icon={Upload}
              spinning={uploadingDocument}
              locked={readOnly}
              onClick={
                realProductRow
                  ? () => documentInputRef.current?.click()
                  : undefined
              }
              disabled={realProductRow ? uploadingDocument : false}
            >
              {uploadingDocument ? 'Uploading…' : 'Upload Documents'}
            </HeaderBtn>
            <input
              ref={documentInputRef}
              type='file'
              accept='.csv,.xlsx,.png,.jpeg,.jpg,.pdf,.docx'
              className='hidden'
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUploadDocument(file);
                e.currentTarget.value = '';
              }}
            />
            <HeaderBtn
              icon={screeningRunState === 'running' ? RotateCw : PlayCircle}
              primary
              spinning={screeningRunState === 'running'}
              locked={readOnly}
              onClick={realProductRow ? handleRunScreening : undefined}
              disabled={
                realProductRow ? screeningRunState === 'running' : false
              }
            >
              {screeningRunState === 'running'
                ? 'Analyzing…'
                : 'Analyze Product'}
            </HeaderBtn>
          </div>
        </div>
      </div>

      <nav className='export-hub-tabs' aria-label='Product views'>
        <button
          className='export-hub-tab'
          data-active={view === 'overview'}
          onClick={() => setView('overview')}
        >
          <Package className='size-4' /> Overview
        </button>
        <button
          className='export-hub-tab'
          data-active={view === 'documents'}
          onClick={() => setView('documents')}
        >
          <FileText className='size-4' /> Documents
        </button>
        <button
          className='export-hub-tab'
          data-active={view === 'comments'}
          onClick={() => setView('comments')}
        >
          <MessageSquare className='size-4' /> Comments
        </button>
        <button
          className='export-hub-tab'
          data-active={view === 'history'}
          onClick={() => setView('history')}
        >
          <History className='size-4' /> Activity Log
        </button>
      </nav>

      {view === 'history' ? (
        <Product360
          product={activeProduct}
          entries={entries}
          detail={detail}
          htsChange={htsActivity}
          isRealProduct={!!realProductRow}
          screeningHistory={realScreeningHistory}
          loading={screeningHistoryLoading}
          error={screeningHistoryError}
          canLoadMore={
            !!realScreeningHistory &&
            realScreeningHistory.loaded < realScreeningHistory.total &&
            activityLimit < 250
          }
          onLoadMore={() =>
            setActivityLimit((current) => Math.min(250, current + 50))
          }
        />
      ) : view === 'documents' ? (
        <DocumentsView
          detail={detail}
          isRealProduct={!!realProductRow}
          documents={realDocuments}
          loading={documentsLoading}
          onDownload={handleDownloadDocument}
        />
      ) : view === 'comments' ? (
        <CommentsView
          detail={detail}
          isRealProduct={!!realProductRow}
          comments={realComments}
          loading={commentsLoading}
          currentUserId={cur_user?.basics?.id}
          posting={postingComment}
          onPost={handlePostComment}
          onDelete={handleDeleteComment}
        />
      ) : (
        <>
          {/* KPI trio: Compliance Score · System Flags · Risk */}
          <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
            <ScoreCard
              score={product.complianceScore}
              assessed={product.complianceAssessed !== false}
              band={scoreBand}
              bandLabel={scoreBandLabel}
              deductionCount={scoreDeductions.filter((d) => !d.passed).length}
              onOpen={() => setScoreOpen(true)}
            />
            <SystemFlagsCard
              flags={allSystemFlags}
              onSetCleared={
                realProductRow ? handleSetComplianceFlagCleared : undefined
              }
              savingFlag={complianceFlagSaving}
            />
            <RiskCard
              state={effectiveRiskState}
              ranAt={effectiveRiskRanAt}
              risk={detail.ai.risk}
              matchProbability={detail.ai.matchProbability}
              onRun={runRisk}
              onOpen={() => setRiskDetailOpen(true)}
              hideRunButton={!!realProductRow}
            />
          </div>

          {/* Quick nav */}
          <QuickNav />

          {/* Classification */}
          <PageSection
            id='classification'
            icon={FileText}
            title='Classification'
            subtitle='Product identity and HTS classification — the data-quality core.'
          >
            {showAnalyzePrompt && (
              <div className='elms-card border border-[color-mix(in_oklch,var(--action-color)_40%,transparent)] bg-[color-mix(in_oklch,var(--action-color)_8%,transparent)] flex items-start gap-3'>
                <PlayCircle className='size-4 mt-0.5 text-[var(--action-color)] shrink-0' />
                <div className='flex-1 min-w-0'>
                  <div className='text-sm font-medium'>
                    HTS code updated
                    {htsPrevious && htsChangedTo
                      ? ` (${formatHtsPretty(htsPrevious)} → ${formatHtsPretty(
                          htsChangedTo,
                        )})`
                      : ''}
                    .
                  </div>
                  <div className='text-xs text-muted-foreground mt-0.5'>
                    Analyze this product now to refresh tariffs, flags, rulings,
                    and screening with the new code.
                  </div>
                </div>
                <div className='flex items-center gap-2 shrink-0'>
                  <button
                    type='button'
                    onClick={() => setShowAnalyzePrompt(false)}
                    className='h-7 px-2.5 rounded-md border border-input bg-background hover:bg-accent text-xs'
                  >
                    Not now
                  </button>
                  <button
                    type='button'
                    onClick={readOnly ? undefined : analyzeAfterHtsChange}
                    disabled={readOnly}
                    title={readOnly ? 'Requires a Business license' : undefined}
                    className='inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed'
                  >
                    {readOnly ? (
                      <Lock className='size-3.5' />
                    ) : (
                      <PlayCircle className='size-3.5' />
                    )}{' '}
                    Analyze Product
                  </button>
                </div>
              </div>
            )}
            <div className='ih-classification-grid grid grid-cols-1 lg:grid-cols-3 gap-4'>
              <div className='lg:col-span-2'>
                <EditableProductDetails
                  product={activeProduct}
                  dbName={db?.name}
                  importSource={realProductRow?.import_source}
                  htsIssue={htsIssue}
                  onSelectRecommendation={applyRecommendedHts}
                  onLoadRecommendations={
                    realProductRow ? loadHtsRecommendations : undefined
                  }
                  recommendationsLoading={htsRecommendationsLoading}
                  recommendationsError={htsRecommendationsError}
                  recommendationsRequested={htsRecommendationsRequested}
                  editing={productDetailsEditing}
                  saving={productDetailsSaving}
                  error={productDetailsError}
                  values={
                    detailsDraft ??
                    productEditDraftFrom(activeProduct, detail, realProductRow)
                  }
                  categories={productCategories}
                  categoriesLoading={categoriesLoading}
                  onChange={setDraftValue}
                  databaseId={realProductRow?.database_id ?? undefined}
                  databaseStructure={
                    realProductRow
                      ? realProductRow.database_structure ?? 'hierarchy'
                      : undefined
                  }
                  onParentChange={(parent) => {
                    setDetailsDraft((current) =>
                      current
                        ? {
                            ...current,
                            parentProductId: parent?.id ?? '',
                            parentSku: parent?.sku ?? '',
                            parentName: parent?.name ?? '',
                            parentHierarchyType: parent?.hierarchyType ?? '',
                          }
                        : current,
                    );
                    setProductDetailsError(null);
                  }}
                  onAdditionalHtsCodesChange={(additionalHtsCodes) => {
                    setDetailsDraft((current) =>
                      current ? { ...current, additionalHtsCodes } : current,
                    );
                    setProductDetailsError(null);
                  }}
                  onAdditionalColumnsChange={(additionalColumns) => {
                    setDetailsDraft((current) =>
                      current ? { ...current, additionalColumns } : current,
                    );
                    setProductDetailsError(null);
                  }}
                  onHtsValidated={setValidatedHtsCode}
                  onEdit={beginProductDetailsEdit}
                  onCancel={cancelProductDetailsEdit}
                  onSave={saveProductDetails}
                />
              </div>
              <div className='ih-tariff-changelog-column'>
                <DutyTariffChangelog
                  product={activeProduct}
                  databaseProductId={
                    realProductRow?.database_product_id ?? undefined
                  }
                  refreshVersion={tariffHistoryVersion}
                />
              </div>
            </div>
          </PageSection>

          {/* Duties & Tariffs */}
          <PageSection
            id='tariffs'
            icon={Percent}
            title='Duties & Tariffs'
            subtitle='Declared value, material inputs, and the full stacked duty breakdown.'
          >
            <FieldGridCard
              title='Tariff Specific Inputs'
              columns={3}
              action={
                tariffInputsEditing ? (
                  <div className='flex items-center gap-2'>
                    <button
                      type='button'
                      onClick={cancelTariffInputsEdit}
                      disabled={tariffInputsSaving}
                      className='h-7 px-2.5 rounded-md border border-input bg-background hover:bg-accent text-xs disabled:opacity-60'
                    >
                      Cancel
                    </button>
                    <button
                      type='button'
                      onClick={saveTariffInputs}
                      disabled={tariffInputsSaving}
                      className='inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-medium disabled:opacity-60'
                    >
                      {tariffInputsSaving && (
                        <span
                          className='ih-spinner'
                          style={{ width: 12, height: 12, borderWidth: 2 }}
                        />
                      )}
                      {tariffInputsSaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                ) : (
                  <button
                    type='button'
                    onClick={readOnly ? undefined : beginTariffInputsEdit}
                    disabled={readOnly}
                    title={readOnly ? 'Requires a Business license' : undefined}
                    className='inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-input bg-background hover:bg-accent text-xs disabled:opacity-60 disabled:cursor-not-allowed'
                  >
                    {readOnly ? (
                      <Lock className='size-3' />
                    ) : (
                      <PencilLine className='size-3' />
                    )}{' '}
                    Edit Tariff Inputs
                  </button>
                )
              }
            >
              <EditableField
                editing={tariffInputsEditing}
                label='Declared value'
                value={
                  tariffInputsEditing
                    ? detailsDraft?.declaredValue ?? ''
                    : String(product.declaredValue)
                }
                displayValue={formatCurrency(product.declaredValue)}
                inputMode='decimal'
                onChange={(value) => setDraftValue('declaredValue', value)}
              />
              {tariffInputsEditing ? (
                <CountrySelectField
                  label='Smelt country'
                  value={detailsDraft?.smeltCountry ?? ''}
                  allowEmpty
                  onChange={(value) => setDraftValue('smeltCountry', value)}
                />
              ) : (
                <Field
                  label='Smelt country'
                  value={detail.tariffInputs.smeltCountry}
                />
              )}
              {tariffInputsEditing ? (
                <SelectField
                  label='Transport method'
                  value={detailsDraft?.transMethod ?? ''}
                  options={TRANSPORT_METHOD_OPTIONS}
                  allowEmpty
                  preserveCurrent
                  onChange={(value) => setDraftValue('transMethod', value)}
                />
              ) : (
                <Field
                  label='Transport method'
                  value={realProductRow?.trans_method ?? ''}
                />
              )}
              <EditableField
                editing={tariffInputsEditing}
                label='Copper %'
                value={
                  tariffInputsEditing
                    ? detailsDraft?.copperPct ?? ''
                    : String(detail.tariffInputs.copperPct)
                }
                displayValue={`${detail.tariffInputs.copperPct}%`}
                inputMode='decimal'
                onChange={(value) => setDraftValue('copperPct', value)}
              />
              <EditableField
                editing={tariffInputsEditing}
                label='Copper value'
                value={
                  tariffInputsEditing
                    ? detailsDraft?.copperValue ?? ''
                    : String(detail.tariffInputs.copperValue)
                }
                displayValue={formatMaterialValue(
                  detail.tariffInputs.copperValue,
                )}
                inputMode='decimal'
                onChange={(value) => setDraftValue('copperValue', value)}
              />
              <EditableField
                editing={tariffInputsEditing}
                label='Aluminum %'
                value={
                  tariffInputsEditing
                    ? detailsDraft?.aluminumPct ?? ''
                    : String(detail.tariffInputs.aluminumPct)
                }
                displayValue={`${detail.tariffInputs.aluminumPct}%`}
                inputMode='decimal'
                onChange={(value) => setDraftValue('aluminumPct', value)}
              />
              <EditableField
                editing={tariffInputsEditing}
                label='Aluminum value'
                value={
                  tariffInputsEditing
                    ? detailsDraft?.aluminumValue ?? ''
                    : String(detail.tariffInputs.aluminumValue)
                }
                displayValue={formatMaterialValue(
                  detail.tariffInputs.aluminumValue,
                )}
                inputMode='decimal'
                onChange={(value) => setDraftValue('aluminumValue', value)}
              />
              <EditableField
                editing={tariffInputsEditing}
                label='Steel %'
                value={
                  tariffInputsEditing
                    ? detailsDraft?.steelPct ?? ''
                    : String(detail.tariffInputs.steelPct)
                }
                displayValue={`${detail.tariffInputs.steelPct}%`}
                inputMode='decimal'
                onChange={(value) => setDraftValue('steelPct', value)}
              />
              <EditableField
                editing={tariffInputsEditing}
                label='Steel value'
                value={
                  tariffInputsEditing
                    ? detailsDraft?.steelValue ?? ''
                    : String(detail.tariffInputs.steelValue)
                }
                displayValue={formatMaterialValue(
                  detail.tariffInputs.steelValue,
                )}
                inputMode='decimal'
                onChange={(value) => setDraftValue('steelValue', value)}
              />
              <DateField
                label='Entry date'
                value={
                  tariffInputsEditing
                    ? detailsDraft?.entryDate ?? ''
                    : entryDate
                }
                disabled={!tariffInputsEditing}
                onChange={(value) => setDraftValue('entryDate', value)}
              />
              <DateField
                label='Loading date'
                value={
                  tariffInputsEditing
                    ? detailsDraft?.loadingDate ?? ''
                    : loadingDate
                }
                disabled={!tariffInputsEditing}
                onChange={(value) => setDraftValue('loadingDate', value)}
              />
              {tariffInputsError && (
                <div className='ih-edit-error md:col-span-3'>
                  <AlertCircle className='size-3.5 shrink-0' />
                  {tariffInputsError}
                </div>
              )}
              {(missingEntryDate || missingLoadingDate) && (
                <div className='col-span-2 md:col-span-3 flex items-start gap-2 rounded-md border border-[var(--edge-divider)] bg-[var(--bg-surface)] px-3 py-2 text-xs text-muted-foreground'>
                  <Info className='mt-0.5 size-3.5 shrink-0' />
                  <span>{missingDatesNotice}</span>
                </div>
              )}
            </FieldGridCard>
            <TariffSummarySection
              detail={detail}
              product={activeProduct}
              databaseProductId={realProductRow?.database_product_id}
              initialTariffResult={realProductRow?.tariff_result}
              onProductSaved={setRealProductRow}
              entryDate={entryDate}
              riskFlagsLoading={!!realProductRow && commodityLoading}
              riskFlagsError={realProductRow ? commodityError : null}
              htsClassification={realHtsClassification}
              htsClassificationLoading={
                !!realProductRow && htsClassificationLoading
              }
              htsClassificationError={
                realProductRow ? htsClassificationError : null
              }
            />
          </PageSection>

          {/* Program Codes */}
          <PageSection
            id='flags'
            icon={Flag}
            title='Program Codes'
            subtitle='Verify and upload information as it applies to AD/CVD, PGA, CPSC, Forced Labor, EU Imports, and other customs related flags.'
            action={
              <SectionRefreshBtn
                loading={realProductRow ? commodityLoading : !!refreshing.flags}
                onClick={
                  realProductRow
                    ? refreshCommodity
                    : () => refreshSection('flags')
                }
              />
            }
          >
            <div className='elms-card space-y-4'>
              <AppliedFlagsSection
                flags={appliedFlags}
                onOpen={openEditorFor}
                onAdd={() => {
                  setCreateInitialHit(null);
                  setCreateOpen(true);
                }}
                addLocked={readOnly}
              />
              <FlagsTabsSection
                detail={detail}
                product={product}
                onOpenRecord={setOpenRecord}
                appliedCodes={appliedFlags.map((flag) => flag.code)}
                onCreateFromHit={
                  readOnly
                    ? undefined
                    : (hit) => {
                        setCreateInitialHit(hit);
                        setCreateOpen(true);
                      }
                }
                loading={!!realProductRow && commodityLoading}
              />
            </div>
          </PageSection>

          {/* Cross Rulings */}
          <PageSection
            id='rulings'
            icon={Scale}
            title='CROSS Rulings'
            subtitle='Relevant CBP CROSS rulings for this HTS.'
            action={
              <SectionRefreshBtn
                loading={crossRulingsLoading}
                onClick={refreshCrossRulings}
              />
            }
          >
            <CrossRulingsCard detail={detail} />
          </PageSection>
        </>
      )}

      <ComplianceCheckDrawer
        open={!!openCheck}
        onClose={() => setOpenCheck(null)}
        type={openCheck}
        product={product}
        detail={detail}
        onOpenRecord={setOpenRecord}
        appliedFlags={appliedFlags}
        onOpenEditor={openEditorFor}
        screeningLoading={!!realProductRow && realScreeningLoading}
        hasScreening={!realProductRow || !!realScreening}
      />
      <PgaFlagEditorDrawer
        open={!!editorCode}
        code={editorCode}
        flag={
          editorCode
            ? draftPgaFlag?.code === editorCode
              ? draftPgaFlag
              : appliedFlags.find((f) => f.code === editorCode) ?? null
            : null
        }
        isDraft={!!draftPgaFlag && draftPgaFlag.code === editorCode}
        onClose={() => {
          if (draftPgaFlag?.code === editorCode) setDraftPgaFlag(null);
          setEditorCode(null);
        }}
        onSave={async (values, repeaters) => {
          if (!editorCode) return false;
          const isDraft = draftPgaFlag?.code === editorCode;
          const current = isDraft
            ? draftPgaFlag
            : appliedFlags.find((flag) => flag.code === editorCode) ?? null;
          if (!current) return false;
          const nextFlag: AppliedPgaFlag = {
            ...current,
            values,
            repeaters,
          };
          const saved = await persistPgaFlagState(nextFlag);
          if (!saved) return false;
          if (isDraft) {
            addAppliedFlag(product.id, editorCode, values, repeaters);
            setDraftPgaFlag(null);
          } else {
            updateAppliedFlag(product.id, editorCode, values, repeaters);
          }
          bumpPga();
          return true;
        }}
        onRemove={() => {
          if (editorCode) {
            if (draftPgaFlag?.code === editorCode) {
              setDraftPgaFlag(null);
              setEditorCode(null);
              return;
            }
            removeAppliedFlag(product.id, editorCode);
            bumpPga();
            persistPgaFlagRemoval(editorCode);
            setEditorCode(null);
          }
        }}
        onAddDocument={
          draftPgaFlag
            ? undefined
            : (doc) => {
                if (editorCode) {
                  addFlagDocument(product.id, editorCode, doc);
                  bumpPga();
                  persistPgaFlag(editorCode);
                }
              }
        }
        onRemoveDocument={
          draftPgaFlag
            ? undefined
            : (docId) => {
                if (editorCode) {
                  removeFlagDocument(product.id, editorCode, docId);
                  bumpPga();
                  persistPgaFlag(editorCode);
                }
              }
        }
      />
      <PgaFlagCreateDialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreateInitialHit(null);
        }}
        appliedCodes={appliedFlags.map((f) => f.code)}
        hits={pgaHits}
        initialHit={createInitialHit}
        productDefaults={{
          'productDetails.productName': product.name,
          'productDetails.description': product.description,
          'productDetails.model': product.sku,
          'productDetails.hsCode': activeProduct.htsCode,
          'productDetails.countryOfOrigin': activeProduct.countryOfOrigin,
        }}
        onCreate={({ code, seedValues, seedRepeaters }) => {
          setDraftPgaFlag({
            code,
            addedAt: new Date().toISOString(),
            values: seedValues,
            repeaters: seedRepeaters,
            documents: [],
          });
          setCreateOpen(false);
          setCreateInitialHit(null);
          setEditorCode(code);
        }}
      />

      <RecordDrawer record={openRecord} onClose={() => setOpenRecord(null)} />
      <ScoreDrawer
        open={scoreOpen}
        onClose={() => setScoreOpen(false)}
        score={product.complianceScore}
        band={scoreBand}
        bandLabel={scoreBandLabel}
        deductions={scoreDeductions}
      />
      {issuesOpen && (
        <div className='fixed inset-0 z-50 flex'>
          <div
            className='flex-1 bg-black/50'
            onClick={() => setIssuesOpen(false)}
          />
          <aside className='w-full max-w-[520px] bg-card border-l border-[var(--edge-separator)] flex flex-col'>
            <header className='flex items-center justify-between px-4 py-3 border-b border-[var(--edge-separator)]'>
              <div>
                <div className='text-[10px] uppercase tracking-wider text-muted-foreground'>
                  Data quality
                </div>
                <h3 className='text-sm font-semibold'>
                  Data Issues ({dataIssues.length})
                </h3>
              </div>
              <button
                onClick={() => setIssuesOpen(false)}
                className='p-1 rounded hover:bg-accent'
              >
                <X className='size-4' />
              </button>
            </header>
            <div className='flex-1 overflow-auto p-4 space-y-2'>
              {dataIssues.map((d, i) => (
                <div
                  key={i}
                  className='rounded-md p-3 flex items-start gap-2 bg-[color-mix(in_oklch,var(--warning)_12%,transparent)]'
                >
                  <AlertCircle className='size-4 mt-0.5 text-[var(--warning)] shrink-0' />
                  <div className='flex-1'>
                    <span className='text-xs'>{d}</span>
                    {d.includes('HTS') && (
                      <div className='mt-2'>
                        <ClassificationLink
                          productId={product.id}
                          hub='import'
                          compact
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}
      {entriesOpen && (
        <div className='fixed inset-0 z-50 flex'>
          <div
            className='flex-1 bg-black/50'
            onClick={() => setEntriesOpen(false)}
          />
          <aside className='w-full max-w-[520px] bg-card border-l border-[var(--edge-separator)] flex flex-col'>
            <header className='flex items-center justify-between px-4 py-3 border-b border-[var(--edge-separator)]'>
              <div>
                <div className='text-[10px] uppercase tracking-wider text-muted-foreground'>
                  Import entries
                </div>
                <h3 className='text-sm font-semibold'>{product.name}</h3>
              </div>
              <button
                onClick={() => setEntriesOpen(false)}
                className='p-1 rounded hover:bg-accent'
              >
                <X className='size-4' />
              </button>
            </header>
            <div className='flex-1 overflow-auto p-4 space-y-2'>
              {entries.length === 0 ? (
                <p className='text-xs text-muted-foreground italic'>
                  No entry packages recorded for this product.
                </p>
              ) : (
                entries.map((e) => (
                  <div
                    key={e.id}
                    className='rounded-md bg-[var(--bg-surface)] p-3 space-y-1'
                  >
                    <div className='flex items-center justify-between gap-2'>
                      <span className='font-mono text-xs'>{e.entryNumber}</span>
                      <EntryStatusPill status={e.status} />
                    </div>
                    <div className='text-[11px] text-muted-foreground'>
                      {e.portOfEntry} · {formatDate(e.entryDate)}
                    </div>
                    <div className='text-xs tabular-nums'>
                      {formatCurrency(e.value)} · {e.htsCode || 'HTS missing'} ·{' '}
                      {e.countryOfOrigin || 'Origin missing'}
                    </div>
                    <div className='pt-2 mt-2 border-t border-[var(--edge-separator)] space-y-1'>
                      {e.documents.map((doc) => (
                        <div
                          key={doc.id}
                          className='flex items-center gap-2 text-[11px]'
                        >
                          <FileText className='size-3 text-muted-foreground' />
                          <span className='flex-1 truncate'>{doc.type}</span>
                          <span className='text-muted-foreground'>
                            {doc.size}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </aside>
        </div>
      )}
      {riskDetailOpen && (
        <div className='fixed inset-0 z-50 flex'>
          <div
            className='flex-1 bg-black/50'
            onClick={() => setRiskDetailOpen(false)}
          />
          <aside className='w-full max-w-[560px] bg-card border-l border-[var(--edge-separator)] flex flex-col'>
            <header className='flex items-center justify-between px-5 py-3 border-b border-[var(--edge-separator)]'>
              <div>
                <div className='text-[10px] uppercase tracking-wider text-muted-foreground'>
                  Risk
                </div>
                <h3 className='text-sm font-semibold'>AI Risk Evaluation</h3>
              </div>
              <button
                onClick={() => setRiskDetailOpen(false)}
                className='p-1 rounded hover:bg-accent'
              >
                <X className='size-4' />
              </button>
            </header>
            <div className='flex-1 overflow-auto p-5 space-y-3'>
              {effectiveRiskState === 'done' ? (
                <>
                  <div className='flex items-center justify-between gap-2 text-[11px] text-muted-foreground'>
                    <span className='inline-flex items-center gap-1'>
                      <Clock className='size-3' /> Generated at{' '}
                      {effectiveRiskRanAt}
                    </span>
                    {!realProductRow && (
                      <button
                        type='button'
                        onClick={runRisk}
                        className='inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-input bg-background hover:bg-accent text-xs'
                      >
                        <RotateCw className='size-3' /> Re-run
                      </button>
                    )}
                  </div>
                  <RiskSummaryContent detail={detail} />
                </>
              ) : (
                <p className='text-xs text-muted-foreground italic'>
                  {realProductRow
                    ? 'Analyze this product to generate a screening-backed AI risk evaluation.'
                    : 'Run the risk evaluation from the Risk card to see full details here.'}
                </p>
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

/* ─── Small primitives ─── */

function Product360({
  product,
  entries,
  detail,
  htsChange,
  isRealProduct,
  screeningHistory,
  loading,
  error,
  canLoadMore,
  onLoadMore,
}: {
  product: ImportProduct;
  entries: ReturnType<typeof getEntriesForProduct>;
  detail: ProductDetail;
  htsChange?: { from: string; to: string; at: string } | null;
  isRealProduct: boolean;
  screeningHistory: ImportServiceScreeningHistory | null;
  loading: boolean;
  error: string | null;
  canLoadMore: boolean;
  onLoadMore: () => void;
}) {
  return (
    <ProductActivityLog
      product={product}
      entries={entries}
      detail={detail}
      htsChange={htsChange}
      isRealProduct={isRealProduct}
      screeningHistory={screeningHistory}
      loading={loading}
      error={error}
      canLoadMore={canLoadMore}
      onLoadMore={onLoadMore}
    />
  );
}

type ActivitySection =
  | 'classification'
  | 'tariffs'
  | 'flags'
  | 'entities'
  | 'entries'
  | 'compliance'
  | 'documents';

interface ActivityEvent {
  id: string;
  at: string;
  section: ActivitySection;
  icon: any;
  tone: Severity;
  title: string;
  body: string;
  actor: string;
  reason?: string;
  from?: string;
  to?: string;
  causedBy?: string;
  details?: { label: string; value: string }[];
}

const SECTION_META: Record<
  ActivitySection,
  { label: string; tone: Severity; icon: any }
> = {
  classification: { label: 'Classification', tone: 'info', icon: FileText },
  tariffs: { label: 'Duties & Tariffs', tone: 'warning', icon: Percent },
  flags: { label: 'Program Codes', tone: 'danger', icon: Flag },
  entities: { label: 'Entities', tone: 'info', icon: Building2 },
  entries: { label: 'Entry Packages', tone: 'success', icon: Truck },
  compliance: { label: 'Compliance/Risk', tone: 'warning', icon: ShieldAlert },
  documents: { label: 'Documents', tone: 'info', icon: Upload },
};

function buildActivityEvents(
  product: ImportProduct,
  entries: ReturnType<typeof getEntriesForProduct>,
  detail: ProductDetail,
  htsChange?: { from: string; to: string; at: string } | null,
): ActivityEvent[] {
  const now = Date.now();
  const iso = (daysAgo: number) =>
    new Date(now - daysAgo * 86400_000).toISOString();
  const events: ActivityEvent[] = [];

  for (const entry of entries) {
    events.push({
      id: `entry-${entry.id}`,
      at: entry.entryDate,
      section: 'entries',
      icon: Truck,
      tone:
        entry.status === 'Detained' || entry.status === 'Hold'
          ? 'warning'
          : 'success',
      title: `Appeared on Entry Package ${entry.entryNumber}`,
      body: `${entry.portOfEntry} · ${formatCurrency(entry.value)} · HTS ${
        entry.htsCode || '—'
      } · Origin ${entry.countryOfOrigin || '—'}`,
      actor: 'System',
      details: [
        { label: 'Port', value: entry.portOfEntry },
        { label: 'Value', value: formatCurrency(entry.value) },
        { label: 'HTS', value: entry.htsCode || '—' },
        { label: 'Origin', value: entry.countryOfOrigin || '—' },
        { label: 'Documents', value: `${entry.documents.length}` },
        { label: 'Status', value: entry.status },
      ],
    });
  }

  events.push({
    id: 'cls-created',
    at: iso(180),
    section: 'classification',
    icon: FileText,
    tone: 'info',
    title: 'Initial HTS classification assigned',
    body: product.htsCode
      ? `HTS ${product.htsCode} assigned to product.`
      : 'Product created without an HTS classification.',
    actor: 'Jamie Chen',
    reason: 'Manual classification during product onboarding.',
  });

  if (htsChange) {
    events.push({
      id: 'cls-expired',
      at: new Date(
        new Date(htsChange.at).getTime() - 3 * 3600_000,
      ).toISOString(),
      section: 'classification',
      icon: AlertTriangle,
      tone: 'danger',
      title: `HTS code ${htsChange.from} expired`,
      body: `Code ${htsChange.from} was replaced in the current USHTS schedule.`,
      actor: 'System',
      reason: 'Detected via USHTS schedule sync.',
    });
    events.push({
      id: 'cls-updated',
      at: htsChange.at,
      section: 'classification',
      icon: Sparkles,
      tone: 'info',
      title: 'HTS code updated from recommendation',
      body: `${htsChange.from} → ${htsChange.to}`,
      actor: 'Jake Walters',
      reason: 'Selected from recommended codes after expiration.',
      from: htsChange.from,
      to: htsChange.to,
      causedBy: 'cls-expired',
    });
    events.push({
      id: 'duty-recalc',
      at: new Date(new Date(htsChange.at).getTime() + 60_000).toISOString(),
      section: 'tariffs',
      icon: Percent,
      tone: 'info',
      title: 'Duty recalculated',
      body: `Base duty rebuilt because HTS ${htsChange.from} was replaced by ${htsChange.to}.`,
      actor: 'System',
      reason: 'Triggered by HTS code change.',
      causedBy: 'cls-updated',
    });
  }

  events.push({
    id: 'tariff-current',
    at: iso(11),
    section: 'tariffs',
    icon: DollarSign,
    tone: detail.cost.totalDutyCost ? 'warning' : 'success',
    title: 'Tariff and duty result refreshed',
    body: `Current calculated duty is ${formatCurrency(
      detail.cost.totalDutyCost,
    )}.`,
    actor: 'System',
    reason: 'Scheduled tariff schedule sync.',
  });
  if (product.section301) {
    events.push({
      id: 'tariff-301',
      at: iso(60),
      section: 'tariffs',
      icon: AlertCircle,
      tone: 'warning',
      title: `Section 301 remedy applied (+${product.section301Rate}%)`,
      body: `China-origin remedy list match on HTS ${product.htsCode ?? '—'}.`,
      actor: 'System',
      reason: 'USTR remedy list match.',
    });
  }

  const applied = getAppliedFlags(product.id);
  for (const f of applied) {
    events.push({
      id: `flag-${f.code}`,
      at: f.addedAt,
      section: 'flags',
      icon: Flag,
      tone: 'warning',
      title: `PGA flag applied (${f.code})`,
      body: `Datapoints captured for ${f.code} filing.`,
      actor: 'Priya Patel',
      reason: 'Required based on product classification and origin.',
    });
  }

  events.push({
    id: 'ent-linked',
    at: iso(90),
    section: 'entities',
    icon: Building2,
    tone: 'info',
    title: 'Supplier linked to product',
    body: 'Manufacturer linked from Ecosystem entity directory.',
    actor: 'Jamie Chen',
  });

  events.push({
    id: 'risk-current',
    at: iso(3),
    section: 'compliance',
    icon: ShieldAlert,
    tone: detail.riskFlags.length ? 'warning' : 'success',
    title: 'Risk evaluation run',
    body: `${detail.riskFlags.length} risk flag(s) reviewed; risk set to ${product.riskLevel}.`,
    actor: 'System',
    reason: 'Scheduled compliance re-evaluation.',
  });
  events.push({
    id: 'score-change',
    at: iso(20),
    section: 'compliance',
    icon: Award,
    tone: 'info',
    title: `Compliance score changed to ${product.complianceScore}`,
    body: 'Score recalculated after latest classification and screening events.',
    actor: 'System',
  });

  events.push({
    id: 'doc-upload',
    at: iso(15),
    section: 'documents',
    icon: Upload,
    tone: 'info',
    title: 'Document uploaded',
    body: 'CPSC_filing.xml added to product documents.',
    actor: 'Priya Patel',
  });

  return events.sort((a, b) => +new Date(b.at) - +new Date(a.at));
}

function activityMetaForAudit(event: ImportServiceAuditEvent): {
  section: ActivitySection;
  icon: any;
} {
  const value =
    `${event.action} ${event.category} ${event.message}`.toLowerCase();
  if (value.includes('tariff') || value.includes('duty')) {
    return { section: 'tariffs', icon: Percent };
  }
  if (value.includes('document') || value.includes('upload')) {
    return { section: 'documents', icon: FileText };
  }
  if (value.includes('classification') || value.includes('hts')) {
    return { section: 'classification', icon: FileText };
  }
  if (
    value.includes('product.details') ||
    value.includes('product details') ||
    value.includes('product.hierarchy')
  ) {
    return { section: 'classification', icon: PencilLine };
  }
  if (value.includes('entity') || value.includes('denied')) {
    return { section: 'entities', icon: Building2 };
  }
  if (value.includes('flag') || value.includes('program')) {
    return { section: 'flags', icon: Flag };
  }
  return { section: 'compliance', icon: ShieldAlert };
}

// Audit metadata is recorded for backend correlation and can carry internal
// identifiers (user/product/database ids) — never surface those in the UI.
const isIdLikeMetadataKey = (key: string): boolean => {
  const normalized = key.replace(/([a-z0-9])([A-Z])/g, '$1_$2');
  return /(^|[_-])ids?$/i.test(normalized);
};

const activityMetadataDetails = (
  event: ImportServiceAuditEvent,
): { label: string; value: string }[] | undefined => {
  const metadata = event.metadata;
  if (!metadata) return undefined;

  const fieldChanges = metadata.field_changes;
  if (
    event.action === 'product.details.updated' &&
    fieldChanges &&
    typeof fieldChanges === 'object' &&
    !Array.isArray(fieldChanges)
  ) {
    const details = Object.entries(
      fieldChanges as Record<string, { from?: unknown; to?: unknown }>,
    ).map(([field, change]) => ({
      label: field.replace(/_/g, ' '),
      value: `${String(change?.from ?? '—')} → ${String(change?.to ?? '—')}`,
    }));
    if (details.length > 0) return details;
  }

  const details = Object.entries(metadata)
    .filter(
      ([key, value]) =>
        (typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean') &&
        !isIdLikeMetadataKey(key),
    )
    .slice(0, 8)
    .map(([label, value]) => ({
      label: label.replace(/_/g, ' '),
      value: String(value),
    }));
  return details.length > 0 ? details : undefined;
};

function buildRealActivityEvents(
  history: ImportServiceScreeningHistoryEntry[],
  auditEvents: ImportServiceAuditEvent[],
  htsChange?: { from: string; to: string; at: string } | null,
): ActivityEvent[] {
  const events: ActivityEvent[] = history.map((run) => {
    const status = run.status?.toLowerCase() ?? 'complete';
    const hits = Number(run.hit_count ?? 0);
    const added = Number(run.diff_added_count ?? 0);
    const removed = Number(run.diff_removed_count ?? 0);
    const modified = Number(run.diff_modified_count ?? 0);
    const completedAt = run.completed_at || run.started_at;
    const title =
      status === 'failed'
        ? 'Product screening failed'
        : status === 'in-progress'
        ? 'Product screening in progress'
        : run.is_base_snapshot
        ? 'Initial product screening completed'
        : 'Product screening completed';
    const body =
      status === 'failed'
        ? run.error || 'The screening run did not complete.'
        : `${hits} screening hit${hits === 1 ? '' : 's'} recorded${
            run.is_base_snapshot
              ? ' as the baseline'
              : ` (${added} added, ${removed} removed, ${modified} modified)`
          }.`;

    return {
      id: `screening-${run.id}`,
      at: completedAt,
      section: 'compliance',
      icon: status === 'failed' ? XCircle : Search,
      tone:
        status === 'failed'
          ? 'danger'
          : hits > 0
          ? 'warning'
          : 'success',
      title,
      body,
      // `screened_by` is a raw user ID for user-triggered runs (or the
      // literal string "system" for automated ones) — never surface the ID.
      actor:
        !run.screened_by || run.screened_by.toLowerCase() === 'system'
          ? 'System'
          : 'Team member',
      reason: run.screening_reason || undefined,
      details: [
        { label: 'Status', value: run.status || 'complete' },
        { label: 'Total hits', value: String(hits) },
        { label: 'New hits', value: String(run.new_total ?? 0) },
        { label: 'AI status', value: run.ai_status || '—' },
        {
          label: 'Verification',
          value: run.verification_status || '—',
        },
      ],
    };
  });

  const richScreeningTimes = history
    .map((run) => new Date(run.completed_at || run.started_at).getTime())
    .filter(Number.isFinite);

  for (const event of auditEvents) {
    // The richer ProductScreeningResult row above represents these same
    // screening events and includes the hit/diff details, so do not duplicate
    // the terse audit item in the timeline. Unchanged runs do not create a new
    // ProductScreeningResult row, so their audit item remains the authoritative
    // history entry and must still be shown.
    if (event.action === 'product.screening.product_screened') {
      const auditTime = new Date(event.occurred_at).getTime();
      const hasRichEntry = richScreeningTimes.some(
        (screeningTime) => Math.abs(screeningTime - auditTime) < 10 * 60_000,
      );
      if (hasRichEntry) continue;
    }

    const meta = activityMetaForAudit(event);
    const tariffHts =
      typeof event.metadata?.hts_code === 'string'
        ? formatHtsPretty(event.metadata.hts_code)
        : null;
    events.push({
      id: `audit-${event.id}`,
      at: event.occurred_at,
      section: meta.section,
      icon: meta.icon,
      tone: event.level >= 2 ? 'danger' : 'info',
      title: event.message || 'Product activity recorded',
      body:
        event.action === 'product.details.updated'
          ? 'Saved changes to this product record.'
          : event.action.startsWith('product.hierarchy.')
          ? 'Updated where this product sits in the bill of materials.'
          : event.action.startsWith('product.tariff.') && tariffHts
          ? `Tariff result recorded for HTS ${tariffHts}.`
          : event.action
              .split('.')
              .map((part) => part.replace(/_/g, ' '))
              .join(' · '),
      actor: event.user_name || (event.is_system ? 'System' : 'Unknown user'),
      reason: event.category || undefined,
      details: activityMetadataDetails(event),
    });
  }

  if (htsChange) {
    events.push({
      id: `classification-${htsChange.at}`,
      at: htsChange.at,
      section: 'classification',
      icon: FileText,
      tone: 'info',
      title: 'HTS classification updated',
      body: `${htsChange.from} → ${htsChange.to}`,
      actor: 'Current user',
      reason: 'Saved during this session.',
      from: htsChange.from,
      to: htsChange.to,
    });
  }

  return events.sort((a, b) => +new Date(b.at) - +new Date(a.at));
}

function ProductActivityLog({
  product,
  entries,
  detail,
  htsChange,
  isRealProduct,
  screeningHistory,
  loading,
  error,
  canLoadMore,
  onLoadMore,
}: {
  product: ImportProduct;
  entries: ReturnType<typeof getEntriesForProduct>;
  detail: ProductDetail;
  htsChange?: { from: string; to: string; at: string } | null;
  isRealProduct: boolean;
  screeningHistory: ImportServiceScreeningHistory | null;
  loading: boolean;
  error: string | null;
  canLoadMore: boolean;
  onLoadMore: () => void;
}) {
  const events = useMemo(
    () =>
      isRealProduct
        ? buildRealActivityEvents(
            screeningHistory?.history ?? [],
            screeningHistory?.auditEvents ?? [],
            htsChange,
          )
        : buildActivityEvents(product, entries, detail, htsChange),
    [product, entries, detail, htsChange, isRealProduct, screeningHistory],
  );
  const [filter, setFilter] = useState<ActivitySection | 'all'>('all');
  const [query, setQuery] = useState('');
  const [groupMode, setGroupMode] = useState<'chronological' | 'section'>(
    'chronological',
  );
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const sections: (ActivitySection | 'all')[] = [
    'all',
    'classification',
    'tariffs',
    'flags',
    'entities',
    // 'entries', // Entry Packages: hidden until real functionality is wired up.
    'compliance',
    'documents',
  ];

  const filtered = events.filter((e) => {
    if (filter !== 'all' && e.section !== filter) return false;
    if (query.trim()) {
      const q = query.toLowerCase();
      const hay = `${e.title} ${e.body} ${e.actor} ${e.reason ?? ''} ${
        e.from ?? ''
      } ${e.to ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const grouped: [ActivitySection, ActivityEvent[]][] =
    groupMode === 'section'
      ? (Object.keys(SECTION_META) as ActivitySection[])
          .map(
            (s) =>
              [s, filtered.filter((e) => e.section === s)] as [
                ActivitySection,
                ActivityEvent[],
              ],
          )
          .filter(([, list]) => list.length > 0)
      : [];

  const causeMap = new Map(events.map((e) => [e.id, e]));

  return (
    <section className='ih-activity-log'>
      <div className='ih-activity-header'>
        <div>
          <h2 className='text-base font-semibold'>Product activity</h2>
          <p className='text-xs text-muted-foreground mt-1'>
            {isRealProduct
              ? 'Screening runs and recorded audit events for this product.'
              : 'Product changes, screenings, duties, documents, and compliance status.'}
          </p>
        </div>
        {!product.htsCode && (
          <ClassificationLink productId={product.id} hub='import' />
        )}
      </div>

      <div className='elms-card ih-activity-card'>
        <div className='ih-activity-toolbar'>
          <div className='relative ih-activity-search'>
            <Search className='absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground' />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Search events, HTS codes, users, and results…'
              className='w-full h-8 pl-7 pr-2 rounded-md border border-input bg-background text-xs focus:outline-none focus:border-primary'
            />
          </div>
          <div className='inline-flex rounded-md border border-input overflow-hidden text-xs'>
            <button
              type='button'
              onClick={() => setGroupMode('chronological')}
              className={cn(
                'px-2.5 h-8 font-medium',
                groupMode === 'chronological'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background hover:bg-accent',
              )}
            >
              Chronological
            </button>
            <button
              type='button'
              onClick={() => setGroupMode('section')}
              className={cn(
                'px-2.5 h-8 font-medium border-l border-input',
                groupMode === 'section'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background hover:bg-accent',
              )}
            >
              By section
            </button>
          </div>
        </div>

        <div className='ih-activity-filters'>
          {sections.map((s) => {
            const label = s === 'all' ? 'All' : SECTION_META[s].label;
            const count =
              s === 'all'
                ? events.length
                : events.filter((e) => e.section === s).length;
            const active = filter === s;
            return (
              <button
                key={s}
                type='button'
                onClick={() => setFilter(s)}
                className={cn(
                  'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[11px] font-medium transition-colors',
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-input hover:bg-accent',
                )}
              >
                {label}
                <span
                  className={cn(
                    'tabular-nums text-[10px]',
                    active ? 'opacity-90' : 'text-muted-foreground',
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className='ih-activity-empty'>
            <RotateCw className='size-4 animate-spin' />
            Loading product activity…
          </div>
        ) : error ? (
          <div className='ih-activity-empty text-[var(--danger)]'>
            <AlertCircle className='size-4' />
            {error}
          </div>
        ) : filtered.length === 0 ? (
          <div className='text-xs text-muted-foreground italic py-6 text-center'>
            {events.length === 0
              ? 'No recorded activity is available for this product yet.'
              : 'No events match your filters.'}
          </div>
        ) : groupMode === 'section' ? (
          <div className='space-y-4'>
            {grouped.map(([sec, list]) => (
              <div key={sec}>
                <div className='flex items-center gap-2 mb-2'>
                  <StatusPill
                    tone={SECTION_META[sec].tone}
                    label={SECTION_META[sec].label}
                  />
                  <span className='text-[10px] text-muted-foreground tabular-nums'>
                    {list.length} event{list.length === 1 ? '' : 's'}
                  </span>
                </div>
                <TimelineList
                  events={list}
                  expanded={expanded}
                  setExpanded={setExpanded}
                  causeMap={causeMap}
                />
              </div>
            ))}
          </div>
        ) : (
          <TimelineList
            events={filtered}
            expanded={expanded}
            setExpanded={setExpanded}
            causeMap={causeMap}
          />
        )}
        {!loading && !error && isRealProduct && canLoadMore && (
          <div className='flex justify-center pt-4'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={onLoadMore}
            >
              Load more activity
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

function TimelineList({
  events,
  expanded,
  setExpanded,
  causeMap,
}: {
  events: ActivityEvent[];
  expanded: Record<string, boolean>;
  setExpanded: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  causeMap: Map<string, ActivityEvent>;
}) {
  return (
    <ol className='ih-activity-timeline'>
      {events.map((event) => {
        const EventIcon = event.icon;
        const meta = SECTION_META[event.section];
        const isOpen = !!expanded[event.id];
        const cause = event.causedBy ? causeMap.get(event.causedBy) : null;
        return (
          <li key={event.id} className='ih-activity-item'>
            <span
              className={cn(
                'ih-activity-icon',
                SEV_BG[event.tone],
                SEV_TEXT[event.tone],
              )}
            >
              <EventIcon className='size-3.5' />
            </span>
            <button
              type='button'
              onClick={() =>
                setExpanded((s) => ({ ...s, [event.id]: !s[event.id] }))
              }
              className='ih-activity-event'
            >
              <div className='ih-activity-event-heading'>
                <div className='ih-activity-event-copy'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <span className='text-sm font-semibold'>{event.title}</span>
                    <StatusPill tone={meta.tone} label={meta.label} />
                  </div>
                  <p className='text-xs text-muted-foreground mt-0.5'>
                    {event.body}
                  </p>
                  <div className='text-[10px] text-muted-foreground mt-1 flex items-center gap-2 flex-wrap'>
                    <span>By {event.actor}</span>
                    {event.reason && <span>· {event.reason}</span>}
                    {cause && (
                      <span className='inline-flex items-center gap-1 text-[var(--action-color)]'>
                        · because “{cause.title}”
                      </span>
                    )}
                  </div>
                </div>
                <div className='ih-activity-event-time'>
                  <time className='text-[10px] text-muted-foreground tabular-nums'>
                    {formatDate(event.at)}
                  </time>
                  <ChevronDown
                    className={cn(
                      'size-3 text-muted-foreground transition-transform',
                      isOpen && 'rotate-180',
                    )}
                  />
                </div>
              </div>
            </button>
            {isOpen && (
              <div className='ih-activity-details space-y-2'>
                {(event.from || event.to) && (
                  <div className='flex items-center gap-2'>
                    <span className='text-muted-foreground uppercase tracking-wider text-[10px]'>
                      Change
                    </span>
                    <span className='font-mono'>{event.from ?? '—'}</span>
                    <span className='text-muted-foreground'>→</span>
                    <span className='font-mono font-semibold'>
                      {event.to ?? '—'}
                    </span>
                  </div>
                )}
                {event.details && (
                  <div className='grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1'>
                    {event.details.map((d) => (
                      <div key={d.label} className='flex flex-col'>
                        <span className='text-[9px] uppercase tracking-wider text-muted-foreground'>
                          {d.label}
                        </span>
                        <span className='font-medium'>
                          {renderFieldWithLinks(d.value)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div className='text-[10px] text-muted-foreground'>
                  {new Date(event.at).toLocaleString()}
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function HeaderBtn({
  icon: Icon,
  children,
  primary,
  onClick,
  disabled,
  spinning,
  locked,
}: {
  icon: any;
  children: React.ReactNode;
  primary?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  spinning?: boolean;
  /** Disabled due to a sub-Business license, rendered with a lock icon. */
  locked?: boolean;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      disabled={disabled || locked}
      title={locked ? 'Requires a Business license' : undefined}
      className={cn(
        'inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium transition-colors',
        primary
          ? 'bg-primary text-primary-foreground hover:bg-primary/90'
          : 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        (disabled || locked) && 'opacity-60 cursor-not-allowed',
      )}
    >
      {locked ? (
        <Lock className='size-3.5' />
      ) : (
        <Icon className={cn('size-3.5', spinning && 'animate-spin')} />
      )}{' '}
      {children}
    </button>
  );
}

function SectionRefreshBtn({
  loading,
  onClick,
  locked,
}: {
  loading?: boolean;
  onClick?: () => void;
  /** This refresh actually re-runs screening — gate it in read-only mode. */
  locked?: boolean;
}) {
  return (
    <button
      type='button'
      onClick={locked ? undefined : onClick}
      disabled={loading || locked}
      title={locked ? 'Requires a Business license' : undefined}
      className='inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-input bg-background hover:bg-accent text-xs font-medium transition-colors disabled:opacity-60'
    >
      {locked ? (
        <Lock className='size-3' />
      ) : (
        <RefreshCw className={cn('size-3', loading && 'animate-spin')} />
      )}
      {loading ? 'Refreshing…' : 'Get latest results'}
    </button>
  );
}

function DateField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className='space-y-0.5'>
      <div
        style={{ fontFamily: 'Inter, sans-serif', fontSize: 11 }}
        className='text-muted-foreground uppercase tracking-wider font-medium'
      >
        {label}
      </div>
      <input
        type='date'
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={
          disabled
            ? {
                fontFamily: 'Inter, sans-serif',
                fontSize: 13,
                fontWeight: 600,
              }
            : undefined
        }
        className={
          disabled
            ? 'w-full bg-transparent border border-transparent px-0 py-1 text-foreground disabled:opacity-100'
            : EDIT_INPUT_CLASS
        }
      />
    </div>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  tone?: Severity;
}) {
  return (
    <div className='flex items-center justify-between gap-3'>
      <span className='text-muted-foreground'>{label}</span>
      <span className={cn('font-medium tabular-nums', tone && SEV_TEXT[tone])}>
        {value}
      </span>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  const empty = !value || value === '—' || value === 'N/A';
  return (
    <div className='ih-field space-y-0.5'>
      <div
        style={{ fontFamily: 'Inter, sans-serif', fontSize: 11 }}
        className='ih-field-label text-muted-foreground uppercase tracking-wider font-medium'
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: 13,
          fontWeight: 600,
        }}
        className={cn(
          'ih-field-value text-foreground',
          mono && 'font-mono',
          empty && 'text-muted-foreground italic font-normal',
        )}
      >
        {value ? renderFieldWithLinks(value) : '—'}
      </div>
    </div>
  );
}

function FieldGridCard({
  title,
  children,
  action,
  columns = 2,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  columns?: 2 | 3;
}) {
  return (
    <div className='elms-card'>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-sm font-semibold'>{title}</h3>
        {action}
      </div>
      <div
        className={cn(
          'grid gap-x-4 gap-y-3',
          columns === 3 ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2',
        )}
      >
        {children}
      </div>
    </div>
  );
}

function EntityRow({
  icon: Icon,
  role,
  entity,
}: {
  icon: any;
  role: string;
  entity: ReturnType<typeof getImportEntity>;
}) {
  if (!entity) {
    return (
      <div className='rounded-md bg-[var(--bg-surface)] p-2 text-xs text-muted-foreground'>
        <div className='flex items-center gap-1.5'>
          <Icon className='size-3.5' /> {role}
        </div>
        <div className='mt-0.5 italic'>Not assigned</div>
      </div>
    );
  }
  const tierTone =
    entity.riskTier === 'high'
      ? 'badge-danger'
      : entity.riskTier === 'medium'
      ? 'badge-warning'
      : 'badge-success';
  return (
    <div className='rounded-md bg-[var(--bg-surface)] p-2'>
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground'>
          <Icon className='size-3' /> {role}
        </div>
        <span
          className={cn(
            'inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5',
            tierTone,
          )}
        >
          {entity.riskTier}
        </span>
      </div>
      <div className='mt-1 text-xs font-medium'>{entity.name}</div>
      <div className='text-[10px] text-muted-foreground'>{entity.country}</div>
    </div>
  );
}

type Status = 'passed' | 'failed' | 'review';
function CheckCard({
  icon: Icon,
  type,
  description,
  status,
  onOpen,
}: {
  icon: any;
  type: CheckType;
  description: string;
  status: Status;
  onOpen: () => void;
}) {
  const meta =
    status === 'passed'
      ? { cls: 'badge-success', label: 'Passed', Icon: CheckCircle2 }
      : status === 'failed'
      ? { cls: 'badge-danger', label: 'Failed', Icon: XCircle }
      : { cls: 'badge-warning', label: 'Review', Icon: AlertTriangle };
  const SIcon = meta.Icon;
  return (
    <button
      onClick={onOpen}
      className='elms-card w-full text-left !p-0 overflow-hidden block hover:bg-[var(--row-hover)] transition-colors'
    >
      <div className='px-4 py-3 flex items-center gap-3'>
        <div className='size-9 rounded-md bg-[var(--bg-surface)] flex items-center justify-center text-primary shrink-0'>
          <Icon className='size-4' />
        </div>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center justify-between gap-2 flex-wrap'>
            <div className='text-sm font-medium'>{type}</div>
            <span
              className={cn(
                'inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5',
                meta.cls,
              )}
            >
              <SIcon className='size-3' /> {meta.label}
            </span>
          </div>
          <p className='text-[11px] text-muted-foreground mt-0.5 truncate'>
            {description}
          </p>
        </div>
        <RotateCw className='size-3.5 text-muted-foreground shrink-0' />
      </div>
    </button>
  );
}

function StatusPill({ tone, label }: { tone: Severity; label: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5',
        SEV_BADGE[tone],
      )}
    >
      {label}
    </span>
  );
}

function ClickableCheckCard({
  icon: Icon,
  type,
  summary,
  pill,
  onOpen,
}: {
  icon: any;
  type: string;
  summary: string;
  pill: React.ReactNode;
  onOpen: () => void;
}) {
  return (
    <div className='elms-card !p-0 overflow-hidden'>
      <button
        type='button'
        onClick={onOpen}
        className='w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[var(--row-hover)] transition-colors'
      >
        <div className='size-9 rounded-md bg-[var(--bg-surface)] flex items-center justify-center text-primary shrink-0'>
          <Icon className='size-4' />
        </div>
        <div className='flex-1 min-w-0'>
          <div className='flex items-center justify-between gap-2 flex-wrap'>
            <div className='text-sm font-medium'>{type}</div>
            {pill}
          </div>
          <p className='text-[11px] text-muted-foreground mt-0.5 truncate'>
            {summary}
          </p>
        </div>
        <ChevronRight className='size-3.5 text-muted-foreground shrink-0' />
      </button>
    </div>
  );
}

/* ─── AI Summary ─── */

function formatAiConfidence(value?: number | null): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const percentage = value <= 1 ? value * 100 : value;
  return `${Math.round(Math.max(0, Math.min(100, percentage)))}%`;
}

/* ─── Screening / BITE Lens ─── */

interface ScreeningMatch {
  entity: string;
  type: string;
  country: string;
  lensName: string;
  lensCode: string;
  list: string;
  score: string;
  date: string;
  raw: Record<string, string | number>;
}

function flattenMatches(groups: BiteLensGroup[]): ScreeningMatch[] {
  const out: ScreeningMatch[] = [];
  groups.forEach((g) => {
    const code = formatEntityLensCode(g.name);
    g.rows.forEach((r) => {
      const entity = String(
        r['Entity'] ??
          r['Name of Entity'] ??
          r['Product Name'] ??
          r['Name'] ??
          r['Party'] ??
          r['HS Code'] ??
          r['Program'] ??
          r['Agency'] ??
          '—',
      );
      const country = String(
        r['Source Country'] ??
          r['List Country'] ??
          r['Country'] ??
          r['Origin'] ??
          '—',
      );
      const list = String(
        r['List Name'] ??
          r['List'] ??
          r['Source'] ??
          r['Program'] ??
          r['Agency'] ??
          g.name,
      );
      const date = String(
        r['Date Updated on BITE Platform'] ??
          r['Date Added'] ??
          r['updated_at'] ??
          '—',
      );
      const score = String(r['Match Score'] ?? r['Score'] ?? '—');
      out.push({
        entity,
        type: String(r['Entity Type'] ?? r['Type'] ?? 'Record'),
        country,
        lensName: g.name,
        lensCode: code,
        list,
        score,
        date,
        raw: r,
      });
    });
  });
  return out;
}

function toPgaHitOption(match: ScreeningMatch): PgaHitOption | null {
  if (match.lensCode !== 'PGA') return null;
  const code = resolvePgaCode(match.raw);
  if (!code) return null;
  return {
    id: buildPgaHitId(code, match.raw),
    code,
    listName: match.list,
    agency: PGA_FLAG_SCHEMAS[code].agency,
    raw: match.raw,
  };
}

type HitSortKey =
  | 'productName'
  | 'hsCode'
  | 'listName'
  | 'agency'
  | 'pgaFlagCode';
interface HitRow {
  productName: string;
  hsCode: string;
  listName: string;
  agency: string;
  pgaFlagCode: string;
  raw: Record<string, string | number>;
  hit: PgaHitOption | null;
}

function Pagination({
  page,
  pageCount,
  onChange,
}: {
  page: number;
  pageCount: number;
  onChange: (p: number) => void;
}) {
  const visible: (number | '…')[] = [];
  for (let i = 1; i <= pageCount; i++) {
    if (i <= 3 || i === pageCount || Math.abs(i - page) <= 1) visible.push(i);
    else if (visible[visible.length - 1] !== '…') visible.push('…');
  }
  return (
    <div className='flex items-center gap-1'>
      <button
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className='inline-flex items-center justify-center size-7 rounded-md border border-input bg-background hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed'
      >
        <ChevronRight className='size-3.5 rotate-180' />
      </button>
      {visible.map((v, i) =>
        v === '…' ? (
          <span key={`e${i}`} className='px-1 text-xs text-muted-foreground'>
            …
          </span>
        ) : (
          <button
            key={v}
            onClick={() => onChange(v)}
            className={cn(
              'inline-flex items-center justify-center size-7 rounded-full text-xs font-medium transition-colors',
              v === page
                ? 'border border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent',
            )}
          >
            {v}
          </button>
        ),
      )}
      <button
        disabled={page >= pageCount}
        onClick={() => onChange(page + 1)}
        className='inline-flex items-center justify-center size-7 rounded-md border border-input bg-background hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed'
      >
        <ChevronRight className='size-3.5' />
      </button>
    </div>
  );
}

/* ─── Tariff & Duty Summary ─── */

type TariffBoolMap = Record<string, boolean>;

interface TariffExclusionCandidate {
  codeDigits?: string;
  codePretty?: string;
  label?: string;
  rateDisplay?: string;
  ratePct?: number;
  note?: string;
  bd_232_question?: string;
  bd_232_answer?: string;
}

interface TariffQuestionOption {
  id: string;
  label: string;
  enablesExclusions: string[];
}

interface TariffExclusionQuestion {
  id: string;
  prompt: string;
  helperText: string;
  options: TariffQuestionOption[];
}

const tariffNumber = (value: unknown, fallback = 0): number => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim()
      ? Number(value)
      : NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
};

const tariffRatePct = (rate: unknown): number => {
  const matches = String(rate ?? '').matchAll(/(-?\d+(?:\.\d+)?)\s*%/g);
  let total = 0;
  for (const match of matches) total += tariffNumber(match[1]);
  return total;
};

const tariffRateDisplay = (rate: unknown): string => {
  const text = String(rate ?? '').trim();
  return text || '—';
};

const isPureAdValoremTariffRate = (rate: unknown): boolean => {
  const text = String(rate ?? '').trim();
  if (!text || text.toLowerCase() === 'free') return true;
  return (
    text.replace(/-?\d+(?:\.\d+)?\s*%/g, '').replace(/[+\s,;()]/g, '')
      .length === 0
  );
};

const tariffCodeDigits = (code: unknown): string =>
  String(code ?? '').replace(/\D/g, '');

const formatTariffCode = (code: unknown): string => {
  const digits = tariffCodeDigits(code).slice(0, 10);
  if (!digits) return String(code ?? '');
  return [
    digits.slice(0, 4),
    digits.slice(4, 6),
    digits.slice(6, 8),
    digits.slice(8, 10),
  ]
    .filter(Boolean)
    .join('.');
};

const normalizeTariffBoolMap = (value: unknown): TariffBoolMap => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, enabled]) => [
      key,
      Boolean(enabled),
    ]),
  );
};

const tariffCalculationIsAvailable = (value: unknown): boolean =>
  Boolean(value) &&
  (value as Record<string, any>)?.insights?.tariffCalculationAvailable !==
    false;

const formatTariffCurrency = (value: unknown): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(tariffNumber(value));

const buildTariffExclusionQuestions = (
  candidates: TariffExclusionCandidate[],
): TariffExclusionQuestion[] => {
  const groups = new Map<string, TariffQuestionOption[]>();

  for (const candidate of candidates) {
    const prompt = candidate.bd_232_question?.trim();
    const rawAnswer = candidate.bd_232_answer?.trim();
    const code = tariffCodeDigits(candidate.codeDigits ?? candidate.codePretty);
    if (!prompt || !rawAnswer || !code) continue;

    const answerId = rawAnswer.toLowerCase();
    const options = groups.get(prompt) ?? [];
    let option = options.find((item) => item.id === answerId);
    if (!option) {
      option = {
        id: answerId,
        label: rawAnswer.charAt(0).toUpperCase() + rawAnswer.slice(1),
        enablesExclusions: [],
      };
      options.push(option);
    }
    if (!option.enablesExclusions.includes(code)) {
      option.enablesExclusions.push(code);
    }
    groups.set(prompt, options);
  }

  const questions = [...groups.entries()].map(
    ([prompt, options], index): TariffExclusionQuestion => ({
      id: `generated-q-${index}`,
      prompt,
      helperText:
        'Select the option that describes your product to apply the correct tariff or exclusion.',
      options: [...options].sort((a, b) => {
        if (a.id === 'yes') return -1;
        if (b.id === 'yes') return 1;
        return a.label.localeCompare(b.label);
      }),
    }),
  );

  const vehiclePrompt =
    'Is it an auto part or heavy duty vehicle part or neither?';
  const hasVehicleCodes = candidates.some((candidate) =>
    ['99037411', '99039406'].includes(
      tariffCodeDigits(candidate.codeDigits ?? candidate.codePretty),
    ),
  );
  if (!hasVehicleCodes) return questions;

  return [
    ...questions.filter((question) => question.prompt !== vehiclePrompt),
    {
      id: 'manual-232-autopart',
      prompt: vehiclePrompt,
      helperText:
        'Select the option that describes your product to apply the correct tariff or exclusion.',
      options: [
        {
          id: 'auto_part',
          label: 'Auto Part',
          enablesExclusions: ['99037411'],
        },
        {
          id: 'heavy_duty',
          label: 'Heavy Duty Vehicle Part',
          enablesExclusions: [],
        },
        {
          id: 'neither',
          label: 'Neither',
          enablesExclusions: ['99037411', '99039406'],
        },
      ],
    },
  ];
};

function TariffToggle({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className={cn('ih-tariff-toggle', disabled && 'is-disabled')}>
      <input
        type='checkbox'
        checked={checked}
        disabled={disabled}
        aria-label={label}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className='ih-tariff-toggle-track' aria-hidden='true'>
        <span className='ih-tariff-toggle-thumb' />
      </span>
    </label>
  );
}

function TariffSummarySection({
  detail,
  product,
  databaseProductId,
  initialTariffResult,
  onProductSaved,
  entryDate,
  riskFlagsLoading,
  riskFlagsError,
  htsClassification,
  htsClassificationLoading,
  htsClassificationError,
}: {
  detail: ProductDetail;
  product: ImportProduct;
  databaseProductId?: string | null;
  initialTariffResult?: any;
  onProductSaved?: (product: ImportServiceProduct) => void;
  entryDate: string;
  riskFlagsLoading?: boolean;
  riskFlagsError?: string | null;
  htsClassification?: ImportHtsClassification | null;
  htsClassificationLoading?: boolean;
  htsClassificationError?: string | null;
}) {
  const readOnly = useImportHubReadOnly();
  const savedHtsClassification = initialTariffResult?.insights
    ?.htsClassification as ImportHtsClassification | undefined;
  const resolvedHtsClassification =
    htsClassification ?? savedHtsClassification ?? null;
  const htsDigits = String(product.htsCode ?? '').replace(/\D/g, '');
  const htsClassificationUnknown = Boolean(
    databaseProductId &&
      htsDigits.length === 10 &&
      !resolvedHtsClassification &&
      htsClassificationError,
  );
  const canUseSavedTariffWhileUnverified = Boolean(
    htsClassificationUnknown &&
      tariffCalculationIsAvailable(initialTariffResult),
  );
  const htsValidForTariffs = databaseProductId
    ? htsDigits.length === 10 &&
      (resolvedHtsClassification?.valid === true ||
        canUseSavedTariffWhileUnverified)
    : true;
  const [tariffResult, setTariffResult] = useState<any>(
    initialTariffResult ?? null,
  );
  const [explanationContext, setExplanationContext] =
    useState<ImportTariffExplanationContext | null>(null);
  const [enabledExclusions, setEnabledExclusions] = useState<TariffBoolMap>(
    normalizeTariffBoolMap(initialTariffResult?.results?.enabledExclusions),
  );
  const [enabledFtas, setEnabledFtas] = useState<TariffBoolMap>(
    normalizeTariffBoolMap(initialTariffResult?.results?.enabledFtas),
  );
  const [clearedExclusionQuestions, setClearedExclusionQuestions] = useState<
    Set<string>
  >(() => new Set());
  const [recalculating, setRecalculating] = useState(false);
  const [explanationText, setExplanationText] = useState('');
  const [explanationLoading, setExplanationLoading] = useState(false);
  const [previewUnavailableReason, setPreviewUnavailableReason] = useState<
    string | null
  >(null);
  const previewRequestRef = useRef(0);
  const explanationRequestRef = useRef(0);

  useEffect(() => {
    const initialExclusions = normalizeTariffBoolMap(
      initialTariffResult?.results?.enabledExclusions,
    );
    const initialFtas = normalizeTariffBoolMap(
      initialTariffResult?.results?.enabledFtas,
    );
    setTariffResult(initialTariffResult ?? null);
    setEnabledExclusions(initialExclusions);
    setEnabledFtas(initialFtas);
    setClearedExclusionQuestions(new Set());
    setExplanationContext(null);
    setExplanationText('');
    setExplanationLoading(false);
    setPreviewUnavailableReason(null);
    explanationRequestRef.current += 1;

    if (!databaseProductId || htsClassificationLoading) return;
    if (!htsValidForTariffs) {
      setRecalculating(false);
      return;
    }
    const requestId = ++previewRequestRef.current;
    setRecalculating(true);
    fetchImportProductTariffPreview(databaseProductId, {
      enabledExclusions: initialExclusions,
      enabledFtas: initialFtas,
    })
      .then(({ success, preview, error }) => {
        if (requestId !== previewRequestRef.current) return;
        if (success && preview?.tariffResult) {
          setTariffResult(preview.tariffResult);
          setExplanationContext(preview.explanationContext);
          setPreviewUnavailableReason(preview.unavailableReason ?? null);
          setEnabledExclusions(
            normalizeTariffBoolMap(
              preview.tariffResult.results?.enabledExclusions,
            ),
          );
          setEnabledFtas(
            normalizeTariffBoolMap(preview.tariffResult.results?.enabledFtas),
          );
        } else if (success && preview?.unavailableReason) {
          setTariffResult(null);
          setExplanationContext(null);
          setPreviewUnavailableReason(preview.unavailableReason);
        } else if (!initialTariffResult) {
          toast.error(error ?? 'Failed to load tariff results');
        }
      })
      .catch(() => {
        if (requestId === previewRequestRef.current && !initialTariffResult) {
          toast.error('Failed to load tariff results');
        }
      })
      .finally(() => {
        if (requestId === previewRequestRef.current) {
          setRecalculating(false);
        }
      });

    return () => {
      previewRequestRef.current += 1;
    };
  }, [
    databaseProductId,
    htsClassificationLoading,
    htsValidForTariffs,
    initialTariffResult,
  ]);

  const recalculate = async (
    nextExclusions: TariffBoolMap,
    nextFtas: TariffBoolMap,
    previousExclusions: TariffBoolMap,
    previousFtas: TariffBoolMap,
  ): Promise<boolean> => {
    if (!databaseProductId) {
      setEnabledExclusions(nextExclusions);
      setEnabledFtas(nextFtas);
      return true;
    }

    const requestId = ++previewRequestRef.current;
    setEnabledExclusions(nextExclusions);
    setEnabledFtas(nextFtas);
    setRecalculating(true);
    try {
      const {
        success,
        product: savedProduct,
        tariffResult: savedTariffResult,
        explanationContext: savedContext,
        error,
      } = await saveImportProductDetails(databaseProductId, {
        enabledExclusions: nextExclusions,
        enabledFtas: nextFtas,
      });
      if (requestId !== previewRequestRef.current) return false;

      if (!success || !savedTariffResult) {
        setEnabledExclusions(previousExclusions);
        setEnabledFtas(previousFtas);
        toast.error(error ?? 'Failed to save tariff selections');
        return false;
      }

      // The parent feeds tariff_result back through the initialization effect,
      // so keep the confirmed user selections on that shared snapshot too.
      const confirmedTariffResult = {
        ...savedTariffResult,
        results: {
          ...(savedTariffResult.results ?? {}),
          enabledExclusions: nextExclusions,
          enabledFtas: nextFtas,
        },
      };
      setTariffResult(confirmedTariffResult);
      setExplanationContext(savedContext ?? null);
      // Keep the requested maps as the controlled switch state. The calculator
      // may omit false keys, but the corresponding option must remain off.
      setEnabledExclusions(nextExclusions);
      setEnabledFtas(nextFtas);
      setExplanationText('');
      if (savedProduct) {
        onProductSaved?.({
          ...savedProduct,
          tariff_result: confirmedTariffResult,
        });
      }
      return true;
    } catch (error) {
      if (requestId !== previewRequestRef.current) return false;
      setEnabledExclusions(previousExclusions);
      setEnabledFtas(previousFtas);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to save tariff selections',
      );
      return false;
    } finally {
      if (requestId === previewRequestRef.current) {
        setRecalculating(false);
      }
    }
  };

  const toggleExclusion = (code: string, checked: boolean) => {
    if (recalculating) return;
    const previousExclusions = enabledExclusions;
    const previousFtas = enabledFtas;
    void recalculate(
      { ...enabledExclusions, [code]: checked },
      enabledFtas,
      previousExclusions,
      previousFtas,
    );
  };

  const toggleFta = (code: string, checked: boolean) => {
    if (recalculating) return;
    const previousExclusions = enabledExclusions;
    const previousFtas = enabledFtas;
    void recalculate(
      enabledExclusions,
      { ...enabledFtas, [code]: checked },
      previousExclusions,
      previousFtas,
    );
  };

  const tariffCalculationAvailable =
    htsValidForTariffs && tariffCalculationIsAvailable(tariffResult);
  const tariffVerificationPending = Boolean(
    databaseProductId &&
      ((htsClassificationLoading && !resolvedHtsClassification) ||
        (recalculating && htsValidForTariffs && !tariffCalculationAvailable)),
  );
  const tariffUnavailableReason =
    previewUnavailableReason ??
    resolvedHtsClassification?.reason ??
    tariffResult?.insights?.tariffUnavailableReason ??
    (htsClassificationUnknown
      ? 'The current HTS status could not be verified.'
      : 'A complete, currently valid ten-digit HTS code is required.');
  const lines: any[] =
    tariffCalculationAvailable && Array.isArray(tariffResult?.results?.lines)
      ? tariffResult.results.lines
      : [];
  const summaryRows: any[] =
    tariffCalculationAvailable &&
    Array.isArray(tariffResult?.results?.summaryRows)
      ? tariffResult.results.summaryRows
      : lines.flatMap((line) => (Array.isArray(line?.rows) ? line.rows : []));
  const declaredValue = tariffNumber(
    tariffResult?.inputs?.productValue,
    detail.cost.unitValue,
  );
  const baseRateRaw = tariffRateDisplay(
    tariffResult?.base?.baseDutyRate ??
      summaryRows.find(
        (row) =>
          row?.type === 'Base Duty' || row?.type === 'Base Duty (Column 2)',
      )?.rate ??
      `${detail.cost.baseRatePct}%`,
  );
  const baseRate = (() => {
    if (lines.length === 0 || baseRateRaw.toLowerCase() === 'free') {
      return baseRateRaw;
    }
    const totalLineValue = lines.reduce(
      (sum, line) => sum + tariffNumber(line?.value),
      0,
    );
    if (totalLineValue <= 0) return baseRateRaw;
    const effectivePct = lines.reduce((sum, line) => {
      const baseRow = Array.isArray(line?.rows)
        ? line.rows.find(
            (row: any) =>
              row?.type === 'Base Duty' || row?.type === 'Base Duty (Column 2)',
          )
        : undefined;
      return (
        sum +
        tariffRatePct(baseRow?.rate ?? baseRateRaw) *
          (tariffNumber(line?.value) / totalLineValue)
      );
    }, 0);
    if (Math.abs(effectivePct - tariffRatePct(baseRateRaw)) < 0.000001) {
      return baseRateRaw;
    }
    return `${effectivePct
      .toFixed(2)
      .replace(/\.00$/, '')
      .replace(/(\.\d)0$/, '$1')}%`;
  })();
  const basePercentCost =
    lines.length > 0
      ? lines.reduce((sum, line) => {
          const baseRow = Array.isArray(line?.rows)
            ? line.rows.find(
                (row: any) =>
                  row?.type === 'Base Duty' ||
                  row?.type === 'Base Duty (Column 2)',
              )
            : undefined;
          if (
            tariffRateDisplay(baseRow?.rate ?? baseRateRaw).toLowerCase() ===
            'free'
          ) {
            return sum;
          }
          return (
            sum +
            (tariffRatePct(baseRow?.rate ?? baseRateRaw) / 100) *
              tariffNumber(line?.value)
          );
        }, 0)
      : (tariffRatePct(baseRateRaw) / 100) * declaredValue;
  const baseCost =
    tariffResult?.math != null
      ? tariffNumber(tariffResult.math.baseAmountDollars) + basePercentCost
      : detail.cost.baseCost;
  const additionalCost =
    tariffResult?.math != null
      ? tariffNumber(tariffResult.math.additionalDutyDollars)
      : detail.cost.additionalCost;
  const totalDutyCost =
    tariffResult?.math != null
      ? tariffNumber(tariffResult.math.dutyCost, baseCost + additionalCost)
      : detail.cost.totalDutyCost;
  const totalCost =
    tariffResult?.math != null
      ? (feeFreeTotalCost(tariffResult.math) ?? declaredValue + totalDutyCost)
      : detail.cost.totalCost;
  const additionalRate =
    declaredValue > 0
      ? `${((additionalCost / declaredValue) * 100)
          .toFixed(2)
          .replace(/\.00$/, '')
          .replace(/(\.\d)0$/, '$1')}%`
      : '0%';

  const dutyLineGroups = lines.map((line, index) => {
    const lineValue = tariffNumber(line?.value);
    const rows = (Array.isArray(line?.rows) ? line.rows : []).map(
      (row: any) => ({
        ...row,
        cost: isPureAdValoremTariffRate(row?.rate)
          ? (tariffRatePct(row?.rate) / 100) * lineValue
          : null,
      }),
    );
    const calculatedDuty = rows.reduce(
      (sum: number, row: any) =>
        row?.type === 'Base Duty'
          ? sum
          : sum + (tariffRatePct(row?.rate) / 100) * lineValue,
      0,
    );
    return {
      id: String(line?.id ?? `line-${index}`),
      label: String(line?.label ?? `Line ${index + 1}`),
      value: lineValue,
      duty:
        typeof line?.duty === 'number'
          ? line.duty
          : lines.length === 1
          ? additionalCost
          : calculatedDuty,
      rows,
    };
  });
  const unsplitDutyRows = detail.dutyLines.map((line) => ({
    code: line.subCode,
    type: line.label,
    rate: `${line.ratePct}%`,
    cost: line.cost,
    note: line.note,
  }));

  const ftaOptions: Array<{ code: string; name: string }> =
    tariffCalculationAvailable &&
    Array.isArray(tariffResult?.results?.ftaOptions)
      ? tariffResult.results.ftaOptions
      : databaseProductId
      ? []
      : detail.ftas
          .filter((fta) => fta.status !== 'Not eligible')
          .map((fta) => ({
            code: fta.program,
            name: fta.program,
          }));
  const exclusionOptions: TariffExclusionCandidate[] =
    tariffCalculationAvailable &&
    Array.isArray(tariffResult?.results?.optionalExclusions)
      ? tariffResult.results.optionalExclusions
      : databaseProductId
      ? []
      : detail.exclusions.map((exclusion) => ({
          codeDigits: tariffCodeDigits(exclusion.subCode),
          codePretty: exclusion.subCode,
          label: exclusion.label,
          rateDisplay: `${exclusion.ratePct}%`,
          note: exclusion.note,
        }));

  const exclusionQuestions = buildTariffExclusionQuestions(exclusionOptions);
  const selectedExclusionAnswers: Record<string, string> = {};
  for (const question of exclusionQuestions) {
    if (clearedExclusionQuestions.has(question.id)) continue;
    const allCodes = new Set(
      question.options.flatMap((option) => option.enablesExclusions),
    );
    const activeCodes = new Set(
      [...allCodes].filter((code) => enabledExclusions[code]),
    );
    const exactMatch = question.options.find(
      (option) =>
        option.enablesExclusions.length === activeCodes.size &&
        option.enablesExclusions.every((code) => activeCodes.has(code)),
    );
    if (exactMatch) {
      selectedExclusionAnswers[question.id] = exactMatch.id;
    }
  }
  const assistantEnabledCodes = Array.from(
    new Set(
      exclusionQuestions.flatMap((question) => {
        const selectedId = selectedExclusionAnswers[question.id];
        return (
          question.options.find((option) => option.id === selectedId)
            ?.enablesExclusions ?? []
        );
      }),
    ),
  );
  const answerExclusionQuestion = (
    question: TariffExclusionQuestion,
    option: TariffQuestionOption,
  ) => {
    if (recalculating) return;
    const wasCleared = clearedExclusionQuestions.has(question.id);
    const isSelected = selectedExclusionAnswers[question.id] === option.id;
    const enabledSet = new Set(isSelected ? [] : option.enablesExclusions);
    const codesToDisable = question.options
      .flatMap((item) => item.enablesExclusions)
      .filter((code) => !enabledSet.has(code));
    const next = { ...enabledExclusions };
    codesToDisable.forEach((code) => {
      next[code] = false;
    });
    enabledSet.forEach((code) => {
      next[code] = true;
    });
    setClearedExclusionQuestions((current) => {
      const updated = new Set(current);
      if (isSelected) updated.add(question.id);
      else updated.delete(question.id);
      return updated;
    });
    const changed = new Set([
      ...Object.keys(enabledExclusions),
      ...Object.keys(next),
    ]);
    if (
      ![...changed].some(
        (code) => Boolean(enabledExclusions[code]) !== Boolean(next[code]),
      )
    ) {
      return;
    }
    void recalculate(next, enabledFtas, enabledExclusions, enabledFtas).then(
      (saved) => {
        if (saved) return;
        setClearedExclusionQuestions((current) => {
          const restored = new Set(current);
          if (wasCleared) restored.add(question.id);
          else restored.delete(question.id);
          return restored;
        });
      },
    );
  };

  const realRiskFlags: ProductDetail['riskFlags'] = [];
  if (databaseProductId) {
    // The same active-order result as the AD/CVD card, table chip and exports.
    if (detail.adcvd.exposure) {
      realRiskFlags.push({
        label: 'Potential AD/CVD',
        severity: 'danger',
        note: `Active AD/CVD orders for this product's country of origin: ${detail.adcvd.cases
          .map((adcvdCase) => adcvdCase.caseNumber)
          .join(', ')}. Confirm the goods fall within each order's scope.`,
      });
    }
    const forcedLaborRows =
      detail.forcedLabor.groups?.flatMap((group) => group.rows) ?? [];
    if (
      forcedLaborRows.some(
        (row) =>
          String(row['List Name'] ?? row.List ?? '').trim() ===
          'Forced and Child Labor HS Codes',
      )
    ) {
      realRiskFlags.push({
        label: 'Forced Labor',
        severity: 'warning',
        note: 'This product or origin may be flagged under UFLPA or WRO enforcement.',
      });
    }
    if (
      detail.biteLensGroups.some(
        (group) => group.name === 'PGA Codes' && group.total > 0,
      )
    ) {
      realRiskFlags.push({
        label: 'PGA Requirement',
        severity: 'info',
        note: 'Partner Government Agency filing may be required for this entry.',
      });
    }
  }
  const tariffRiskFlags = databaseProductId ? realRiskFlags : detail.riskFlags;
  const previewHistory: Array<{
    id?: string;
    date?: string;
    summary?: string;
  }> = Array.isArray(tariffResult?.insights?.history)
    ? tariffResult.insights.history
    : [];
  const tariffHtsHistory = databaseProductId
    ? (htsClassification?.history ?? []).map((item, index) => ({
        id: item.id ?? `history-${index}`,
        date: item.date ?? '—',
        summary: item.summary ?? '',
      }))
    : previewHistory.length > 0
    ? previewHistory.map((item, index) => ({
        id: item.id ?? `history-${index}`,
        date: item.date ?? '—',
        summary: item.summary ?? '',
      }))
    : detail.htsHistory.map((item, index) => ({
        id: `history-${index}`,
        date: item.date,
        summary: item.change,
      }));

  const explainResults = async () => {
    if (explanationLoading) return;
    const requestId = ++explanationRequestRef.current;
    setExplanationLoading(true);
    const chapter99Codes = Array.from(
      new Set(
        summaryRows
          .map((row) => formatTariffCode(row?.code))
          .filter((code) => tariffCodeDigits(code).startsWith('99')),
      ),
    );
    const otherTariffLayers = summaryRows
      .filter(
        (row) =>
          row?.type !== 'Base Duty' && row?.type !== 'Base Duty (Column 2)',
      )
      .map((row) => ({
        code: row?.code,
        rate: row?.rate,
        type: row?.type,
      }));

    const response = await runAIExplanationOnly({
      hs_code: formatTariffCode(explanationContext?.hsCode || product.htsCode),
      country:
        tariffResult?.inputs?.originCountry || product.countryOfOrigin || '',
      transaction_date: tariffResult?.inputs?.entryDate
        ? String(tariffResult.inputs.entryDate).slice(0, 10)
        : entryDate,
      general_rate: explanationContext?.generalRate || baseRateRaw,
      column2_rate: `Not Applicable - ${
        tariffResult?.inputs?.originCountry ||
        product.countryOfOrigin ||
        'this origin'
      } is not subject to Column 2 duties`,
      special_duty_rate:
        explanationContext?.specialDutyRate.trim() || 'No Special Duty Found',
      ad_cvd_status: 'This transaction may not be subject to AD / CVD orders.',
      chapter99_codes: chapter99Codes,
      other_tariff_layers: otherTariffLayers,
    });
    if (requestId !== explanationRequestRef.current) return;
    setExplanationLoading(false);
    if (!response?.explanation) {
      toast.error('Unable to generate an explanation. Please try again.');
      return;
    }
    setExplanationText(response.explanation);
  };

  return (
    <section className='elms-card space-y-4'>
      <div className='flex items-end justify-between gap-2 flex-wrap'>
        <div>
          <h2 className='text-base font-semibold tracking-tight'>
            Tariff & Duty Summary
          </h2>
          <p className='text-xs text-muted-foreground mt-0.5'>
            Per-line duty exposure, exclusions, and risk flags.
          </p>
        </div>
        {recalculating && (
          <span className='ih-tariff-calculating'>
            <RotateCw className='size-3.5 animate-spin' />
            Recalculating
          </span>
        )}
      </div>

      <div className='ih-tariff-insights-grid'>
        <article className='ih-tariff-insight-card ih-tariff-assistant-card'>
          <div className='ih-tariff-insight-heading'>
            <Sparkles className='size-4 text-primary' />
            <div>
              <h3>Exclusion Applicability Assistant</h3>
              <p>
                Answer the applicable questions to update exclusions and costs.
              </p>
            </div>
          </div>
          {exclusionQuestions.length === 0 ? (
            <EmptyState text='No exclusions applicable for assistance were found for this transaction.' />
          ) : (
            <div className='ih-tariff-question-list'>
              {exclusionQuestions.map((question) => (
                <fieldset
                  key={question.id}
                  className='ih-tariff-question'
                  disabled={recalculating || readOnly}
                >
                  <legend>{question.prompt}</legend>
                  <p>{question.helperText}</p>
                  <div className='ih-tariff-question-options'>
                    {question.options.map((option) => {
                      const selected =
                        selectedExclusionAnswers[question.id] === option.id;
                      return (
                        <button
                          key={option.id}
                          type='button'
                          className={cn(
                            'ih-tariff-question-option',
                            selected && 'is-selected',
                          )}
                          aria-pressed={selected}
                          onClick={() =>
                            answerExclusionQuestion(question, option)
                          }
                        >
                          <span
                            className='ih-tariff-question-radio'
                            aria-hidden='true'
                          />
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              ))}
              <div className='ih-tariff-assistant-enabled'>
                Enabled codes:{' '}
                <strong>
                  {assistantEnabledCodes.length > 0
                    ? assistantEnabledCodes
                        .map((code) => formatTariffCode(code))
                        .join(', ')
                    : 'None'}
                </strong>
              </div>
            </div>
          )}
        </article>

        <article className='ih-tariff-insight-card'>
          <div className='ih-tariff-insight-heading'>
            <AlertTriangle className='size-4 text-[var(--warning)]' />
            <div>
              <h3>Risk Flags</h3>
              <p>AD/CVD, Forced Labor, and PGA indicators.</p>
            </div>
          </div>
          {riskFlagsLoading ? (
            <div className='ih-tariff-risk-status'>
              <RotateCw className='size-3.5 animate-spin' />
              Checking current commodity sources…
            </div>
          ) : riskFlagsError ? (
            <div className='ih-tariff-risk-status is-error'>
              <AlertCircle className='size-3.5' />
              Risk flags are unavailable. Refresh Program Codes to try again.
            </div>
          ) : tariffRiskFlags.length === 0 ? (
            <EmptyState text='No flags found for AD/CVD, Forced Labor, or PGA.' />
          ) : (
            <ul className='ih-tariff-risk-list'>
              {tariffRiskFlags.map((flag, index) => (
                <li
                  key={`${flag.label}-${index}`}
                  className={cn('ih-tariff-risk-flag', `is-${flag.severity}`)}
                >
                  <div>{flag.label}</div>
                  <p>{flag.note}</p>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className='ih-tariff-insight-card'>
          <div className='ih-tariff-insight-heading'>
            <History className='size-4 text-primary' />
            <div>
              <h3>HTS Code History</h3>
              <p>Recent and upcoming classification changes.</p>
            </div>
          </div>
          {htsClassificationLoading ? (
            <div className='ih-tariff-risk-status'>
              <RotateCw className='size-3.5 animate-spin' />
              Checking the current USHTS schedule…
            </div>
          ) : htsClassificationError ? (
            <div className='ih-tariff-risk-status is-error'>
              <AlertCircle className='size-3.5' />
              HTS history is unavailable. Try refreshing this section.
            </div>
          ) : (
            <>
              {databaseProductId && htsClassification?.requiresAttention && (
                <div
                  className={cn(
                    'ih-hts-history-status',
                    htsClassification.status === 'expiring_soon'
                      ? 'is-warning'
                      : 'is-danger',
                  )}
                >
                  <AlertTriangle className='size-3.5 shrink-0' />
                  <span>{htsClassification.reason}</span>
                </div>
              )}
              {tariffHtsHistory.length === 0 ? (
                <EmptyState text='No recent changes detected for this HTS code.' />
              ) : (
                <ul className='ih-tariff-history-list'>
                  {tariffHtsHistory.map((item) => (
                    <li key={item.id}>
                      <time>{item.date}</time>
                      <p>{item.summary}</p>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </article>
      </div>

      {tariffVerificationPending ? (
        <div className='flex items-center gap-2 rounded-md border border-[var(--edge-divider)] bg-[var(--bg-surface)] px-3 py-3 text-sm text-muted-foreground'>
          <RotateCw className='size-4 animate-spin' />
          Verifying the current HTS code before calculating tariffs…
        </div>
      ) : !tariffCalculationAvailable ? (
        <div className='flex items-start gap-2 rounded-md border border-[var(--danger)]/40 bg-[var(--bg-surface)] px-3 py-3 text-sm'>
          <AlertTriangle className='mt-0.5 size-4 shrink-0 text-[var(--danger)]' />
          <div>
            <div className='font-medium text-[var(--danger)]'>
              {htsClassificationUnknown
                ? 'HTS verification unavailable'
                : 'Tariff calculation unavailable'}
            </div>
            <p className='mt-0.5 text-xs text-muted-foreground'>
              {tariffUnavailableReason}{' '}
              {htsClassificationUnknown
                ? 'Refresh the product to try verification again.'
                : 'Correct the HTS code and analyze the product again to generate a verified tariff stack.'}
            </p>
          </div>
        </div>
      ) : null}

      {/* Compact two-column layout: line breakdown + FTAs + exclusions on the left, cost summary as a right rail */}
      <div
        className={cn(
          'grid grid-cols-1 lg:grid-cols-3 gap-4',
          !tariffCalculationAvailable && 'hidden',
        )}
      >
        <div className='lg:col-span-2 space-y-5'>
          {/* Duty line breakdown */}
          <div>
            <SectionLabel>Duty Breakdown</SectionLabel>
            {dutyLineGroups.length > 0 ? (
              <div className='ih-duty-lines'>
                {dutyLineGroups.map((line) => (
                  <div key={line.id} className='ih-duty-line'>
                    <div className='ih-duty-line-header'>
                      <span>{line.label}</span>
                      <div>
                        <span>
                          Value:{' '}
                          <strong>{formatTariffCurrency(line.value)}</strong>
                        </span>
                        <span aria-hidden='true'>•</span>
                        <span>
                          Duty:{' '}
                          <strong>{formatTariffCurrency(line.duty)}</strong>
                        </span>
                      </div>
                    </div>
                    <TariffDutyTable rows={line.rows} />
                  </div>
                ))}
              </div>
            ) : (
              <TariffDutyTable rows={unsplitDutyRows} />
            )}
          </div>

          {/* FTAs — compact list */}
          <div>
            <SectionLabel>Free Trade Agreements</SectionLabel>
            {ftaOptions.length === 0 ? (
              <div className='mt-2'>
                <EmptyState text='No applicable free trade agreements.' />
              </div>
            ) : (
              <ul className='mt-2 divide-y divide-[var(--edge-separator)] text-xs'>
                {ftaOptions.map((fta) => (
                  <li
                    key={fta.code}
                    className='flex items-center justify-between gap-2 py-2'
                  >
                    <div className='min-w-0'>
                      <span className='font-medium'>{fta.name}</span>
                      <span className='text-[11px] text-muted-foreground ml-2 font-mono'>
                        {fta.code}
                      </span>
                    </div>
                    <div className='flex items-center gap-2'>
                      <span className='text-[10px] uppercase tracking-wider font-semibold badge-success rounded-full px-2 py-0.5'>
                        Free
                      </span>
                      <TariffToggle
                        checked={Boolean(enabledFtas[fta.code])}
                        disabled={recalculating || readOnly}
                        label={`Toggle ${fta.name}`}
                        onChange={(checked) => toggleFta(fta.code, checked)}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Potential exclusions — compact table */}
          <div>
            <SectionLabel>Potential Exclusions / Applicability</SectionLabel>
            {exclusionOptions.length === 0 ? (
              <div className='mt-2'>
                <EmptyState text='No exclusions applicable to this product.' />
              </div>
            ) : (
              <div className='overflow-x-auto mt-2'>
                <table className='w-full text-xs'>
                  <thead className='text-muted-foreground'>
                    <tr className='border-b border-[var(--edge-divider)]'>
                      <th className='text-left font-medium py-2 pr-3 text-[11px] uppercase tracking-wider'>
                        Sub-code
                      </th>
                      <th className='text-left font-medium py-2 pr-3 text-[11px] uppercase tracking-wider'>
                        Exclusion
                      </th>
                      <th className='text-right font-medium py-2 pr-3 text-[11px] uppercase tracking-wider'>
                        Rate
                      </th>
                      <th className='text-right font-medium py-2 pl-3 text-[11px] uppercase tracking-wider'>
                        Apply
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {exclusionOptions.map((exclusion, index) => {
                      const code =
                        exclusion.codeDigits ||
                        tariffCodeDigits(exclusion.codePretty);
                      return (
                        <tr
                          key={`${code}-${index}`}
                          className='border-b border-[var(--edge-separator)]'
                        >
                          <td className='py-2 pr-3 font-mono'>
                            {exclusion.codePretty || formatTariffCode(code)}
                          </td>
                          <td className='py-2 pr-3'>
                            <div>{exclusion.label}</div>
                            {exclusion.note && (
                              <div className='text-[11px] text-muted-foreground'>
                                {exclusion.note}
                              </div>
                            )}
                          </td>
                          <td className='py-2 pr-3 text-right tabular-nums'>
                            {exclusion.rateDisplay ??
                              `${tariffNumber(exclusion.ratePct)}%`}
                          </td>
                          <td className='py-2 pl-3'>
                            <div className='flex justify-end'>
                              <TariffToggle
                                checked={Boolean(enabledExclusions[code])}
                                disabled={recalculating || readOnly}
                                label={`Toggle ${exclusion.label}`}
                                onChange={(checked) =>
                                  toggleExclusion(code, checked)
                                }
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right rail — Cost Summary */}
        <aside className='rounded-md bg-[var(--bg-surface)] p-3 self-start'>
          <div className='text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2'>
            Cost Summary
          </div>
          <dl className='space-y-1.5 text-xs tabular-nums'>
            <CostRow
              label='Declared value'
              value={formatTariffCurrency(declaredValue)}
            />
            <CostRow label='Base duty rate' value={baseRate} />
            <CostRow
              label='Base duty cost'
              value={formatTariffCurrency(baseCost)}
            />
            <CostRow label='Additional duty rate' value={additionalRate} />
            <CostRow
              label='Additional duty cost'
              value={formatTariffCurrency(additionalCost)}
            />
            <CostRow
              label='Total duty cost'
              value={formatTariffCurrency(totalDutyCost)}
            />
          </dl>
          <div className='mt-3 pt-3 border-t border-[var(--edge-divider)]'>
            <div className='text-[10px] uppercase tracking-wider text-muted-foreground font-medium'>
              Total Landed Cost
            </div>
            <div className='text-2xl font-semibold tabular-nums mt-0.5'>
              {formatTariffCurrency(totalCost)}
            </div>
            <div className='text-[11px] text-muted-foreground'>
              Declared value + duty
            </div>
          </div>
        </aside>
      </div>

      {tariffCalculationAvailable &&
        (explanationLoading || explanationText) && (
          <div className='ih-tariff-explanation'>
            <div className='flex items-center gap-2 mb-2'>
              <Sparkles className='size-4 text-primary' />
              <span className='text-xs font-semibold uppercase tracking-wider'>
                BITE&apos;s Explanation
              </span>
            </div>
            {explanationLoading ? (
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <RotateCw className='size-4 animate-spin' />
                Generating explanation…
              </div>
            ) : (
              <div className='ih-tariff-explanation-content'>
                <ReactMarkdown>{explanationText}</ReactMarkdown>
              </div>
            )}
          </div>
        )}

      {tariffCalculationAvailable && (
        <div className='ih-tariff-footer'>
          <HeaderBtn
            icon={explanationLoading ? RotateCw : Sparkles}
            onClick={() => void explainResults()}
            disabled={explanationLoading || recalculating}
            spinning={explanationLoading}
            locked={readOnly}
          >
            {explanationText ? 'Re-run Explanation' : 'Explain Results'}
          </HeaderBtn>
        </div>
      )}
    </section>
  );
}

function TariffDutyTable({ rows }: { rows: any[] }) {
  return (
    <div className='overflow-x-auto mt-2'>
      <table className='w-full text-xs'>
        <thead className='text-muted-foreground'>
          <tr className='border-b border-[var(--edge-divider)]'>
            <th className='text-left font-medium py-2 pr-3 text-[11px] uppercase tracking-wider'>
              HTS Sub-code
            </th>
            <th className='text-left font-medium py-2 pr-3 text-[11px] uppercase tracking-wider'>
              Line item
            </th>
            <th className='text-right font-medium py-2 pr-3 text-[11px] uppercase tracking-wider'>
              Rate
            </th>
            <th className='text-right font-medium py-2 pl-3 text-[11px] uppercase tracking-wider'>
              Cost
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={`${row.code || row.type}-${index}`}
              className='border-b border-[var(--edge-separator)]'
            >
              <td className='py-2 pr-3 font-mono'>{row.code || '—'}</td>
              <td className='py-2 pr-3'>
                <div>{row.type || '—'}</div>
                {row.note && (
                  <div className='text-[11px] text-muted-foreground'>
                    {row.note}
                  </div>
                )}
              </td>
              <td
                className={cn(
                  'py-2 pr-3 text-right tabular-nums',
                  tariffRatePct(row.rate) > 0 &&
                    'text-[var(--warning)] font-medium',
                )}
              >
                {tariffRateDisplay(row.rate)}
              </td>
              <td className='py-2 pl-3 text-right tabular-nums'>
                {row.cost == null ? (
                  <span className='text-[11px] text-muted-foreground'>
                    Included in total duty
                  </span>
                ) : (
                  formatTariffCurrency(row.cost)
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CostRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex items-baseline justify-between gap-3'>
      <dt className='text-muted-foreground'>{label}</dt>
      <dd className='font-medium'>{value}</dd>
    </div>
  );
}

function SubPanel({
  title,
  children,
  defaultOpen,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className='rounded-md bg-[var(--bg-surface)] overflow-hidden'>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        className='w-full px-3 py-2 flex items-center justify-between gap-2 hover:bg-[var(--row-hover)] transition-colors'
      >
        <span className='text-xs font-semibold uppercase tracking-wider'>
          {title}
        </span>
        {open ? (
          <ChevronDown className='size-3.5 text-muted-foreground' />
        ) : (
          <ChevronRight className='size-3.5 text-muted-foreground' />
        )}
      </button>
      {open && <div className='px-3 pb-3 pt-1'>{children}</div>}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return <p className='text-xs text-muted-foreground italic py-2'>{text}</p>;
}

/* ─── CROSS / Entity risk / COO / Child / Documents / Comments / Activity ─── */

function CrossRulingsCard({ detail }: { detail: ProductDetail }) {
  const [expanded, setExpanded] = useState(false);
  const visibleRulings = expanded
    ? detail.crossRulings
    : detail.crossRulings.slice(0, 5);

  return (
    <div className='elms-card'>
      {detail.crossRulings.length === 0 ? (
        <EmptyState text='No CROSS rulings loaded yet. Click "Get latest results" to fetch matching customs rulings for this product.' />
      ) : (
        <>
          <ul className='divide-y divide-[var(--edge-separator)]'>
            {visibleRulings.map((r) => (
              <li key={r.id} className='py-2.5'>
                <a
                  className='flex items-start justify-between gap-3 text-sm text-primary hover:underline'
                  href={r.url}
                  target='_blank'
                  rel='noreferrer'
                >
                  <span className='min-w-0 break-all'>{r.url}</span>
                  <ExternalLink className='size-3.5 shrink-0 mt-0.5' />
                </a>
              </li>
            ))}
          </ul>
          {detail.crossRulings.length > 5 && (
            <button
              type='button'
              className='mt-2 text-xs text-primary hover:underline'
              onClick={() => setExpanded((current) => !current)}
            >
              {expanded ? 'Read Less' : 'Read More'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function EntityRiskCard({ detail }: { detail: ProductDetail }) {
  return (
    <div className='elms-card'>
      <h3 className='text-sm font-semibold mb-3'>Entity Risk Screening</h3>
      {detail.deniedPartyMatches.length === 0 ? (
        <div className='rounded-md bg-[var(--bg-surface)] p-3 text-xs'>
          <div className='flex items-center gap-2 text-[var(--success)]'>
            <CheckCircle2 className='size-4' />
            <span className='font-medium'>No Denied Party Matches Found</span>
          </div>
          <div className='text-muted-foreground mt-1'>
            All linked entities cleared against OFAC SDN, EPL, DPL, UVL.
          </div>
        </div>
      ) : (
        <ul className='space-y-2'>
          {detail.deniedPartyMatches.map((m, i) => (
            <li key={i} className={cn('rounded-md p-3', SEV_BG.danger)}>
              <div className='flex items-center justify-between gap-2'>
                <span className='text-sm font-medium text-[var(--danger)]'>
                  {m.entity}
                </span>
                <span
                  className={cn(
                    'inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5',
                    SEV_BADGE.danger,
                  )}
                >
                  Match
                </span>
              </div>
              <div className='text-[11px] text-muted-foreground mt-0.5'>
                {m.list} · added {m.date}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CooBreakdownCard({ detail }: { detail: ProductDetail }) {
  return (
    <div className='elms-card'>
      <h3 className='text-sm font-semibold mb-3'>
        Country of Origin Breakdown
      </h3>
      {detail.coBreakdown.length === 0 ? (
        <EmptyState text='No country of origin declared.' />
      ) : (
        <ul className='space-y-2'>
          {detail.coBreakdown.map((c) => (
            <li key={c.country} className='space-y-1'>
              <div className='flex items-center justify-between text-xs'>
                <span className='font-medium'>{c.country}</span>
                <span className='tabular-nums text-muted-foreground'>
                  {c.percent}%
                </span>
              </div>
              <div className='h-1.5 rounded-full bg-[var(--bg-surface)] overflow-hidden'>
                <div
                  className='h-full bg-primary'
                  style={{ width: `${c.percent}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChildComponentsCard({ detail }: { detail: ProductDetail }) {
  return (
    <div className='elms-card'>
      <h3 className='text-sm font-semibold mb-3'>Child Component HTS Codes</h3>
      {detail.childComponents.length === 0 ? (
        <EmptyState text='Not an assembly — no child components.' />
      ) : (
        <table className='w-full text-xs'>
          <thead className='text-muted-foreground'>
            <tr className='border-b border-[var(--edge-divider)]'>
              <th className='text-left py-2 pr-3 text-[11px] uppercase tracking-wider font-medium'>
                Component
              </th>
              <th className='text-left py-2 pr-3 text-[11px] uppercase tracking-wider font-medium'>
                HTS
              </th>
              <th className='text-left py-2 pr-3 text-[11px] uppercase tracking-wider font-medium'>
                Origin
              </th>
              <th className='text-right py-2 text-[11px] uppercase tracking-wider font-medium'>
                %
              </th>
            </tr>
          </thead>
          <tbody>
            {detail.childComponents.map((c, i) => (
              <tr key={i} className='border-b border-[var(--edge-separator)]'>
                <td className='py-2 pr-3'>{c.name}</td>
                <td className='py-2 pr-3 font-mono'>{c.hts}</td>
                <td className='py-2 pr-3'>{c.origin}</td>
                <td className='py-2 text-right tabular-nums'>{c.percent}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DocumentsCard({ detail }: { detail: ProductDetail }) {
  return (
    <div className='elms-card'>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-sm font-semibold'>Documents</h3>
        <HeaderBtn icon={Upload}>Upload</HeaderBtn>
      </div>
      {detail.documents.length === 0 ? (
        <EmptyState text='No documents uploaded for this product.' />
      ) : (
        <table className='w-full text-xs'>
          <thead className='text-muted-foreground'>
            <tr className='border-b border-[var(--edge-divider)]'>
              <th className='text-left py-2 pr-3 text-[11px] uppercase tracking-wider font-medium'>
                Name
              </th>
              <th className='text-left py-2 pr-3 text-[11px] uppercase tracking-wider font-medium'>
                Type
              </th>
              <th className='text-left py-2 pr-3 text-[11px] uppercase tracking-wider font-medium'>
                Date
              </th>
              <th className='text-right py-2 text-[11px] uppercase tracking-wider font-medium'>
                Size
              </th>
            </tr>
          </thead>
          <tbody>
            {detail.documents.map((d) => (
              <tr
                key={d.id}
                className='border-b border-[var(--edge-separator)]'
              >
                <td className='py-2 pr-3 flex items-center gap-2'>
                  <FileText className='size-3.5 text-muted-foreground' />
                  <span className='text-primary hover:underline cursor-pointer'>
                    {d.name}
                  </span>
                </td>
                <td className='py-2 pr-3'>{d.type}</td>
                <td className='py-2 pr-3'>{d.date}</td>
                <td className='py-2 text-right tabular-nums'>{d.size}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CommentsCard({ detail }: { detail: ProductDetail }) {
  return (
    <div className='elms-card'>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-sm font-semibold flex items-center gap-2'>
          <MessageSquare className='size-3.5' /> Comments
        </h3>
      </div>
      {detail.comments.length === 0 ? (
        <EmptyState text='No comments yet.' />
      ) : (
        <ul className='space-y-3'>
          {detail.comments.map((c) => (
            <li key={c.id} className='rounded-md bg-[var(--bg-surface)] p-3'>
              <div className='flex items-center justify-between text-xs'>
                <span className='font-medium'>{c.author}</span>
                <span className='text-muted-foreground'>{c.time}</span>
              </div>
              <p className='text-sm mt-1'>{c.text}</p>
            </li>
          ))}
        </ul>
      )}
      <div className='mt-3 flex gap-2'>
        <input
          placeholder='Add a comment…'
          className='flex-1 h-9 px-3 rounded-md border border-input bg-background text-sm'
        />
        <HeaderBtn icon={MessageSquare} primary>
          Post
        </HeaderBtn>
      </div>
    </div>
  );
}

function ActivityCard({
  product,
  detail,
}: {
  product: ImportProduct;
  detail: ProductDetail;
}) {
  const entries = [
    { time: 'Just now', text: 'Screening completed', icon: Search },
    {
      time: '2h ago',
      text: `Tariff classification confirmed at ${product.htsCode || '—'}`,
      icon: FileText,
    },
    ...(detail.deniedPartyMatches.length
      ? [
          {
            time: '1d ago',
            text: 'Denied party match raised for review',
            icon: ShieldAlert,
          },
        ]
      : []),
    { time: '3d ago', text: 'Product added to database', icon: Package },
  ];
  return (
    <div className='elms-card'>
      <h3 className='text-sm font-semibold mb-3'>Activity</h3>
      <ul className='divide-y divide-[var(--edge-separator)]'>
        {entries.map((e, i) => {
          const Icon = e.icon;
          return (
            <li key={i} className='py-2 flex items-center gap-3 text-xs'>
              <Icon className='size-3.5 text-muted-foreground' />
              <span className='flex-1'>{e.text}</span>
              <span className='text-muted-foreground'>{e.time}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ─── Drawers ─── */

function ComplianceCheckDrawer({
  open,
  onClose,
  type,
  product,
  detail,
  onOpenRecord,
  appliedFlags,
  onOpenEditor,
  screeningLoading,
  hasScreening,
}: {
  open: boolean;
  onClose: () => void;
  type: CheckType | null;
  product: ImportProduct;
  detail: ProductDetail;
  onOpenRecord: (r: Record<string, string | number> | null) => void;
  appliedFlags: AppliedPgaFlag[];
  onOpenEditor: (code: PgaCode) => void;
  screeningLoading?: boolean;
  hasScreening?: boolean;
}) {
  if (!open || !type) return null;
  return (
    <div className='fixed inset-0 z-50 flex'>
      <div className='flex-1 bg-black/50' onClick={onClose} />
      <aside
        className={cn(
          'w-full bg-card border-l border-[var(--edge-separator)] flex flex-col',
          type === 'Customs Flags' ? 'max-w-[820px]' : 'max-w-[520px]',
        )}
      >
        <header className='flex items-center justify-between px-4 py-3 border-b border-[var(--edge-separator)]'>
          <div>
            <div className='text-[10px] uppercase tracking-wider text-muted-foreground'>
              Compliance check
            </div>
            <h3 className='text-sm font-semibold'>{type}</h3>
          </div>
          <button onClick={onClose} className='p-1 rounded hover:bg-accent'>
            <X className='size-4' />
          </button>
        </header>
        <div className='flex-1 overflow-auto p-4 space-y-3 text-xs'>
          {type === 'Risk Summary' && <RiskSummaryContent detail={detail} />}
          {type === 'Customs Flags' && (
            <div className='space-y-5'>
              <AppliedFlagsSection flags={appliedFlags} onOpen={onOpenEditor} />
              <div className='space-y-2'>
                <h4 className='text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5'>
                  <Search className='size-3.5 text-primary' /> Screening Hits
                </h4>
                <ScreeningTable
                  detail={detail}
                  product={product}
                  onOpenRecord={onOpenRecord}
                />
              </div>
            </div>
          )}
          {type === 'Cross Rulings' && <CrossRulingsContent detail={detail} />}
          {type === 'Entity Risk Screening' && (
            <EntityRiskContent
              product={product}
              detail={detail}
              onOpenRecord={onOpenRecord}
              loading={screeningLoading}
              hasScreening={hasScreening}
            />
          )}
          {type === 'HTS Classification' && (
            <>
              <div className='grid grid-cols-2 gap-3'>
                <Field
                  label='Assigned HTS'
                  value={detail.classification.code}
                  mono
                />
                <Field
                  label='Confidence'
                  value={
                    detail.classification.confidence
                      ? `${detail.classification.confidence}%`
                      : '—'
                  }
                />
              </div>
              <SectionLabel>Description</SectionLabel>
              <p>{detail.classification.description}</p>
              <SectionLabel>Reasoning</SectionLabel>
              <p className='text-muted-foreground'>
                {detail.classification.reasoning}
              </p>
              {detail.classification.alternates.length > 0 && (
                <>
                  <SectionLabel>Alternate codes considered</SectionLabel>
                  <ul className='space-y-1.5'>
                    {detail.classification.alternates.map((a) => (
                      <li
                        key={a.code}
                        className='rounded bg-[var(--bg-surface)] px-2 py-1.5'
                      >
                        <span className='font-mono'>{a.code}</span>
                        <span className='text-muted-foreground'>
                          {' '}
                          — {a.reason}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {detail.crossRulings.length > 0 && (
                <>
                  <SectionLabel>CROSS Rulings</SectionLabel>
                  <ul className='divide-y divide-[var(--edge-separator)]'>
                    {detail.crossRulings.map((r) => (
                      <li key={r.id} className='py-2'>
                        <div className='flex items-start justify-between gap-2'>
                          <div className='min-w-0'>
                            <div className='text-xs font-medium'>
                              {r.id}{' '}
                              <span className='text-muted-foreground font-normal'>
                                — {r.title}
                              </span>
                            </div>
                            <div className='text-[11px] text-muted-foreground mt-0.5'>
                              {r.summary}
                            </div>
                          </div>
                          <div className='shrink-0 text-[11px] text-muted-foreground flex items-center gap-1'>
                            {r.date} <ExternalLink className='size-3' />
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
          {type === 'PGA Review' && (
            <>
              <p className='text-muted-foreground'>
                Per-agency review requirements.
              </p>
              <ul className='space-y-1.5'>
                {detail.pga.map((p) => (
                  <li
                    key={p.agency}
                    className='rounded bg-[var(--bg-surface)] px-2 py-1.5 flex items-start gap-2'
                  >
                    <span
                      className={cn(
                        'mt-1.5 size-1.5 rounded-full shrink-0',
                        p.flagged
                          ? 'bg-[var(--warning)]'
                          : 'bg-[var(--success)]',
                      )}
                    />
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center justify-between gap-2'>
                        <span className='font-medium'>{p.agency}</span>
                        <span
                          className={cn(
                            'inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5',
                            p.flagged ? 'badge-warning' : 'badge-success',
                          )}
                        >
                          {p.flagged ? 'Flagged' : 'Clear'}
                        </span>
                      </div>
                      <div className='text-[11px] text-muted-foreground mt-0.5'>
                        {p.note}
                      </div>
                      {p.program && (
                        <div className='text-[11px] mt-0.5'>
                          Program:{' '}
                          <span className='font-medium'>{p.program}</span>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
          {(type === 'Tariff Exposure' || type === 'Tariff & Duty Summary') && (
            <>
              <div className='flex items-center justify-between gap-2'>
                <SectionLabel>BITE&apos;s Explanation</SectionLabel>
                <div className='flex items-center gap-1.5'>
                  <HeaderBtn icon={Sparkles}>Explain</HeaderBtn>
                  <HeaderBtn icon={PencilLine}>Edit Inputs</HeaderBtn>
                </div>
              </div>
              <p className='text-muted-foreground'>{detail.tariffReasoning}</p>

              <SectionLabel>Duty Breakdown</SectionLabel>
              <div className='overflow-x-auto -mx-1 px-1'>
                <table className='w-full text-[11px]'>
                  <thead className='text-muted-foreground'>
                    <tr className='border-b border-[var(--edge-divider)]'>
                      <th className='text-left font-medium py-1.5 pr-2 uppercase tracking-wider'>
                        Sub-code
                      </th>
                      <th className='text-left font-medium py-1.5 pr-2 uppercase tracking-wider'>
                        Line item
                      </th>
                      <th className='text-right font-medium py-1.5 pr-2 uppercase tracking-wider'>
                        Rate
                      </th>
                      <th className='text-right font-medium py-1.5 pl-2 uppercase tracking-wider'>
                        Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.dutyLines.map((l, i) => (
                      <tr
                        key={i}
                        className='border-b border-[var(--edge-separator)]'
                      >
                        <td className='py-1.5 pr-2 font-mono'>{l.subCode}</td>
                        <td className='py-1.5 pr-2'>
                          <div>{l.label}</div>
                          {l.note && (
                            <div className='text-[10px] text-muted-foreground'>
                              {l.note}
                            </div>
                          )}
                        </td>
                        <td
                          className={cn(
                            'py-1.5 pr-2 text-right tabular-nums',
                            l.ratePct > 0 &&
                              'text-[var(--warning)] font-medium',
                          )}
                        >
                          {l.ratePct}%
                        </td>
                        <td className='py-1.5 pl-2 text-right tabular-nums'>
                          {formatCurrency(l.cost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <SectionLabel>Cost Summary</SectionLabel>
              <div className='rounded-md bg-[var(--bg-surface)] p-3'>
                <dl className='space-y-1 tabular-nums'>
                  <div className='flex justify-between'>
                    <dt className='text-muted-foreground'>Base duty</dt>
                    <dd>{`${detail.cost.baseRatePct}% · ${formatCurrency(
                      detail.cost.baseCost,
                    )}`}</dd>
                  </div>
                  <div className='flex justify-between'>
                    <dt className='text-muted-foreground'>Additional duty</dt>
                    <dd>{`${detail.cost.additionalRatePct}% · ${formatCurrency(
                      detail.cost.additionalCost,
                    )}`}</dd>
                  </div>
                  <div className='flex justify-between'>
                    <dt className='text-muted-foreground'>Total duty cost</dt>
                    <dd>{formatCurrency(detail.cost.totalDutyCost)}</dd>
                  </div>
                </dl>
                <div className='mt-2 pt-2 border-t border-[var(--edge-divider)] flex items-baseline justify-between'>
                  <span className='text-[10px] uppercase tracking-wider text-muted-foreground font-medium'>
                    Total Cost
                  </span>
                  <span className='text-base font-semibold tabular-nums'>
                    {formatCurrency(detail.cost.totalCost)}
                  </span>
                </div>
              </div>

              <SectionLabel>Free Trade Agreements</SectionLabel>
              <ul className='space-y-1'>
                {detail.ftas.map((f) => (
                  <li
                    key={f.program}
                    className='flex items-center justify-between gap-2 rounded bg-[var(--bg-surface)] px-2 py-1.5'
                  >
                    <div>
                      <div className='font-medium'>{f.program}</div>
                      {f.note && (
                        <div className='text-[10px] text-muted-foreground'>
                          {f.note}
                        </div>
                      )}
                    </div>
                    <span
                      className={cn(
                        'inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5',
                        f.status === 'Free'
                          ? 'badge-success'
                          : f.status === 'Reduced'
                          ? 'badge-info'
                          : 'badge-warning',
                      )}
                    >
                      {f.status}
                    </span>
                  </li>
                ))}
              </ul>

              {detail.exclusions.length > 0 && (
                <>
                  <SectionLabel>Potential Exclusions</SectionLabel>
                  <ul className='space-y-1'>
                    {detail.exclusions.map((e, i) => (
                      <li
                        key={i}
                        className='rounded bg-[var(--bg-surface)] px-2 py-1.5'
                      >
                        <div className='flex items-center justify-between gap-2'>
                          <span className='font-mono text-[11px]'>
                            {e.subCode}
                          </span>
                          <span
                            className={cn(
                              'inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5',
                              e.applicability === 'applicable'
                                ? 'badge-success'
                                : e.applicability === 'candidate'
                                ? 'badge-info'
                                : 'badge-warning',
                            )}
                          >
                            {e.applicability === 'applicable'
                              ? 'Applicable'
                              : e.applicability === 'candidate'
                              ? 'Candidate'
                              : 'Not applicable'}
                          </span>
                        </div>
                        <div className='mt-0.5'>{e.label}</div>
                        {e.note && (
                          <div className='text-[10px] text-muted-foreground'>
                            {e.note}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {detail.riskFlags.length > 0 && (
                <>
                  <SectionLabel>Risk Flags</SectionLabel>
                  <ul className='space-y-1.5'>
                    {detail.riskFlags.map((r, i) => (
                      <li
                        key={i}
                        className={cn(
                          'rounded p-2 flex items-start gap-2',
                          SEV_BG[r.severity],
                        )}
                      >
                        <AlertTriangle
                          className={cn(
                            'size-3.5 mt-0.5 shrink-0',
                            SEV_TEXT[r.severity],
                          )}
                        />
                        <div className='min-w-0'>
                          <div
                            className={cn('font-medium', SEV_TEXT[r.severity])}
                          >
                            {r.label}
                          </div>
                          <div className='text-[10px] text-muted-foreground mt-0.5'>
                            {r.note}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
          {type === 'Forced-Labor Screening' && (
            <>
              <SectionLabel>Result</SectionLabel>
              <p
                className={cn(
                  'font-medium',
                  SEV_TEXT[detail.forcedLabor.severity],
                )}
              >
                {detail.forcedLabor.result}
              </p>
              <SectionLabel>Risk basis</SectionLabel>
              <p className='text-muted-foreground'>
                {detail.forcedLabor.basis}
              </p>
              {detail.forcedLabor.entities.length > 0 && (
                <>
                  <SectionLabel>Flagged entities</SectionLabel>
                  <ul className='space-y-1'>
                    {detail.forcedLabor.entities.map((e) => (
                      <li
                        key={e}
                        className='rounded bg-[var(--bg-surface)] px-2 py-1.5'
                      >
                        {e}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
          {type === 'FTA Eligibility' && (
            <>
              <div className='grid grid-cols-2 gap-3'>
                <Field label='Program' value={detail.ftaDetail.program} />
                <Field
                  label='Status'
                  value={detail.ftaDetail.eligible ? 'Eligible' : 'Unconfirmed'}
                />
              </div>
              <SectionLabel>Reasoning</SectionLabel>
              <p className='text-muted-foreground'>
                {detail.ftaDetail.reasoning}
              </p>
              <SectionLabel>Qualifying criteria</SectionLabel>
              <ul className='space-y-1'>
                {detail.ftaDetail.criteria.map((c) => (
                  <li
                    key={c}
                    className='rounded bg-[var(--bg-surface)] px-2 py-1.5'
                  >
                    {c}
                  </li>
                ))}
              </ul>
            </>
          )}
          {type === 'AD/CVD' && (
            <>
              <div className='grid grid-cols-2 gap-3'>
                <Field
                  label='Exposure'
                  value={detail.adcvd.exposure ? 'In scope' : 'Out of scope'}
                />
                <Field
                  label='Active cases'
                  value={String(detail.adcvd.cases.length)}
                />
              </div>
              <SectionLabel>Scope determination</SectionLabel>
              <p className={cn('font-medium', SEV_TEXT[detail.adcvd.severity])}>
                {detail.adcvd.scopeDetermination}
              </p>
              {detail.adcvd.cases.length > 0 && (
                <>
                  <SectionLabel>Cases</SectionLabel>
                  <div className='overflow-x-auto -mx-1 px-1'>
                    <table className='w-full text-[11px]'>
                      <thead className='text-muted-foreground'>
                        <tr className='border-b border-[var(--edge-divider)]'>
                          <th className='text-left font-medium py-1.5 pr-2 uppercase tracking-wider'>
                            Case #
                          </th>
                          <th className='text-left font-medium py-1.5 pr-2 uppercase tracking-wider'>
                            Product
                          </th>
                          <th className='text-left font-medium py-1.5 pr-2 uppercase tracking-wider'>
                            Producer
                          </th>
                          <th className='text-left font-medium py-1.5 pr-2 uppercase tracking-wider'>
                            Country
                          </th>
                          <th className='text-right font-medium py-1.5 pl-2 uppercase tracking-wider'>
                            Rate
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.adcvd.cases.map((c) => (
                          <tr
                            key={c.caseNumber}
                            className='border-b border-[var(--edge-separator)]'
                          >
                            <td className='py-1.5 pr-2 font-mono'>
                              {c.caseNumber}
                            </td>
                            <td className='py-1.5 pr-2'>{c.product}</td>
                            <td className='py-1.5 pr-2'>{c.producer}</td>
                            <td className='py-1.5 pr-2'>{c.country}</td>
                            <td className='py-1.5 pl-2 text-right tabular-nums text-[var(--warning)] font-medium'>
                              {c.rate === null ? '—' : `${c.rate}%`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              <SectionLabel>Affected country / producer</SectionLabel>
              <p>
                <span className='font-medium'>
                  {product.countryOfOrigin || '—'}
                </span>
                {getImportEntity(product.supplierId)?.name && (
                  <span className='text-muted-foreground'>
                    {' '}
                    — {getImportEntity(product.supplierId)?.name}
                  </span>
                )}
              </p>
              <SectionLabel>Reasoning</SectionLabel>
              <p className='text-muted-foreground'>{detail.adcvd.reasoning}</p>
            </>
          )}
        </div>
        <footer className='px-4 py-3 border-t border-[var(--edge-separator)] flex items-center justify-between text-[11px] text-muted-foreground'>
          <span className='inline-flex items-center gap-1'>
            <Clock className='size-3' /> Last checked just now
          </span>
          <button onClick={onClose} className='text-foreground hover:underline'>
            Close
          </button>
        </footer>
      </aside>
    </div>
  );
}

function RecordDrawer({
  record,
  onClose,
}: {
  record: Record<string, string | number> | null;
  onClose: () => void;
}) {
  if (!record) return null;
  const title = String(
    record.Entity ??
      record.Party ??
      record['HS Code'] ??
      record.Program ??
      'Record detail',
  );
  return (
    <div className='fixed inset-0 z-50 flex'>
      <div className='flex-1 bg-black/50' onClick={onClose} />
      <aside className='w-full max-w-[520px] bg-card border-l border-[var(--edge-separator)] flex flex-col'>
        <header className='flex items-center justify-between px-4 py-3 border-b border-[var(--edge-separator)]'>
          <div>
            <div className='text-[10px] uppercase tracking-wider text-muted-foreground'>
              Screening record
            </div>
            <h3 className='text-sm font-semibold'>{title}</h3>
          </div>
          <button onClick={onClose} className='p-1 rounded hover:bg-accent'>
            <X className='size-4' />
          </button>
        </header>
        <div className='flex-1 overflow-auto p-4'>
          <div className='grid grid-cols-2 gap-3'>
            {Object.entries(record).map(([k, v]) => (
              <Field key={k} label={k} value={String(v ?? '—')} />
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className='text-[10px] uppercase tracking-wider text-muted-foreground font-medium pt-1'>
      {children}
    </div>
  );
}

/* ─── Tabbed compact panel: Documents / Comments / Activity ─── */

function TabbedPanel({
  product,
  detail,
}: {
  product: ImportProduct;
  detail: ProductDetail;
}) {
  void product;
  void detail;
  return null;
}

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function timeAgo(dateStr?: string): string {
  if (!dateStr) return '—';
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return '—';
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function DocumentsView({
  detail,
  isRealProduct,
  documents,
  loading,
  onDownload,
}: {
  detail: ProductDetail;
  isRealProduct: boolean;
  documents: ImportServiceDocument[] | null;
  loading: boolean;
  onDownload: (doc: ImportServiceDocument) => void;
}) {
  const rows: {
    id: string;
    name: string;
    type: string;
    date: string;
    size: string;
    onClick?: () => void;
  }[] = isRealProduct
    ? (documents ?? []).map((d) => ({
        id: d.id,
        name: d.file_name,
        type: d.file_content_type,
        date: formatDate(d.created_at),
        size: formatFileSize(d.file_size),
        onClick: () => onDownload(d),
      }))
    : detail.documents.map((d) => ({
        id: d.id,
        name: d.name,
        type: d.type,
        date: d.date,
        size: d.size,
      }));

  return (
    <section className='space-y-3'>
      <div>
        <h2 className='text-base font-semibold'>Documents</h2>
        <p className='text-xs text-muted-foreground mt-0.5'>
          All files uploaded for this product.
        </p>
      </div>
      <div className='elms-card'>
        {isRealProduct && loading ? (
          <div className='p-6 text-center text-sm text-muted-foreground'>
            <span className='inline-flex items-center gap-2'>
              <span
                className='ih-spinner'
                style={{ width: 14, height: 14, borderWidth: 2 }}
              />
              Loading documents…
            </span>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState text='No documents uploaded for this product.' />
        ) : (
          <table className='w-full text-xs'>
            <thead className='text-muted-foreground'>
              <tr className='border-b border-[var(--edge-divider)]'>
                <th className='text-left py-2 pr-3 text-[11px] uppercase tracking-wider font-medium'>
                  Name
                </th>
                <th className='text-left py-2 pr-3 text-[11px] uppercase tracking-wider font-medium'>
                  Type
                </th>
                <th className='text-left py-2 pr-3 text-[11px] uppercase tracking-wider font-medium'>
                  Date
                </th>
                <th className='text-right py-2 text-[11px] uppercase tracking-wider font-medium'>
                  Size
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr
                  key={d.id}
                  className='border-b border-[var(--edge-separator)]'
                >
                  <td className='py-2 pr-3 flex items-center gap-2'>
                    <FileText className='size-3.5 text-muted-foreground' />
                    <span
                      className={cn(
                        'text-primary',
                        d.onClick && 'hover:underline cursor-pointer',
                      )}
                      onClick={d.onClick}
                    >
                      {d.name}
                    </span>
                  </td>
                  <td className='py-2 pr-3'>{d.type}</td>
                  <td className='py-2 pr-3'>{d.date}</td>
                  <td className='py-2 text-right tabular-nums'>{d.size}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function CommentsView({
  detail,
  isRealProduct,
  comments,
  loading,
  currentUserId,
  posting,
  onPost,
  onDelete,
}: {
  detail: ProductDetail;
  isRealProduct: boolean;
  comments: ImportServiceComment[] | null;
  loading: boolean;
  currentUserId?: string;
  posting: boolean;
  onPost: (content: string) => void;
  onDelete: (commentId: string) => void;
}) {
  const readOnly = useImportHubReadOnly();
  const [draft, setDraft] = useState('');

  const rows: {
    id: string;
    author: string;
    time: string;
    text: string;
    canDelete: boolean;
  }[] = isRealProduct
    ? (comments ?? []).map((c) => ({
        id: c.id,
        author: c.author_name || 'Anonymous',
        time: timeAgo(c.created_at),
        text: c.content,
        canDelete: !!currentUserId && c.author_id === currentUserId,
      }))
    : detail.comments.map((c) => ({
        id: c.id,
        author: c.author,
        time: c.time,
        text: c.text,
        canDelete: false,
      }));

  const submit = () => {
    if (!draft.trim() || readOnly) return;
    onPost(draft);
    setDraft('');
  };

  return (
    <section className='space-y-3'>
      <div>
        <h2 className='text-base font-semibold'>Comments</h2>
        <p className='text-xs text-muted-foreground mt-0.5'>
          Discussion thread for this product.
        </p>
      </div>
      <div className='elms-card'>
        {isRealProduct && loading ? (
          <div className='p-6 text-center text-sm text-muted-foreground'>
            <span className='inline-flex items-center gap-2'>
              <span
                className='ih-spinner'
                style={{ width: 14, height: 14, borderWidth: 2 }}
              />
              Loading comments…
            </span>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState text='No comments yet.' />
        ) : (
          <ul className='space-y-2.5'>
            {rows.map((c) => (
              <li key={c.id} className='rounded-md bg-[var(--bg-surface)] p-3'>
                <div className='flex items-center justify-between text-xs'>
                  <span className='font-medium'>{c.author}</span>
                  <span className='flex items-center gap-2'>
                    <span className='text-muted-foreground'>{c.time}</span>
                    {c.canDelete && (
                      <button
                        type='button'
                        onClick={() => !readOnly && onDelete(c.id)}
                        disabled={readOnly}
                        aria-label='Delete comment'
                        title={
                          readOnly ? 'Requires a Business license' : undefined
                        }
                        className='text-muted-foreground hover:text-[var(--danger)] disabled:opacity-60 disabled:cursor-not-allowed'
                      >
                        {readOnly ? (
                          <Lock className='size-3.5' />
                        ) : (
                          <X className='size-3.5' />
                        )}
                      </button>
                    )}
                  </span>
                </div>
                <p className='text-sm mt-1'>{c.text}</p>
              </li>
            ))}
          </ul>
        )}
        {isRealProduct && (
          <div className='mt-3 flex gap-2'>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder='Add a comment…'
              className='flex-1 h-9 px-3 rounded-md border border-input bg-background text-sm'
            />
            <HeaderBtn
              icon={MessageSquare}
              primary
              onClick={submit}
              disabled={posting || !draft.trim()}
              spinning={posting}
              locked={readOnly}
            >
              Post
            </HeaderBtn>
          </div>
        )}
      </div>
    </section>
  );
}

function _LegacyTabbedPanel({
  product,
  detail,
}: {
  product: ImportProduct;
  detail: ProductDetail;
}) {
  const activityEntries = [
    { time: 'Just now', text: 'Screening completed', icon: Search },
    {
      time: '2h ago',
      text: `Tariff classification confirmed at ${product.htsCode || '—'}`,
      icon: FileText,
    },
    ...(detail.deniedPartyMatches.length
      ? [
          {
            time: '1d ago',
            text: 'Denied party match raised for review',
            icon: ShieldAlert,
          },
        ]
      : []),
    { time: '3d ago', text: 'Product added to database', icon: Package },
  ];
  const [tab, setTab] = useState<'documents' | 'comments' | 'activity'>(
    'documents',
  );
  const tabs: { id: typeof tab; label: string; count: number }[] = [
    { id: 'documents', label: 'Documents', count: detail.documents.length },
    { id: 'comments', label: 'Comments', count: detail.comments.length },
    { id: 'activity', label: 'Activity', count: activityEntries.length },
  ];
  return (
    <div className='elms-card !p-0 overflow-hidden'>
      <div className='flex items-center justify-between gap-2 px-3 pt-2 border-b border-[var(--edge-divider)]'>
        <div className='flex items-center gap-1'>
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                'px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px',
                tab === t.id
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
              <span className='ml-1.5 text-[10px] tabular-nums text-muted-foreground'>
                {t.count}
              </span>
            </button>
          ))}
        </div>
        {tab === 'documents' && <HeaderBtn icon={Upload}>Upload</HeaderBtn>}
      </div>
      <div className='p-3'>
        {tab === 'documents' &&
          (detail.documents.length === 0 ? (
            <EmptyState text='No documents uploaded for this product.' />
          ) : (
            <table className='w-full text-xs'>
              <thead className='text-muted-foreground'>
                <tr className='border-b border-[var(--edge-divider)]'>
                  <th className='text-left py-2 pr-3 text-[11px] uppercase tracking-wider font-medium'>
                    Name
                  </th>
                  <th className='text-left py-2 pr-3 text-[11px] uppercase tracking-wider font-medium'>
                    Type
                  </th>
                  <th className='text-left py-2 pr-3 text-[11px] uppercase tracking-wider font-medium'>
                    Date
                  </th>
                  <th className='text-right py-2 text-[11px] uppercase tracking-wider font-medium'>
                    Size
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.documents.map((d) => (
                  <tr
                    key={d.id}
                    className='border-b border-[var(--edge-separator)]'
                  >
                    <td className='py-2 pr-3 flex items-center gap-2'>
                      <FileText className='size-3.5 text-muted-foreground' />
                      <span className='text-primary hover:underline cursor-pointer'>
                        {d.name}
                      </span>
                    </td>
                    <td className='py-2 pr-3'>{d.type}</td>
                    <td className='py-2 pr-3'>{d.date}</td>
                    <td className='py-2 text-right tabular-nums'>{d.size}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}
        {tab === 'comments' && (
          <div>
            {detail.comments.length === 0 ? (
              <EmptyState text='No comments yet.' />
            ) : (
              <ul className='space-y-2.5'>
                {detail.comments.map((c) => (
                  <li
                    key={c.id}
                    className='rounded-md bg-[var(--bg-surface)] p-3'
                  >
                    <div className='flex items-center justify-between text-xs'>
                      <span className='font-medium'>{c.author}</span>
                      <span className='text-muted-foreground'>{c.time}</span>
                    </div>
                    <p className='text-sm mt-1'>{c.text}</p>
                  </li>
                ))}
              </ul>
            )}
            <div className='mt-3 flex gap-2'>
              <input
                placeholder='Add a comment…'
                className='flex-1 h-9 px-3 rounded-md border border-input bg-background text-sm'
              />
              <HeaderBtn icon={MessageSquare} primary>
                Post
              </HeaderBtn>
            </div>
          </div>
        )}
        {tab === 'activity' &&
          (activityEntries.length === 0 ? (
            <EmptyState text='No activity yet.' />
          ) : (
            <ul className='divide-y divide-[var(--edge-separator)]'>
              {activityEntries.map((e, i) => {
                const Icon = e.icon;
                return (
                  <li key={i} className='py-2 flex items-center gap-3 text-xs'>
                    <Icon className='size-3.5 text-muted-foreground' />
                    <span className='flex-1'>{e.text}</span>
                    <span className='text-muted-foreground'>{e.time}</span>
                  </li>
                );
              })}
            </ul>
          ))}
      </div>
    </div>
  );
}
/* ─── Compliance Score helpers ─── */

interface ScoreDeduction {
  rule: string;
  passed: boolean;
  points: number; // negative when failed
  reason: string;
  category: 'HTS Classification Accuracy' | 'Duty and Tariff Validation';
}

function buildRealScoreDeductions(
  issues: string[],
  clearFlags: string[],
): ScoreDeduction[] {
  const isActive = (issue: string) =>
    issues.includes(issue) && !clearFlags.includes(issue);

  return [
    {
      category: 'HTS Classification Accuracy',
      rule:
        isActive('Missing HTS Code') || isActive('Invalid HTS Code')
          ? 'HTS code not present and valid'
          : 'HTS code present and valid',
      passed: !isActive('Missing HTS Code') && !isActive('Invalid HTS Code'),
      points:
        isActive('Missing HTS Code') || isActive('Invalid HTS Code') ? -25 : 0,
      reason: isActive('Missing HTS Code')
        ? 'No HTS code is on file.'
        : isActive('Invalid HTS Code')
        ? 'The current HTS code could not be verified.'
        : 'The product has a valid HTS classification or the issue was cleared.',
    },
    {
      category: 'HTS Classification Accuracy',
      rule: 'Country of origin present',
      passed: !isActive('Missing Country of Origin'),
      points: isActive('Missing Country of Origin') ? -10 : 0,
      reason: isActive('Missing Country of Origin')
        ? 'Country of origin is missing.'
        : 'Country of origin is present or the issue was cleared.',
    },
    {
      category: 'Duty and Tariff Validation',
      rule: 'Screening risk below medium',
      passed: !isActive('Risk Level Medium/High'),
      points: isActive('Risk Level Medium/High') ? -10 : 0,
      reason: isActive('Risk Level Medium/High')
        ? 'The latest AI screening result is Medium or High risk.'
        : 'No active Medium or High AI risk deduction applies.',
    },
  ];
}

function buildScoreDeductions(
  p: ImportProduct,
  detail: ProductDetail,
  htsCondensed: string,
): ScoreDeduction[] {
  const out: ScoreDeduction[] = [];

  // HTS Classification Accuracy
  const htsValid = Boolean(p.htsCode && htsCondensed.length === 10);
  out.push({
    category: 'HTS Classification Accuracy',
    rule: htsValid
      ? 'HTS code present and valid'
      : 'HTS code not present and valid',
    passed: htsValid,
    points: htsValid ? 0 : -25,
    reason: !p.htsCode
      ? 'No HTS code is on file.'
      : htsCondensed.length !== 10
      ? 'HTS code is not a complete ten-digit number.'
      : 'The product has a valid HTS classification.',
  });
  const descOk = (p.description || '').length >= 40;
  out.push({
    category: 'HTS Classification Accuracy',
    rule: 'Product description quality',
    passed: descOk,
    points: descOk ? 0 : -10,
    reason: descOk
      ? 'Description provides sufficient detail vs. HTS heading language.'
      : 'Product description is shorter than the official HTS heading language requires.',
  });

  // Duty and Tariff Validation
  const dutyCalcOk = detail.cost.totalDutyCost >= 0; // demo: always ok unless explicitly broken
  out.push({
    category: 'Duty and Tariff Validation',
    rule: 'Total stacked duty/tariff calculation',
    passed: dutyCalcOk,
    points: dutyCalcOk ? 0 : -10,
    reason: dutyCalcOk
      ? 'Stacked duty calculation reconciles to declared value and applicable lines.'
      : 'Total stacked duty/tariff calculation incorrect.',
  });
  const stackingOk = !(p.section232 && !p.htsCode); // proxy
  out.push({
    category: 'Duty and Tariff Validation',
    rule: 'Stacking validation — exclusions/exemptions',
    passed: stackingOk,
    points: stackingOk ? 0 : -10,
    reason: stackingOk
      ? 'Applicable exclusions and exemptions are correctly applied to the stack.'
      : 'Stacking validation does not correctly apply exclusions/exemptions.',
  });

  return out;
}

interface ScoreFlag {
  label: string;
  detail: string;
}

function buildScoreFlags(p: ImportProduct, detail: ProductDetail): ScoreFlag[] {
  const out: ScoreFlag[] = [];
  if (detail.adcvd.exposure) {
    out.push({
      label: 'AD/CVD flag',
      detail: `${detail.adcvd.cases.length} active order${
        detail.adcvd.cases.length > 1 ? 's' : ''
      } — country of origin required.`,
    });
  }
  p.pgaFlags.forEach((a) => {
    out.push({ label: `PGA — ${a}`, detail: `${a} filing required at entry.` });
  });
  if (p.pgaFlags.includes('CPSC' as never)) {
    // already listed
  }
  if (p.uflpaRisk !== 'none') {
    out.push({
      label: 'Forced-labor code',
      detail: `UFLPA risk: ${p.uflpaRisk.toUpperCase()}.`,
    });
  }
  if (p.section232) {
    out.push({
      label: 'Future steel/aluminum license',
      detail:
        'Anticipated import license requirement for Section 232 commodities.',
    });
  }
  return out;
}

const BAND_BG: Record<Severity, string> = {
  success: 'bg-[var(--success)]',
  info: 'bg-[var(--action-color)]',
  warning: 'bg-[var(--warning)]',
  danger: 'bg-[var(--danger)]',
};

function ScoreBar({ score, band }: { score: number; band: Severity }) {
  return (
    <div className='h-1.5 w-full rounded-full bg-[var(--bg-surface)] overflow-hidden'>
      <div
        className={cn('h-full rounded-full transition-all', BAND_BG[band])}
        style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
      />
    </div>
  );
}

function ScoreKpiTile({
  score,
  band,
  bandLabel,
  onClick,
}: {
  score: number;
  band: Severity;
  bandLabel: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className='kpi-tile text-left hover:bg-[var(--row-hover)] transition-colors'
    >
      <div
        className={cn(
          'flex items-center gap-1.5 text-[11px] uppercase tracking-wider',
          SEV_TEXT[band],
        )}
      >
        <Award className='size-3.5' /> Compliance Score
      </div>
      <div className='mt-1 flex items-baseline gap-2'>
        <span className='text-xl font-semibold tabular-nums'>{score}</span>
        <span
          className={cn(
            'text-[10px] uppercase tracking-wider font-semibold',
            SEV_TEXT[band],
          )}
        >
          {bandLabel}
        </span>
      </div>
      <div className='mt-2'>
        <ScoreBar score={score} band={band} />
      </div>
    </button>
  );
}

/* ─── KPI trio (Score · System Flags · Risk) ─── */

interface SystemFlagItem {
  label: string;
  detail?: string;
  severity: Severity;
  anchor?: 'classification' | 'tariffs' | 'screening' | 'flags' | 'entities';
  cleared?: boolean;
  sourceIssue?: string;
}

function complianceIssueSeverity(issue: string): Severity {
  if (
    issue === 'Risk Level Medium/High' ||
    issue === 'Missing HTS Code' ||
    issue === 'Invalid HTS Code' ||
    issue === 'Missing Country of Origin'
  ) {
    return 'danger';
  }
  if (issue === 'FTA Eligible') return 'info';
  return 'warning';
}

function complianceIssueAnchor(issue: string): SystemFlagItem['anchor'] {
  if (issue.includes('HTS') || issue.includes('Country of Origin')) {
    return 'classification';
  }
  if (
    issue.includes('Duty') ||
    issue.includes('Tariff') ||
    issue.includes('FTA')
  ) {
    return 'tariffs';
  }
  if (issue.includes('Risk')) return 'screening';
  return 'flags';
}

function buildSystemFlags(
  p: ImportProduct,
  detail: ProductDetail,
  htsCondensed: string,
  invalidHts?: boolean,
): SystemFlagItem[] {
  const out: SystemFlagItem[] = [];
  if (!p.htsCode || htsCondensed.length !== 10 || invalidHts) {
    out.push({
      label: 'HTS Code Invalid',
      detail: !p.htsCode
        ? 'No HTS on file.'
        : invalidHts
        ? 'HTS code is expired or invalid — recommended replacements available.'
        : 'HTS code is not a complete 10-digit.',
      severity: 'danger',
      anchor: 'classification',
    });
  } else {
    out.push({
      label: 'HTS Document Unverified',
      detail: 'Supporting classification doc not yet verified.',
      severity: 'warning',
      anchor: 'classification',
    });
  }
  const addlDuty =
    (p.section301Rate || 0) +
    (p.section232Rate || 0) +
    (p.additionalDutyPct || 0);
  if (addlDuty > 0) {
    out.push({
      label: 'Additional Duty Rate > 0%',
      detail: `Stacked additional duty ~${addlDuty}%.`,
      severity: 'warning',
      anchor: 'tariffs',
    });
  }
  if (p.section301) {
    out.push({
      label: 'Tariff Changes',
      detail: 'Section 301 rate updated recently.',
      severity: 'warning',
      anchor: 'tariffs',
    });
  }
  if (p.ftaEligible) {
    out.push({
      label: 'FTA Eligible',
      detail: `${p.fta} program eligibility detected.`,
      severity: 'info',
      anchor: 'tariffs',
    });
  }
  p.pgaFlags.forEach((a) => {
    out.push({
      label: `New Flag Identified — ${a}`,
      detail: `${a} filing required at entry.`,
      severity: 'warning',
      anchor: 'flags',
    });
  });
  if (p.uflpaRisk !== 'none') {
    out.push({
      label: 'Forced-Labor Risk',
      detail: `UFLPA risk: ${p.uflpaRisk.toUpperCase()}.`,
      severity: p.uflpaRisk === 'high' ? 'danger' : 'warning',
      anchor: 'flags',
    });
  }
  if (detail.adcvd.exposure) {
    out.push({
      label: 'AD/CVD Exposure',
      detail: `${detail.adcvd.cases.length} active order(s).`,
      severity: 'danger',
      anchor: 'flags',
    });
  }
  return out;
}

function ScoreCard({
  score,
  assessed,
  band,
  bandLabel,
  deductionCount,
  onOpen,
}: {
  score: number;
  assessed: boolean;
  band: Severity;
  bandLabel: string;
  deductionCount: number;
  onOpen: () => void;
}) {
  return (
    <button
      onClick={assessed ? onOpen : undefined}
      disabled={!assessed}
      className={cn(
        'elms-card text-left group transition-all flex flex-col gap-3',
        assessed
          ? 'cursor-pointer hover:shadow-md hover:border-primary/40'
          : 'cursor-default',
      )}
      aria-label={
        assessed
          ? 'Open compliance score breakdown'
          : 'Compliance score not assessed'
      }
    >
      <div className='flex items-center justify-between gap-2'>
        <div
          className={cn(
            'flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold',
            assessed ? SEV_TEXT[band] : 'text-muted-foreground',
          )}
        >
          <Award className='size-3.5' /> Compliance Score
        </div>
        {assessed && (
          <span
            className={cn(
              'inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5',
              SEV_BADGE[band],
            )}
          >
            {bandLabel}
          </span>
        )}
      </div>
      {assessed ? (
        <>
          <div className='flex items-baseline gap-2'>
            <span className='text-4xl font-semibold tabular-nums leading-none'>
              {score}
            </span>
            <span className='text-xs text-muted-foreground'>/ 100</span>
          </div>
          <ScoreBar score={score} band={band} />
          <div className='flex items-center justify-between text-[11px] text-muted-foreground'>
            <span>
              {deductionCount > 0
                ? `${deductionCount} deduction${
                    deductionCount > 1 ? 's' : ''
                  } applied`
                : 'No deductions applied'}
            </span>
            <span className='inline-flex items-center gap-1 text-primary group-hover:underline'>
              How we calculate your score <ChevronRight className='size-3' />
            </span>
          </div>
        </>
      ) : (
        <div className='flex flex-1 flex-col justify-center gap-1 py-3'>
          <span className='text-xl font-semibold'>Not assessed</span>
          <span className='text-xs text-muted-foreground'>
            Run Analyze Product to calculate a compliance score.
          </span>
        </div>
      )}
    </button>
  );
}

function SystemFlagsCard({
  flags,
  onSetCleared,
  savingFlag,
}: {
  flags: SystemFlagItem[];
  onSetCleared?: (flag: string, cleared: boolean) => void;
  savingFlag?: string | null;
}) {
  const readOnly = useImportHubReadOnly();
  const [allOpen, setAllOpen] = useState(false);
  const activeFlags = flags.filter((flag) => !flag.cleared);
  const worst: Severity = activeFlags.some((f) => f.severity === 'danger')
    ? 'danger'
    : activeFlags.some((f) => f.severity === 'warning')
    ? 'warning'
    : activeFlags.length
    ? 'info'
    : 'success';
  const SEV_RANK: Record<Severity, number> = {
    danger: 0,
    warning: 1,
    info: 2,
    success: 3,
  };
  const sorted = [...flags].sort(
    (a, b) =>
      Number(Boolean(a.cleared)) - Number(Boolean(b.cleared)) ||
      SEV_RANK[a.severity] - SEV_RANK[b.severity],
  );
  const recent = sorted.filter((flag) => !flag.cleared).slice(0, 3);
  const extra = activeFlags.length - recent.length;
  return (
    <>
      <div className='elms-card flex flex-col gap-3'>
        <div className='flex items-center justify-between gap-2'>
          <div
            className={cn(
              'flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold',
              SEV_TEXT[worst],
            )}
          >
            <Flag className='size-3.5' /> System Flags
          </div>
          <span className='text-[10px] uppercase tracking-wider text-muted-foreground tabular-nums'>
            {activeFlags.length} active
          </span>
        </div>
        {activeFlags.length === 0 ? (
          <div className='rounded-md p-3 bg-[color-mix(in_oklch,var(--success)_10%,transparent)] text-xs flex items-center gap-2'>
            <CheckCircle2 className='size-4 text-[var(--success)]' />
            {flags.length > 0
              ? 'All product flags have been cleared.'
              : 'No system flags on this product.'}
          </div>
        ) : (
          <ul className='space-y-1.5'>
            {recent.map((f, i) => (
              <li key={i}>
                <button
                  type='button'
                  onClick={() => scrollToAnchor(f.anchor)}
                  className={cn(
                    'w-full text-left rounded-md p-2 flex items-start gap-2 cursor-pointer transition-colors hover:bg-[var(--row-hover)]',
                    SEV_BG[f.severity],
                  )}
                >
                  <AlertTriangle
                    className={cn(
                      'size-3.5 mt-0.5 shrink-0',
                      SEV_TEXT[f.severity],
                    )}
                  />
                  <div className='flex-1 min-w-0'>
                    <div className='text-xs font-medium truncate'>
                      {f.label}
                    </div>
                    {f.detail && (
                      <div className='text-[11px] text-muted-foreground truncate'>
                        {f.detail}
                      </div>
                    )}
                  </div>
                  <ChevronRight className='size-3 text-muted-foreground mt-1 shrink-0' />
                </button>
              </li>
            ))}
          </ul>
        )}
        {flags.length > 0 && (
          <button
            type='button'
            onClick={() => setAllOpen(true)}
            className='mt-auto text-[11px] text-primary hover:underline inline-flex items-center gap-1 self-start'
          >
            {extra > 0
              ? `View all ${flags.length} flags`
              : flags.some((flag) => flag.cleared)
              ? 'View cleared flags'
              : 'View all flags'}{' '}
            <ChevronRight className='size-3' />
          </button>
        )}
      </div>
      {allOpen && (
        <div className='fixed inset-0 z-50 flex'>
          <div
            className='flex-1 bg-black/50'
            onClick={() => setAllOpen(false)}
          />
          <aside className='w-full max-w-[520px] bg-card border-l border-[var(--edge-separator)] flex flex-col'>
            <header className='flex items-center justify-between px-4 py-3 border-b border-[var(--edge-separator)]'>
              <div>
                <div className='text-[10px] uppercase tracking-wider text-muted-foreground'>
                  System
                </div>
                <h3 className='text-sm font-semibold'>
                  System Flags ({flags.length})
                </h3>
              </div>
              <button
                onClick={() => setAllOpen(false)}
                className='p-1 rounded hover:bg-accent'
              >
                <X className='size-4' />
              </button>
            </header>
            <div className='flex-1 overflow-auto p-4 space-y-2'>
              {sorted.map((f, i) => (
                <div
                  key={i}
                  className={cn(
                    'w-full rounded-md p-3 flex items-start gap-2 transition-colors',
                    f.cleared
                      ? 'bg-[var(--bg-surface)] opacity-75'
                      : SEV_BG[f.severity],
                  )}
                >
                  {f.cleared ? (
                    <CheckCircle2 className='size-4 mt-0.5 shrink-0 text-[var(--success)]' />
                  ) : (
                    <AlertTriangle
                      className={cn(
                        'size-4 mt-0.5 shrink-0',
                        SEV_TEXT[f.severity],
                      )}
                    />
                  )}
                  <div className='flex-1 min-w-0'>
                    <button
                      type='button'
                      onClick={() => {
                        setAllOpen(false);
                        scrollToAnchor(f.anchor);
                      }}
                      className='text-left text-xs font-medium hover:underline'
                    >
                      {f.label}
                    </button>
                    {f.detail && (
                      <div className='text-[11px] text-muted-foreground mt-0.5'>
                        {f.detail}
                      </div>
                    )}
                    {f.cleared && (
                      <div className='text-[10px] uppercase tracking-wider text-[var(--success)] mt-1'>
                        Cleared
                      </div>
                    )}
                  </div>
                  {onSetCleared && f.sourceIssue ? (
                    <button
                      type='button'
                      disabled={Boolean(savingFlag) || readOnly}
                      title={
                        readOnly ? 'Requires a Business license' : undefined
                      }
                      onClick={() =>
                        onSetCleared(f.sourceIssue as string, !f.cleared)
                      }
                      className='h-7 px-2.5 rounded-md border border-input bg-background hover:bg-accent text-[11px] font-medium disabled:opacity-60 shrink-0'
                    >
                      {readOnly && <Lock className='size-3' />}{' '}
                      {savingFlag === f.sourceIssue
                        ? 'Saving…'
                        : f.cleared
                        ? 'Restore'
                        : 'Clear'}
                    </button>
                  ) : (
                    <ChevronRight className='size-3 text-muted-foreground mt-1 shrink-0' />
                  )}
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

function RiskCard({
  state,
  ranAt,
  risk,
  matchProbability,
  onRun,
  onOpen,
  hideRunButton,
}: {
  state: 'idle' | 'running' | 'done';
  ranAt: string | null;
  risk: string;
  matchProbability?: number | null;
  onRun: () => void;
  onOpen: () => void;
  /** True when this evaluation is real (not the mock Run/Re-run flow) — there's
   * no real re-run action to wire yet, so the button is hidden rather than
   * left pointing at the mock. */
  hideRunButton?: boolean;
}) {
  const riskTone: Severity =
    risk === 'Critical'
      ? 'danger'
      : risk === 'High'
      ? 'warning'
      : risk === 'Medium'
      ? 'info'
      : 'success';
  const confidence = formatAiConfidence(matchProbability);

  if (state !== 'done') {
    return (
      <div className='elms-card flex flex-col gap-3'>
        <div className='flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground'>
          <Sparkles className='size-3.5' /> Risk
        </div>
        <div className='flex-1 flex flex-col items-center justify-center text-center gap-3 py-2'>
          <div className='size-10 rounded-full bg-[var(--bg-surface)] flex items-center justify-center text-primary'>
            <Sparkles className='size-4' />
          </div>
          {hideRunButton ? (
            <p className='text-[11px] text-muted-foreground max-w-[240px]'>
              No AI risk evaluation is on file. Use Analyze Product to run the
              product screening.
            </p>
          ) : (
            <>
              <p className='text-[11px] text-muted-foreground max-w-[240px]'>
                Generate an AI risk evaluation — risk level, confidence, key
                factors and recommendation.
              </p>
              <button
                type='button'
                onClick={onRun}
                disabled={state === 'running'}
                className='inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-medium disabled:opacity-60'
              >
                {state === 'running' ? (
                  <>
                    <RotateCw className='size-3.5 animate-spin' /> Running…
                  </>
                ) : (
                  <>
                    <PlayCircle className='size-3.5' /> Run Risk Evaluation
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }
  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className='elms-card text-left group cursor-pointer transition-all hover:shadow-md hover:border-primary/40 flex flex-col gap-3'
      aria-label='Open full risk evaluation'
    >
      <div className='flex items-center justify-between gap-2'>
        <div
          className={cn(
            'flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold',
            SEV_TEXT[riskTone],
          )}
        >
          <Sparkles className='size-3.5' /> Risk
        </div>
        <span className='text-[10px] text-muted-foreground inline-flex items-center gap-1'>
          <Clock className='size-3' /> {ranAt}
        </span>
      </div>
      <div className='grid grid-cols-2 gap-3'>
        <div>
          <div className='text-[10px] uppercase tracking-wider text-muted-foreground font-medium'>
            Risk
          </div>
          <div className={cn('text-lg font-semibold', SEV_TEXT[riskTone])}>
            {risk}
          </div>
        </div>
        <div>
          <div className='text-[10px] uppercase tracking-wider text-muted-foreground font-medium'>
            Confidence
          </div>
          <div className='text-lg font-semibold text-foreground'>
            {confidence || '—'}
          </div>
        </div>
      </div>
      <div
        className={cn(
          'flex items-center gap-2 text-[11px] text-muted-foreground',
          hideRunButton ? 'justify-end' : 'justify-between',
        )}
      >
        {!hideRunButton && (
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              onRun();
            }}
            className='inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-input bg-background hover:bg-accent text-xs'
          >
            <RotateCw className='size-3' /> Re-run
          </button>
        )}
        <span className='inline-flex items-center gap-1 text-primary group-hover:underline'>
          View full evaluation <ChevronRight className='size-3' />
        </span>
      </div>
    </div>
  );
}

function ScoreDrawer({
  open,
  onClose,
  score,
  band,
  bandLabel,
  deductions,
}: {
  open: boolean;
  onClose: () => void;
  score: number;
  band: Severity;
  bandLabel: string;
  deductions: ScoreDeduction[];
}) {
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  if (!open) return null;

  const failed = deductions.filter((d) => !d.passed);
  const passed = deductions.filter((d) => d.passed);
  const totalDeducted = failed.reduce((s, d) => s + d.points, 0);

  const grouped = {
    'HTS Classification Accuracy': deductions.filter(
      (d) => d.category === 'HTS Classification Accuracy',
    ),
    'Duty and Tariff Validation': deductions.filter(
      (d) => d.category === 'Duty and Tariff Validation',
    ),
  };

  return (
    <div className='fixed inset-0 z-50 flex'>
      <div className='flex-1 bg-black/50' onClick={onClose} />
      <aside className='w-full max-w-[520px] bg-card border-l border-[var(--edge-separator)] flex flex-col'>
        <header className='flex items-center justify-between px-5 py-3 border-b border-[var(--edge-separator)]'>
          <div>
            <div className='text-[10px] uppercase tracking-wider text-muted-foreground'>
              Compliance
            </div>
            <h3 className='text-sm font-semibold'>Score Breakdown</h3>
          </div>
          <button onClick={onClose} className='p-1 rounded hover:bg-accent'>
            <X className='size-4' />
          </button>
        </header>

        <div className='flex-1 overflow-auto'>
          {/* Part A — this product's score */}
          <section className='p-5 space-y-5'>
            <div className='rounded-lg bg-[var(--bg-surface)] p-4'>
              <div className='flex items-end justify-between gap-3'>
                <div>
                  <div className='text-[10px] uppercase tracking-wider text-muted-foreground'>
                    This product
                  </div>
                  <div className='mt-1 flex items-baseline gap-2'>
                    <span className='text-3xl font-semibold tabular-nums'>
                      {score}
                    </span>
                    <span className='text-sm text-muted-foreground'>/ 100</span>
                  </div>
                </div>
                <span
                  className={cn(
                    'inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5',
                    SEV_BADGE[band],
                  )}
                >
                  {bandLabel}
                </span>
              </div>
              <div className='mt-3'>
                <ScoreBar score={score} band={band} />
              </div>
              <div className='mt-2 text-[11px] text-muted-foreground tabular-nums'>
                {failed.length > 0 ? (
                  <>
                    Total deductions applied:{' '}
                    <span className={SEV_TEXT.danger}>{totalDeducted}%</span>{' '}
                    across {failed.length} rule{failed.length > 1 ? 's' : ''}.
                  </>
                ) : (
                  <>No deductions applied — all scored rules passed.</>
                )}
              </div>
            </div>

            <div>
              <h4 className='text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2'>
                Deductions
              </h4>
              {failed.length === 0 ? (
                <div className='rounded-md p-3 bg-[color-mix(in_oklch,var(--success)_10%,transparent)] text-xs'>
                  All scored rules passed — no points lost.
                </div>
              ) : (
                <ul className='space-y-2'>
                  {failed.map((d, i) => (
                    <li
                      key={i}
                      className={cn(
                        'rounded-md p-3 flex items-start gap-3',
                        SEV_BG.danger,
                      )}
                    >
                      <XCircle className='size-4 mt-0.5 text-[var(--danger)] shrink-0' />
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center justify-between gap-2'>
                          <span className='text-xs font-medium'>{d.rule}</span>
                          <span
                            className={cn(
                              'text-[11px] font-semibold tabular-nums',
                              SEV_TEXT.danger,
                            )}
                          >
                            {d.points}%
                          </span>
                        </div>
                        <p className='text-[11px] text-muted-foreground mt-0.5'>
                          {d.reason}
                        </p>
                        <div className='text-[10px] uppercase tracking-wider text-muted-foreground mt-1'>
                          {d.category}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {passed.length > 0 && (
              <div>
                <h4 className='text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2'>
                  Passed
                </h4>
                <ul className='space-y-1.5'>
                  {passed.map((d, i) => (
                    <li
                      key={i}
                      className='rounded-md p-2.5 flex items-start gap-2 bg-[var(--bg-surface)]'
                    >
                      <CheckCircle2 className='size-3.5 mt-0.5 text-[var(--success)] shrink-0' />
                      <div className='flex-1 min-w-0'>
                        <div className='text-xs font-medium'>{d.rule}</div>
                        <p className='text-[11px] text-muted-foreground mt-0.5'>
                          {d.reason}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* Part B — scoring criteria reference */}
          <section className='border-t border-[var(--edge-separator)] px-5 py-4'>
            <button
              onClick={() => setCriteriaOpen((v) => !v)}
              className='w-full flex items-center justify-between gap-2 text-left'
            >
              <span className='inline-flex items-center gap-1.5 text-xs font-semibold'>
                <Info className='size-3.5 text-muted-foreground' /> How is this
                calculated?
              </span>
              <ChevronDown
                className={cn(
                  'size-4 text-muted-foreground transition-transform',
                  criteriaOpen && 'rotate-180',
                )}
              />
            </button>

            {criteriaOpen && (
              <div className='mt-4 space-y-5'>
                <CriteriaBlock
                  title='HTS Classification Accuracy'
                  impact='−25% for missing or invalid HTS'
                  impactTone='danger'
                  conditions={['HTS code not present and valid']}
                  data={['HTS code']}
                />
                <CriteriaBlock
                  title='Duty and Tariff Validation'
                  impact='−10% each'
                  impactTone='danger'
                  conditions={[
                    'Total stacked duty/tariff calculation incorrect',
                    'Stacking validation does not correctly apply exclusions/exemptions',
                  ]}
                  data={[
                    'HTS code',
                    'Country of origin',
                    'Entry date and loading date',
                    'Aluminum and steel declared values',
                  ]}
                />
              </div>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}

function CriteriaBlock({
  title,
  impact,
  impactTone,
  conditions,
  data,
  helper,
}: {
  title: string;
  impact: string;
  impactTone: Severity;
  conditions: string[];
  data: string[];
  helper?: string;
}) {
  return (
    <div>
      <div className='flex items-center justify-between gap-2'>
        <h5 className='text-xs font-semibold'>{title}</h5>
        <span
          className={cn(
            'inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5',
            SEV_BADGE[impactTone],
          )}
        >
          {impact}
        </span>
      </div>
      {helper && (
        <p className='text-[11px] text-muted-foreground mt-1'>{helper}</p>
      )}
      <ul className='mt-2 space-y-1'>
        {conditions.map((c, i) => (
          <li
            key={i}
            className='text-xs text-foreground flex items-start gap-2'
          >
            <span className='mt-1.5 size-1 rounded-full bg-muted-foreground shrink-0' />
            <span>{c}</span>
          </li>
        ))}
      </ul>
      {data.length > 0 && (
        <div className='mt-2 text-[11px] text-muted-foreground'>
          <span className='uppercase tracking-wider font-medium'>
            Required data:
          </span>{' '}
          {data.join(' · ')}
        </div>
      )}
    </div>
  );
}

/* ─── New Compliance Check content components ─── */

function RiskSummaryContent({ detail }: { detail: ProductDetail }) {
  const riskTone =
    detail.ai.risk === 'Critical'
      ? 'text-[var(--danger)]'
      : detail.ai.risk === 'High'
      ? 'text-[var(--warning)]'
      : detail.ai.risk === 'Medium'
      ? 'text-[var(--action-color)]'
      : 'text-[var(--success)]';
  const confidence = formatAiConfidence(detail.ai.matchProbability);

  if (detail.ai.status === 'error') {
    return (
      <div className='rounded-md p-3 bg-[color-mix(in_oklch,var(--danger)_8%,transparent)] text-xs'>
        <div className='flex items-center gap-2 text-[var(--danger)] font-medium'>
          <XCircle className='size-4' />
          AI screening could not be completed
        </div>
        <p className='mt-1 text-muted-foreground'>
          {detail.ai.error || 'The screening result contains an AI error.'}
        </p>
      </div>
    );
  }

  return (
    <div
      className='rounded-lg p-4'
      style={{
        background: 'var(--bg-surface)',
        borderLeft:
          '3px solid color-mix(in oklch, var(--action-color) 30%, transparent)',
      }}
    >
      <div className='flex items-start justify-between gap-3 mb-3'>
        <div className='flex items-center gap-2'>
          <Sparkles className='size-4 text-primary' />
          <span className='text-xs font-semibold uppercase tracking-wider'>
            AI Summary
          </span>
        </div>
      </div>
      <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
        <Field label='HTS code' value={detail.classification.code} mono />
        <div className='space-y-0.5'>
          <div className='text-[11px] text-muted-foreground uppercase tracking-wider font-medium'>
            Risk
          </div>
          <div className={cn('text-sm font-semibold', riskTone)}>
            {detail.ai.risk}
          </div>
        </div>
        <div className='space-y-0.5'>
          <div className='text-[11px] text-muted-foreground uppercase tracking-wider font-medium'>
            Confidence
          </div>
          <div className='text-sm font-semibold'>{confidence || '—'}</div>
        </div>
      </div>
      <div className='mt-4'>
        <div className='text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5'>
          Key factors
        </div>
        {detail.ai.keyFactors.length ? (
          <ul className='text-xs space-y-1 list-disc list-inside marker:text-muted-foreground'>
            {detail.ai.keyFactors.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        ) : (
          <p className='text-xs text-muted-foreground italic'>
            No key factors identified.
          </p>
        )}
      </div>
      {detail.ai.topMatches && detail.ai.topMatches.length > 0 && (
        <div className='mt-3'>
          <div className='text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5'>
            Top matches
          </div>
          <ul className='space-y-1.5'>
            {detail.ai.topMatches.map((match, index) => (
              <li
                key={match.id || `${match.name}-${index}`}
                className='flex items-center justify-between gap-3 rounded-md bg-background px-2.5 py-2 text-xs'
              >
                <span className='font-medium'>{match.name}</span>
                <span className='text-muted-foreground tabular-nums'>
                  {formatAiConfidence(match.confidence) || '—'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {detail.ai.riskAssessment?.rationale && (
        <div className='mt-3'>
          <div className='text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1'>
            Risk assessment
          </div>
          <p className='text-sm leading-relaxed'>
            {detail.ai.riskAssessment.rationale}
          </p>
          {detail.ai.riskAssessment.highLists &&
            detail.ai.riskAssessment.highLists.length > 0 && (
              <div className='mt-2 text-xs text-muted-foreground'>
                High-risk lists: {detail.ai.riskAssessment.highLists.join(', ')}
              </div>
            )}
        </div>
      )}
      <div className='mt-3'>
        <div className='text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1'>
          Explanation
        </div>
        <p className='text-sm leading-relaxed whitespace-pre-line'>
          {detail.ai.narrative}
        </p>
      </div>
    </div>
  );
}

function ScreeningTable({
  detail,
  product,
  onOpenRecord,
  onCreateFromHit,
  appliedCodes = [],
}: {
  detail: ProductDetail;
  product: ImportProduct;
  onOpenRecord: (r: Record<string, string | number>) => void;
  onCreateFromHit?: (hit: PgaHitOption) => void;
  appliedCodes?: PgaCode[];
}) {
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState<HitSortKey>('productName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const all = useMemo(
    () =>
      flattenMatches(detail.biteLensGroups).filter((m) => m.lensCode === 'PGA'),
    [detail.biteLensGroups],
  );
  const rows = useMemo<HitRow[]>(() => {
    return all.map((m) => {
      const hit = toPgaHitOption(m);
      const resolvedPgaCode = hit?.code ?? null;
      const schema = resolvedPgaCode ? PGA_FLAG_SCHEMAS[resolvedPgaCode] : null;
      const storedPgaFlagCode = String(
        m.raw['PGA Flag Code'] ?? m.raw['PGA Code'] ?? '',
      ).trim();
      const hs = String(m.raw['HS Code'] ?? product.htsCode ?? '—');
      const agency =
        schema?.agency ?? String(m.raw['Agency'] ?? m.lensName ?? '—');
      return {
        productName: product.name,
        hsCode: hs,
        listName: m.list,
        agency,
        pgaFlagCode: storedPgaFlagCode || resolvedPgaCode || '—',
        raw: m.raw,
        hit,
      };
    });
  }, [all, product]);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase();
    const base = !needle
      ? rows
      : rows.filter((m) =>
          [m.productName, m.hsCode, m.listName, m.agency, m.pgaFlagCode].some(
            (v) => String(v).toLowerCase().includes(needle),
          ),
        );
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...base].sort(
      (a, b) => String(a[sortKey]).localeCompare(String(b[sortKey])) * dir,
    );
  }, [rows, q, sortKey, sortDir]);

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(page, pageCount);
  const startIdx = (pageSafe - 1) * pageSize;
  const pageRows = filtered.slice(startIdx, startIdx + pageSize);

  const toggleSort = (k: HitSortKey) => {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(k);
      setSortDir('asc');
    }
  };

  const cols: { key: HitSortKey; label: string }[] = [
    { key: 'productName', label: 'Product' },
    { key: 'hsCode', label: 'HS Code' },
    { key: 'listName', label: 'List Name' },
    { key: 'agency', label: 'Agency' },
    { key: 'pgaFlagCode', label: 'PGA Flag Code' },
  ];

  if (total === 0) {
    return (
      <div className='space-y-3'>
        <div className='relative max-w-[320px]'>
          <Search className='size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground' />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder='Search product, HS, list, agency, flag code…'
            className='h-9 pl-8 pr-2 rounded-md border border-input bg-background text-xs w-full'
          />
        </div>
        <EmptyState
          text={
            q
              ? 'No results match your search.'
              : 'No screening matches — product is clean across this active BITE lens.'
          }
        />
      </div>
    );
  }

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between gap-2 flex-wrap'>
        <div className='relative flex-1 max-w-[320px]'>
          <Search className='size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground' />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder='Search product, HS, list, agency, flag code…'
            className='h-9 pl-8 pr-2 rounded-md border border-input bg-background text-xs w-full'
          />
        </div>
        <div className='flex items-center gap-1.5'>
          <button className='inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-input bg-background hover:bg-accent text-xs font-medium'>
            <Filter className='size-3.5' /> Filters
          </button>
          <button className='inline-flex items-center justify-center size-8 rounded-md border border-input bg-background hover:bg-accent'>
            <RefreshCw className='size-3.5' />
          </button>
        </div>
      </div>

      <div className='rounded-md overflow-hidden bg-[var(--bg-surface)]'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='text-[10px] uppercase tracking-wider text-muted-foreground font-medium'>
              {cols.map((c) => (
                <th key={c.key} className='text-left px-3 py-2.5'>
                  <button
                    onClick={() => toggleSort(c.key)}
                    className='inline-flex items-center gap-1 hover:text-foreground transition-colors'
                  >
                    {c.label}
                    <ArrowUp
                      className={cn(
                        'size-3 transition-transform',
                        sortKey === c.key ? 'text-foreground' : 'opacity-50',
                        sortKey === c.key && sortDir === 'desc' && 'rotate-180',
                      )}
                    />
                  </button>
                </th>
              ))}
              {onCreateFromHit && (
                <th
                  className='text-right px-3 py-2.5'
                  aria-label='PGA flag action'
                />
              )}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr
                key={i}
                className='transition-colors hover:bg-[var(--row-hover)] border-t border-[var(--edge-separator)] cursor-pointer'
                onClick={() => onOpenRecord(r.raw)}
              >
                <td className='px-3 py-3 text-xs font-medium truncate max-w-[220px]'>
                  {r.productName}
                </td>
                <td className='px-3 py-3 text-xs font-mono'>{r.hsCode}</td>
                <td className='px-3 py-3 text-xs'>{r.listName}</td>
                <td className='px-3 py-3 max-w-[200px]'>
                  <span
                    className='inline-flex max-w-[180px] items-center overflow-hidden whitespace-nowrap text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5 badge-info'
                    title={r.agency}
                  >
                    <span className='min-w-0 truncate'>{r.agency}</span>
                  </span>
                </td>
                <td className='px-3 py-3 text-xs font-mono'>{r.pgaFlagCode}</td>
                {onCreateFromHit && (
                  <td className='px-3 py-3 text-right'>
                    {!r.hit ? (
                      <span
                        className='text-[10px] text-muted-foreground whitespace-nowrap'
                        title='This screening record does not identify a supported PGA flag type.'
                      >
                        No flag code
                      </span>
                    ) : (
                      <button
                        type='button'
                        className='ih-btn ih-btn-outline ih-btn-sm'
                        disabled={appliedCodes.includes(r.hit.code)}
                        title={
                          appliedCodes.includes(r.hit.code)
                            ? 'A flag of this type is already applied.'
                            : 'Attach this hit and create its PGA flag.'
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          onCreateFromHit(r.hit as PgaHitOption);
                        }}
                      >
                        <Plus className='size-3.5' />
                        {appliedCodes.includes(r.hit.code)
                          ? 'Applied'
                          : 'Use hit'}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='flex items-center justify-between gap-2 flex-wrap'>
        <div className='text-xs text-muted-foreground'>
          Showing {startIdx + 1} to {Math.min(startIdx + pageSize, total)} of{' '}
          {total} results
        </div>
        <Pagination page={pageSafe} pageCount={pageCount} onChange={setPage} />
      </div>
    </div>
  );
}

type OtherSortKey = 'productName' | 'hsCode' | 'listName' | 'agency' | 'lens';
interface OtherHitRow {
  productName: string;
  hsCode: string;
  listName: string;
  agency: string;
  lens: string;
  raw: Record<string, string | number>;
}

/**
 * 'Other' tab (Slice 9) — every live commodity hit outside PGA/Forced-Labor/
 * AD-CVD (detail.other), grouped one BiteLensGroup per lens name. Read-only:
 * modeled on ScreeningTable's search/sort/paginate mechanics but with no
 * Apply column (no PGA/flag concept applies to these lenses) and an added
 * Lens column, since rows span many different lenses at once here.
 */
function OtherTable({
  detail,
  product,
  onOpenRecord,
  groups,
  emptyText = 'No other regulatory-lens matches for this HTS code.',
  searchPlaceholder = 'Search product, HS, list, agency, lens…',
}: {
  detail: ProductDetail;
  product: ImportProduct;
  onOpenRecord: (r: Record<string, string | number>) => void;
  groups?: BiteLensGroup[];
  emptyText?: string;
  searchPlaceholder?: string;
}) {
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState<OtherSortKey>('lens');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const rows = useMemo<OtherHitRow[]>(() => {
    return flattenMatches(groups ?? detail.other).map((m) => ({
      productName: product.name,
      hsCode: String(m.raw['HS Code'] ?? product.htsCode ?? '—'),
      listName: m.list,
      agency: String(m.raw['Agency'] ?? '—'),
      lens: m.lensName,
      raw: m.raw,
    }));
  }, [detail.other, groups, product]);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase();
    const base = !needle
      ? rows
      : rows.filter((m) =>
          [m.productName, m.hsCode, m.listName, m.agency, m.lens].some((v) =>
            String(v).toLowerCase().includes(needle),
          ),
        );
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...base].sort(
      (a, b) => String(a[sortKey]).localeCompare(String(b[sortKey])) * dir,
    );
  }, [rows, q, sortKey, sortDir]);

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(page, pageCount);
  const startIdx = (pageSafe - 1) * pageSize;
  const pageRows = filtered.slice(startIdx, startIdx + pageSize);

  const toggleSort = (k: OtherSortKey) => {
    if (sortKey === k) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(k);
      setSortDir('asc');
    }
  };

  const cols: { key: OtherSortKey; label: string }[] = [
    { key: 'productName', label: 'Product' },
    { key: 'hsCode', label: 'HS Code' },
    { key: 'listName', label: 'List Name' },
    { key: 'agency', label: 'Agency' },
    { key: 'lens', label: 'Lens' },
  ];

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between gap-2 flex-wrap'>
        <div className='relative flex-1 max-w-[320px]'>
          <Search className='size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground' />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            placeholder={searchPlaceholder}
            className='h-9 pl-8 pr-2 rounded-md border border-input bg-background text-xs w-full'
          />
        </div>
      </div>

      {total === 0 ? (
        <EmptyState text={q ? 'No results match your search.' : emptyText} />
      ) : (
        <>
          <div className='rounded-md overflow-hidden bg-[var(--bg-surface)]'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='text-[10px] uppercase tracking-wider text-muted-foreground font-medium'>
                  {cols.map((c) => (
                    <th key={c.key} className='text-left px-3 py-2.5'>
                      <button
                        onClick={() => toggleSort(c.key)}
                        className='inline-flex items-center gap-1 hover:text-foreground transition-colors'
                      >
                        {c.label}
                        <ArrowUp
                          className={cn(
                            'size-3 transition-transform',
                            sortKey === c.key
                              ? 'text-foreground'
                              : 'opacity-50',
                            sortKey === c.key &&
                              sortDir === 'desc' &&
                              'rotate-180',
                          )}
                        />
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r, i) => (
                  <tr
                    key={i}
                    className='transition-colors hover:bg-[var(--row-hover)] border-t border-[var(--edge-separator)] cursor-pointer'
                    onClick={() => onOpenRecord(r.raw)}
                  >
                    <td className='px-3 py-3 text-xs font-medium truncate max-w-[220px]'>
                      {r.productName}
                    </td>
                    <td className='px-3 py-3 text-xs font-mono'>{r.hsCode}</td>
                    <td className='px-3 py-3 text-xs'>{r.listName}</td>
                    <td className='px-3 py-3'>
                      <span className='inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5 badge-info'>
                        {r.agency}
                      </span>
                    </td>
                    <td className='px-3 py-3 text-xs text-muted-foreground'>
                      {r.lens}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className='flex items-center justify-between gap-2 flex-wrap'>
            <div className='text-xs text-muted-foreground'>
              Showing {startIdx + 1} to {Math.min(startIdx + pageSize, total)}{' '}
              of {total} results
            </div>
            <Pagination
              page={pageSafe}
              pageCount={pageCount}
              onChange={setPage}
            />
          </div>
        </>
      )}
    </div>
  );
}

function TariffDutyContent({
  detail,
  onOpenDrawer,
}: {
  detail: ProductDetail;
  onOpenDrawer: () => void;
}) {
  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
        <div className='lg:col-span-2 overflow-x-auto'>
          <table className='w-full text-xs'>
            <thead className='text-muted-foreground'>
              <tr className='border-b border-[var(--edge-divider)]'>
                <th className='text-left font-medium py-2 pr-3 text-[11px] uppercase tracking-wider'>
                  HTS Sub-code
                </th>
                <th className='text-left font-medium py-2 pr-3 text-[11px] uppercase tracking-wider'>
                  Line item
                </th>
                <th className='text-right font-medium py-2 pr-3 text-[11px] uppercase tracking-wider'>
                  Rate
                </th>
                <th className='text-right font-medium py-2 pl-3 text-[11px] uppercase tracking-wider'>
                  Cost
                </th>
              </tr>
            </thead>
            <tbody>
              {detail.dutyLines.map((l, i) => (
                <tr key={i} className='border-b border-[var(--edge-separator)]'>
                  <td className='py-2 pr-3 font-mono'>{l.subCode}</td>
                  <td className='py-2 pr-3'>
                    <div>{l.label}</div>
                    {l.note && (
                      <div className='text-[11px] text-muted-foreground'>
                        {l.note}
                      </div>
                    )}
                  </td>
                  <td
                    className={cn(
                      'py-2 pr-3 text-right tabular-nums',
                      l.ratePct > 0 && 'text-[var(--warning)] font-medium',
                    )}
                  >
                    {l.ratePct}%
                  </td>
                  <td className='py-2 pl-3 text-right tabular-nums'>
                    {formatCurrency(l.cost)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className='rounded-md bg-[var(--bg-surface)] p-3'>
          <div className='text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2'>
            Cost Summary
          </div>
          <dl className='space-y-1.5 text-xs tabular-nums'>
            <CostRow
              label='Base duty rate'
              value={`${detail.cost.baseRatePct}%`}
            />
            <CostRow
              label='Base duty cost'
              value={formatCurrency(detail.cost.baseCost)}
            />
            <CostRow
              label='Additional duty rate'
              value={`${detail.cost.additionalRatePct}%`}
            />
            <CostRow
              label='Additional duty cost'
              value={formatCurrency(detail.cost.additionalCost)}
            />
            <CostRow
              label='Total duty cost'
              value={formatCurrency(detail.cost.totalDutyCost)}
            />
          </dl>
          <div className='mt-3 pt-3 border-t border-[var(--edge-divider)]'>
            <div className='text-[10px] uppercase tracking-wider text-muted-foreground font-medium'>
              Total Cost
            </div>
            <div className='text-2xl font-semibold tabular-nums mt-0.5'>
              {formatCurrency(detail.cost.totalCost)}
            </div>
            <div className='text-[11px] text-muted-foreground'>
              Declared value + duty
            </div>
          </div>
        </div>
      </div>

      {detail.riskFlags.length > 0 && (
        <div>
          <div className='text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2'>
            Risk Flags
          </div>
          <ul className='space-y-2'>
            {detail.riskFlags.map((r, i) => (
              <li
                key={i}
                className={cn(
                  'rounded-md p-3 flex items-start gap-3',
                  SEV_BG[r.severity],
                )}
              >
                <AlertTriangle
                  className={cn('size-4 mt-0.5 shrink-0', SEV_TEXT[r.severity])}
                />
                <div className='min-w-0'>
                  <div
                    className={cn('text-sm font-medium', SEV_TEXT[r.severity])}
                  >
                    {r.label}
                  </div>
                  <div className='text-xs text-muted-foreground mt-0.5'>
                    {r.note}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className='flex items-center justify-end gap-2 pt-2 border-t border-[var(--edge-divider)]'>
        <HeaderBtn icon={Sparkles}>Explain Results</HeaderBtn>
        <HeaderBtn icon={PencilLine}>Edit Tariff Inputs</HeaderBtn>
        <button
          type='button'
          onClick={onOpenDrawer}
          className='inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90'
        >
          <ExternalLink className='size-3.5' /> View full breakdown
        </button>
      </div>
    </div>
  );
}

function CrossRulingsContent({ detail }: { detail: ProductDetail }) {
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const rows = useMemo(() => {
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...detail.crossRulings].sort(
      (a, b) => (a.applicability - b.applicability) * dir,
    );
  }, [detail.crossRulings, sortDir]);

  if (rows.length === 0) {
    return <EmptyState text='No CROSS rulings indexed for this HTS.' />;
  }

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-end'>
        <button
          onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
          className='inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground'
        >
          Sort by applicability{' '}
          <ArrowUp
            className={cn(
              'size-3 transition-transform',
              sortDir === 'desc' && 'rotate-180',
            )}
          />
        </button>
      </div>
      <ul className='divide-y divide-[var(--edge-separator)]'>
        {rows.map((r) => {
          const band: Severity =
            r.applicability >= 80
              ? 'success'
              : r.applicability >= 60
              ? 'info'
              : r.applicability >= 40
              ? 'warning'
              : 'danger';
          return (
            <li key={r.id} className='py-3'>
              <a
                href={r.url}
                target='_blank'
                rel='noreferrer'
                className='flex items-start justify-between gap-3 group'
              >
                <div className='min-w-0 flex-1'>
                  <div className='text-sm font-medium group-hover:underline'>
                    <span className='font-mono'>{r.id}</span>{' '}
                    <span className='text-muted-foreground font-normal'>
                      — {r.title}
                    </span>
                  </div>
                  <div className='text-[11px] text-muted-foreground mt-0.5'>
                    {r.summary}
                  </div>
                  <div className='text-[10px] text-muted-foreground mt-1 tabular-nums'>
                    {r.date}
                  </div>
                </div>
                <div className='shrink-0 flex flex-col items-end gap-1 w-[110px]'>
                  <div className='flex items-center gap-1.5'>
                    <StatusPill tone={band} label={`${r.applicability}%`} />
                    <ExternalLink className='size-3 text-muted-foreground' />
                  </div>
                  <div className='h-1.5 w-full rounded-full bg-[var(--bg-surface)] overflow-hidden'>
                    <div
                      className={cn('h-full rounded-full', BAND_BG[band])}
                      style={{ width: `${r.applicability}%` }}
                    />
                  </div>
                  <div className='text-[10px] uppercase tracking-wider text-muted-foreground'>
                    Applicability
                  </div>
                </div>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function EntityRiskContent({
  product: _product,
  detail,
  onOpenRecord,
  loading,
  hasScreening = true,
  matches,
  error,
}: {
  product: ImportProduct;
  detail: ProductDetail;
  onOpenRecord: (r: Record<string, string | number>) => void;
  loading?: boolean;
  hasScreening?: boolean;
  matches?: ProductDetail['deniedPartyMatches'];
  error?: string | null;
}) {
  const entityMatches = matches ?? detail.deniedPartyMatches;

  return (
    <div className='space-y-3'>
      {loading ? (
        <div className='flex items-center justify-center gap-2 py-8 text-xs text-muted-foreground'>
          <RotateCw className='size-4 animate-spin' />
          Loading entity-risk results…
        </div>
      ) : error ? (
        <div className='rounded-md bg-[var(--bg-surface)] p-3 text-xs'>
          <div className='flex items-center gap-2 text-[var(--danger)]'>
            <AlertCircle className='size-4' />
            <span className='font-medium'>Entity screening unavailable</span>
          </div>
          <div className='text-muted-foreground mt-1'>{error}</div>
        </div>
      ) : !hasScreening ? (
        <div className='rounded-md bg-[var(--bg-surface)] p-3 text-xs'>
          <div className='flex items-center gap-2 text-muted-foreground'>
            <Clock className='size-4' />
            <span className='font-medium'>Not yet screened</span>
          </div>
          <div className='text-muted-foreground mt-1'>
            Get the latest results to check the manufacturer or vendor for
            denied-party and sanctions matches.
          </div>
        </div>
      ) : entityMatches.length === 0 ? (
        <div className='rounded-md bg-[var(--bg-surface)] p-3 text-xs'>
          <div className='flex items-center gap-2 text-[var(--success)]'>
            <CheckCircle2 className='size-4' />
            <span className='font-medium'>No denied-party matches found</span>
          </div>
          <div className='text-muted-foreground mt-1'>
            The latest entity screening returned no denied-party or sanctions
            matches.
          </div>
        </div>
      ) : (
        <div className='rounded-md overflow-x-auto bg-[var(--bg-surface)]'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='text-muted-foreground'>
                <th className='text-left px-3 py-2.5 text-[12px] normal-case tracking-normal font-normal'>
                  Entity Name
                </th>
                <th className='text-left px-3 py-2.5 text-[12px] normal-case tracking-normal font-normal'>
                  Agency
                </th>
                <th className='text-left px-3 py-2.5 text-[12px] normal-case tracking-normal font-normal'>
                  Country
                </th>
                <th className='text-left px-3 py-2.5 text-[12px] normal-case tracking-normal font-normal'>
                  List
                </th>
                <th className='text-left px-3 py-2.5 text-[12px] normal-case tracking-normal font-normal'>
                  Match Score
                </th>
                <th className='text-left px-3 py-2.5 text-[12px] normal-case tracking-normal font-normal'>
                  Detection Date
                </th>
                <th className='text-left px-3 py-2.5 text-[12px] normal-case tracking-normal font-normal'>
                  Source
                </th>
              </tr>
            </thead>
            <tbody>
              {entityMatches.map((match, index) => (
                <tr
                  key={`${match.entity}-${match.list}-${index}`}
                  onClick={() =>
                    onOpenRecord({
                      Entity: match.entity,
                      List: match.list,
                      Agency: match.agency ?? '',
                      Country: match.country ?? '',
                      'Detection Date': match.date,
                      Source: match.source ?? '',
                      'Source URL': match.sourceUrl ?? '',
                      'Match Score': match.score ?? '',
                    })
                  }
                  className='cursor-pointer transition-colors hover:bg-[var(--row-hover)] border-t border-[var(--edge-separator)]'
                >
                  <td className='px-3 py-3 text-primary hover:underline text-xs font-medium'>
                    {match.entity}
                  </td>
                  <td className='px-3 py-3 text-xs'>{match.agency || '—'}</td>
                  <td className='px-3 py-3 text-xs'>{match.country || '—'}</td>
                  <td className='px-3 py-3 text-xs'>{match.list || '—'}</td>
                  <td className='px-3 py-3 text-xs tabular-nums'>
                    {match.score || '—'}
                  </td>
                  <td className='px-3 py-3 text-xs tabular-nums'>
                    {match.date || '—'}
                  </td>
                  <td className='px-3 py-3 text-xs'>
                    {match.sourceUrl ? (
                      <a
                        href={match.sourceUrl}
                        target='_blank'
                        rel='noreferrer'
                        onClick={(event) => event.stopPropagation()}
                        className='inline-flex items-center gap-1 text-primary hover:underline'
                      >
                        {match.source || 'View source'}
                        <ExternalLink className='size-3' />
                      </a>
                    ) : (
                      match.source || '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Quick Nav (sticky in-page anchor menu) ─── */

const QUICK_NAV_ITEMS = [
  { id: 'classification', label: 'Classification' },
  { id: 'tariffs', label: 'Duties & Tariffs' },
  { id: 'flags', label: 'Program Codes' },
  { id: 'rulings', label: 'Cross Rulings' },
] as const;

function QuickNav() {
  const [active, setActive] = useState<string>('classification');
  useEffect(() => {
    const els = QUICK_NAV_ITEMS.map((i) =>
      document.getElementById(i.id),
    ).filter(Boolean) as HTMLElement[];
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) =>
              (a.target as HTMLElement).offsetTop -
              (b.target as HTMLElement).offsetTop,
          );
        if (visible[0]) setActive((visible[0].target as HTMLElement).id);
      },
      { rootMargin: '-96px 0px -60% 0px', threshold: [0, 0.25, 0.5, 1] },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
  return (
    <div className='sticky top-0 z-20 -mx-6 px-6 py-2 bg-[var(--bg-colors)]/80 backdrop-blur border-b border-[var(--edge-divider)]'>
      <nav
        className='flex items-center justify-center gap-2 overflow-x-auto'
        aria-label='Jump to section'
      >
        <span className='text-[10px] uppercase tracking-wider text-muted-foreground mr-1 hidden sm:inline'>
          Jump to
        </span>
        {QUICK_NAV_ITEMS.map((it) => (
          <a
            key={it.id}
            href={`#${it.id}`}
            onClick={(e) => {
              e.preventDefault();
              scrollToAnchor(it.id);
            }}
            className={cn(
              'inline-flex items-center h-8 px-3.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer border',
              active === it.id
                ? 'bg-primary/10 text-primary border-primary/40'
                : 'text-muted-foreground border-[var(--edge-divider)] hover:text-foreground hover:bg-[var(--row-hover)] hover:border-primary/30',
            )}
          >
            {it.label}
          </a>
        ))}
      </nav>
    </div>
  );
}

function PageSection({
  id,
  icon: Icon,
  title,
  subtitle,
  children,
  action,
}: {
  id: string;
  icon: any;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section id={id} className='space-y-4 scroll-mt-24 pt-4 first:pt-0'>
      <div className='flex items-end justify-between gap-3 border-b border-[var(--edge-divider)] pb-3'>
        <div className='flex items-center gap-3'>
          <div className='size-10 rounded-lg bg-[var(--bg-surface)] flex items-center justify-center text-primary'>
            <Icon className='size-5' />
          </div>
          <div>
            <h2 className='text-xl font-bold tracking-tight leading-tight'>
              {title}
            </h2>
            {subtitle && (
              <p className='text-xs text-muted-foreground mt-1'>{subtitle}</p>
            )}
          </div>
        </div>
        {action && <div className='shrink-0'>{action}</div>}
      </div>
      <div className='space-y-4'>{children}</div>
    </section>
  );
}

/* ─── Editable Product Details ─── */

// Additional columns shown before "View all" in read mode (five rows of the
// two-column grid). Customer templates can carry dozens of extra columns.
const ADDITIONAL_COLUMNS_PREVIEW_COUNT = 10;

function EditableProductDetails({
  product,
  dbName,
  importSource,
  htsIssue,
  onSelectRecommendation,
  onLoadRecommendations,
  recommendationsLoading,
  recommendationsError,
  recommendationsRequested,
  editing,
  saving,
  error,
  values,
  categories,
  categoriesLoading,
  onChange,
  databaseId,
  databaseStructure,
  onParentChange,
  onAdditionalHtsCodesChange,
  onAdditionalColumnsChange,
  onHtsValidated,
  onEdit,
  onCancel,
  onSave,
}: {
  product: ImportProduct;
  dbName?: string;
  importSource?: ImportProductSource | null;
  htsIssue?: {
    reason: string;
    recommendations: HtsRecommendation[];
    status?: ImportHtsClassification['status'];
    changedDate?: string | null;
    severity?: 'warning' | 'danger';
  } | null;
  onSelectRecommendation?: (rec: HtsRecommendation) => void;
  onLoadRecommendations?: () => void;
  recommendationsLoading?: boolean;
  recommendationsError?: string | null;
  recommendationsRequested?: boolean;
  editing: boolean;
  saving: boolean;
  error: string | null;
  values: ProductEditDraft;
  categories: ImportProductCategory[];
  categoriesLoading: boolean;
  onChange: (key: ProductEditStringKey, value: string) => void;
  databaseId?: string;
  databaseStructure?: string;
  onParentChange: (parent: ImportParentCandidate | null) => void;
  onAdditionalHtsCodesChange: (codes: AdditionalHtsCodeDraft[]) => void;
  onAdditionalColumnsChange: (columns: AdditionalColumnDraft[]) => void;
  onHtsValidated: (code: string) => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const readOnly = useImportHubReadOnly();
  const [showAllAdditionalColumns, setShowAllAdditionalColumns] =
    useState(false);
  const visibleAdditionalColumns = values.additionalColumns.filter(
    (column) => !isHiddenAdditionalColumn(column.key),
  );
  const hasVisibleAdditionalColumns = visibleAdditionalColumns.length > 0;
  const hiddenAdditionalColumnCount = Math.max(
    0,
    visibleAdditionalColumns.length - ADDITIONAL_COLUMNS_PREVIEW_COUNT,
  );
  const sourceBadge = (() => {
    switch (importSource) {
      case 'document_upload':
        return { label: 'Document Upload', className: 'ih-badge-info' };
      case 'xml':
        return { label: 'XML Upload', className: 'ih-badge-warning' };
      case 'csv_xlsx':
        return { label: 'CSV/XLSX', className: 'ih-badge-success' };
      case 'manual':
        return { label: 'Manual Entry', className: 'ih-badge-muted' };
      case 'unknown':
        return { label: 'Existing Record', className: 'ih-badge-muted' };
      default:
        return null;
    }
  })();
  return (
    <div className='elms-card'>
      <div className='flex items-center justify-between mb-3'>
        <div className='flex items-center gap-2'>
          <h3 className='text-sm font-semibold'>Product Details</h3>
          {sourceBadge && (
            <span className={`ih-badge ${sourceBadge.className}`}>
              {sourceBadge.label}
            </span>
          )}
        </div>
        {editing ? (
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={onCancel}
              disabled={saving}
              className='h-7 px-2.5 rounded-md border border-input bg-background hover:bg-accent text-xs disabled:opacity-60'
            >
              Cancel
            </button>
            <button
              type='button'
              onClick={onSave}
              disabled={saving}
              className='inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-medium disabled:opacity-60'
            >
              {saving && (
                <span
                  className='ih-spinner'
                  style={{ width: 12, height: 12, borderWidth: 2 }}
                />
              )}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        ) : (
          <button
            type='button'
            onClick={readOnly ? undefined : onEdit}
            disabled={readOnly}
            title={readOnly ? 'Requires a Business license' : undefined}
            className='inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-input bg-background hover:bg-accent text-xs disabled:opacity-60 disabled:cursor-not-allowed'
          >
            {readOnly ? (
              <Lock className='size-3' />
            ) : (
              <PencilLine className='size-3' />
            )}{' '}
            Edit
          </button>
        )}
      </div>
      <div className='grid grid-cols-2 gap-x-4 gap-y-3'>
        <EditableField
          editing={editing}
          label='Name'
          value={values.name}
          onChange={(v) => onChange('name', v)}
        />
        <EditableField
          editing={editing}
          label='SKU'
          value={values.sku}
          mono
          onChange={(v) => onChange('sku', v)}
        />
        {editing ? (
          <SelectField
            label='Hierarchy type'
            value={values.hierarchyType}
            options={[
              { value: 'Assembly', label: 'Assembly' },
              { value: 'Sub-Assembly', label: 'Sub-Assembly' },
              { value: 'Component', label: 'BOM Item/Component' },
            ]}
            allowEmpty
            onChange={(v) => {
              onChange('hierarchyType', v);
              if (
                values.parentProductId &&
                !(IMPORT_HIERARCHY_PARENT_TYPES[v] ?? []).includes(
                  values.parentHierarchyType,
                )
              ) {
                onParentChange(null);
              }
            }}
          />
        ) : (
          <Field label='Hierarchy type' value={values.hierarchyType} />
        )}
        {databaseStructure === 'hierarchy' &&
          (editing && databaseId ? (
            <div className='space-y-1'>
              <label className='ih-edit-label'>Parent</label>
              <ImportParentProductPicker
                databaseId={databaseId}
                childHierarchyType={values.hierarchyType}
                excludeProductId={product.id}
                value={
                  values.parentProductId
                    ? {
                        id: values.parentProductId,
                        sku: values.parentSku,
                        name: values.parentName,
                        hierarchyType: values.parentHierarchyType,
                      }
                    : null
                }
                onChange={onParentChange}
                className={EDIT_INPUT_CLASS}
                compact
              />
            </div>
          ) : (
            <Field
              label='Parent'
              value={
                values.parentProductId
                  ? `${formatParentCandidate({
                      sku: values.parentSku,
                      name: values.parentName,
                    })}${
                      values.parentHierarchyType
                        ? ` (${values.parentHierarchyType})`
                        : ''
                    }`
                  : values.hierarchyType === 'Assembly'
                  ? 'Top-level assembly'
                  : 'No parent'
              }
            />
          ))}
        {editing ? (
          <div className='space-y-1'>
            <label className='ih-edit-label'>Category</label>
            <ImportCategorySelect
              compact
              value={values.category}
              loading={categoriesLoading}
              options={[
                ...IMPORT_CATEGORY_DEFAULTS,
                ...categories.map(({ name }) => name),
              ]}
              onChange={(value) => onChange('category', value)}
            />
          </div>
        ) : (
          <Field label='Category' value={values.category} />
        )}
        <EditableField
          editing={editing}
          label='Manufacturer'
          value={values.manufacturer}
          onChange={(v) => onChange('manufacturer', v)}
        />
        <EditableField
          editing={editing}
          label='Vendor'
          value={values.vendor}
          onChange={(v) => onChange('vendor', v)}
        />
        <div>
          {editing ? (
            <HtsCodeSearchField
              value={values.htsCode}
              country={values.countryOfOrigin}
              onChange={(value) => onChange('htsCode', value)}
              onValidated={onHtsValidated}
            />
          ) : (
            <Field
              label='HTS code'
              value={formatHtsPretty(values.htsCode)}
              mono
            />
          )}
          {htsIssue && !editing && (
            <div
              className={cn(
                'mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-medium',
                htsIssue.severity === 'warning'
                  ? 'text-[var(--warning)]'
                  : 'text-[var(--danger)]',
              )}
            >
              <AlertTriangle className='size-3' />
              {htsIssue.reason}
            </div>
          )}
          <div className='mt-2'>
            <ClassificationLink productId={product.id} hub='import' compact />
          </div>
        </div>
        {editing ? (
          <CountrySelectField
            label='Country of origin'
            value={values.countryOfOrigin}
            onChange={(v) => onChange('countryOfOrigin', v)}
          />
        ) : (
          <Field label='Country of origin' value={values.countryOfOrigin} />
        )}
        <EditableField
          editing={editing}
          label='Quantity'
          value={values.quantity}
          onChange={(v) => onChange('quantity', v)}
        />
        <EditableField
          editing={editing}
          label='Unit of measure'
          value={values.unitOfMeasure}
          onChange={(v) => onChange('unitOfMeasure', v)}
        />
        <Field label='Database' value={dbName ?? '—'} />
        <div className='col-span-full'>
          <EditableField
            editing={editing}
            label='Description'
            value={values.description}
            multiline
            onChange={(v) => onChange('description', v)}
          />
        </div>
        <div className='col-span-full'>
          <EditableField
            editing={editing}
            label='TRACES'
            value={values.traces}
            multiline
            onChange={(v) => onChange('traces', v)}
          />
        </div>
        <div className='col-span-full'>
          <EditableField
            editing={editing}
            label='Specifications'
            value={values.specifications}
            multiline
            onChange={(v) => onChange('specifications', v)}
          />
        </div>

        <div className='col-span-full border-t border-[var(--edge-divider)] pt-3'>
          <div className='mb-2 flex items-center justify-between gap-3'>
            <div>
              <div className='ih-edit-label'>Additional HTS codes</div>
              <p className='mt-0.5 text-[11px] text-muted-foreground'>
                Optional classifications for other countries or product uses.
              </p>
            </div>
            {editing && (
              <button
                type='button'
                onClick={() =>
                  onAdditionalHtsCodesChange([
                    ...values.additionalHtsCodes,
                    {
                      draftKey: `new-hts-${Date.now()}-${
                        values.additionalHtsCodes.length
                      }`,
                      htsCode: '',
                      countryOfOrigin: values.countryOfOrigin,
                      validatedHtsCode: '',
                    },
                  ])
                }
                className='inline-flex h-7 items-center gap-1.5 rounded-md border border-input bg-background px-2.5 text-xs hover:bg-accent'
              >
                <Plus className='size-3.5' /> Add HTS code
              </button>
            )}
          </div>
          {values.additionalHtsCodes.length === 0 ? (
            <div className='rounded-md border border-dashed border-[var(--edge-divider)] px-3 py-2 text-xs text-muted-foreground'>
              No additional HTS codes.
            </div>
          ) : (
            <div className='space-y-2'>
              {values.additionalHtsCodes.map((code, index) =>
                editing ? (
                  <div
                    key={code.draftKey}
                    className='grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-start gap-2 rounded-md border border-[var(--edge-divider)] p-2'
                  >
                    <HtsCodeSearchField
                      value={code.htsCode}
                      country={code.countryOfOrigin || values.countryOfOrigin}
                      onChange={(htsCode) =>
                        onAdditionalHtsCodesChange(
                          values.additionalHtsCodes.map((item, itemIndex) =>
                            itemIndex === index
                              ? {
                                  ...item,
                                  htsCode,
                                  validatedHtsCode:
                                    item.htsCode === htsCode
                                      ? item.validatedHtsCode
                                      : '',
                                }
                              : item,
                          ),
                        )
                      }
                      onValidated={(validatedHtsCode) =>
                        onAdditionalHtsCodesChange(
                          values.additionalHtsCodes.map((item, itemIndex) =>
                            itemIndex === index
                              ? { ...item, validatedHtsCode }
                              : item,
                          ),
                        )
                      }
                    />
                    <CountrySelectField
                      label='Country of origin'
                      value={code.countryOfOrigin}
                      allowEmpty
                      onChange={(countryOfOrigin) =>
                        onAdditionalHtsCodesChange(
                          values.additionalHtsCodes.map((item, itemIndex) =>
                            itemIndex === index
                              ? { ...item, countryOfOrigin }
                              : item,
                          ),
                        )
                      }
                    />
                    <button
                      type='button'
                      aria-label={`Remove additional HTS code ${index + 1}`}
                      title='Remove HTS code'
                      onClick={() =>
                        onAdditionalHtsCodesChange(
                          values.additionalHtsCodes.filter(
                            (_, itemIndex) => itemIndex !== index,
                          ),
                        )
                      }
                      className='mt-5 inline-flex size-8 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground'
                    >
                      <Trash2 className='size-3.5' />
                    </button>
                  </div>
                ) : (
                  <div
                    key={code.draftKey}
                    className='grid grid-cols-2 gap-4 rounded-md border border-[var(--edge-divider)] p-2'
                  >
                    <Field
                      label='HTS code'
                      value={formatHtsPretty(code.htsCode)}
                      mono
                    />
                    <Field
                      label='Country of origin'
                      value={code.countryOfOrigin}
                    />
                  </div>
                ),
              )}
            </div>
          )}
        </div>

        <div className='col-span-full border-t border-[var(--edge-divider)] pt-3'>
          <div className='mb-2 flex items-center justify-between gap-3'>
            <div>
              <div className='ih-edit-label'>Additional columns</div>
              <p className='mt-0.5 text-[11px] text-muted-foreground'>
                Custom values imported with this product.
              </p>
            </div>
            {editing && (
              <button
                type='button'
                onClick={() =>
                  onAdditionalColumnsChange([
                    ...values.additionalColumns,
                    {
                      id: `new-column-${Date.now()}-${
                        values.additionalColumns.length
                      }`,
                      key: '',
                      value: '',
                    },
                  ])
                }
                className='inline-flex h-7 items-center gap-1.5 rounded-md border border-input bg-background px-2.5 text-xs hover:bg-accent'
              >
                <Plus className='size-3.5' /> Add column
              </button>
            )}
          </div>
          {!hasVisibleAdditionalColumns ? (
            <div className='rounded-md border border-dashed border-[var(--edge-divider)] px-3 py-2 text-xs text-muted-foreground'>
              No additional columns.
            </div>
          ) : editing ? (
            <div className='space-y-2'>
              {values.additionalColumns.map((column, index) =>
                isHiddenAdditionalColumn(column.key) ? null : (
                  <div
                    key={column.id}
                    className='grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-2'
                  >
                    <EditableField
                      editing
                      label='Column name'
                      value={column.key}
                      onChange={(key) =>
                        onAdditionalColumnsChange(
                          values.additionalColumns.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, key } : item,
                          ),
                        )
                      }
                    />
                    <EditableField
                      editing
                      label='Value'
                      value={column.value}
                      onChange={(value) =>
                        onAdditionalColumnsChange(
                          values.additionalColumns.map((item, itemIndex) =>
                            itemIndex === index ? { ...item, value } : item,
                          ),
                        )
                      }
                    />
                    <button
                      type='button'
                      aria-label={`Remove additional column ${index + 1}`}
                      title='Remove column'
                      onClick={() =>
                        onAdditionalColumnsChange(
                          values.additionalColumns.filter(
                            (_, itemIndex) => itemIndex !== index,
                          ),
                        )
                      }
                      className='inline-flex size-8 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:bg-accent hover:text-foreground'
                    >
                      <Trash2 className='size-3.5' />
                    </button>
                  </div>
                ),
              )}
            </div>
          ) : (
            <>
              <div className='grid grid-cols-2 gap-x-4 gap-y-3'>
                {(showAllAdditionalColumns
                  ? visibleAdditionalColumns
                  : visibleAdditionalColumns.slice(
                      0,
                      ADDITIONAL_COLUMNS_PREVIEW_COUNT,
                    )
                ).map((column) => (
                  <Field
                    key={column.id}
                    label={column.key}
                    value={column.value}
                  />
                ))}
              </div>
              {hiddenAdditionalColumnCount > 0 && (
                <button
                  type='button'
                  onClick={() =>
                    setShowAllAdditionalColumns((current) => !current)
                  }
                  aria-expanded={showAllAdditionalColumns}
                  className='mt-3 text-[11px] text-primary hover:underline inline-flex items-center gap-1'
                >
                  {showAllAdditionalColumns
                    ? 'Show fewer'
                    : `View all ${visibleAdditionalColumns.length} columns`}
                  <ChevronDown
                    className={`size-3 transition-transform ${
                      showAllAdditionalColumns ? 'rotate-180' : ''
                    }`}
                  />
                </button>
              )}
            </>
          )}
        </div>
        {error && (
          <div className='ih-edit-error col-span-full'>
            <AlertCircle className='size-3.5 shrink-0' />
            {error}
          </div>
        )}
      </div>
      {!editing && htsIssue && onSelectRecommendation && (
        <HtsRecommendationsPanel
          issue={htsIssue}
          onSelect={onSelectRecommendation}
          onLoad={onLoadRecommendations}
          loading={recommendationsLoading}
          error={recommendationsError}
          requested={recommendationsRequested}
        />
      )}
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
  loading,
  preserveCurrent,
  allowEmpty,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  loading?: boolean;
  preserveCurrent?: boolean;
  allowEmpty?: boolean;
}) {
  const hasCurrent = options.some((option) => option.value === value);
  return (
    <div className='space-y-1'>
      <label className='ih-edit-label'>{label}</label>
      <select
        value={value}
        disabled={loading}
        onChange={(event) => onChange(event.target.value)}
        className={EDIT_INPUT_CLASS}
      >
        <option value='' disabled={!allowEmpty}>
          {loading
            ? 'Loading…'
            : allowEmpty
            ? 'Not specified'
            : `Select ${label.toLowerCase()}`}
        </option>
        {preserveCurrent && value && !hasCurrent && (
          <option value={value}>{value}</option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function CountrySelectField({
  label,
  value,
  onChange,
  allowEmpty,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  allowEmpty?: boolean;
}) {
  return (
    <div className='space-y-1'>
      <label className='ih-edit-label'>{label}</label>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={EDIT_INPUT_CLASS}
      >
        <option value='' disabled={!allowEmpty}>
          {allowEmpty ? 'Not specified' : 'Select country'}
        </option>
        {countries.map(({ name, iso2 }) => (
          <option key={iso2} value={iso2}>
            {name} ({iso2})
          </option>
        ))}
      </select>
    </div>
  );
}

function HtsCodeSearchField({
  value,
  country,
  onChange,
  onValidated,
}: {
  value: string;
  country: string;
  onChange: (value: string) => void;
  onValidated: (code: string) => void;
}) {
  const [options, setOptions] = useState<ImportHtsOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const requestRef = useRef(0);
  const optionsId = React.useId();
  const digits = value.replace(/\D/g, '').slice(0, 10);

  useEffect(() => {
    const requestId = ++requestRef.current;
    if (digits.length < 6) {
      setOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const timeout = window.setTimeout(() => {
      fetchImportHtsOptions(digits.slice(0, 6), country).then(
        ({ success, options: nextOptions, error }) => {
          if (requestId !== requestRef.current) return;
          setLoading(false);
          if (!success) {
            setOptions([]);
            toast.error(error ?? 'Failed to load HTS codes');
            return;
          }
          setOptions(nextOptions);
          if (nextOptions.some((option) => option.code === digits)) {
            onValidated(digits);
          }
        },
      );
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [digits.slice(0, 6), country]);

  return (
    <div className='space-y-1'>
      <label className='ih-edit-label'>HTS code</label>
      <div className='ih-hts-picker'>
        <input
          value={formatHtsPretty(digits)}
          inputMode='numeric'
          maxLength={13}
          role='combobox'
          aria-expanded={open && options.length > 0}
          aria-controls={optionsId}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onChange={(event) => {
            onValidated('');
            onChange(event.target.value.replace(/\D/g, '').slice(0, 10));
            setOpen(true);
          }}
          placeholder='Enter at least 6 digits'
          className={`${EDIT_INPUT_CLASS} font-mono`}
        />
        {loading && <span className='ih-hts-loading'>Searching…</span>}
        {open && !loading && digits.length >= 6 && options.length > 0 && (
          <div id={optionsId} className='ih-hts-options' role='listbox'>
            {options.map((option) => (
              <button
                type='button'
                role='option'
                aria-selected={option.code === digits}
                key={option.code}
                className='ih-hts-option'
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(option.code);
                  onValidated(option.code);
                  setOpen(false);
                }}
              >
                <span className='font-mono font-semibold'>
                  {option.label.split(' - ')[0]}
                </span>
                <span>{option.description || 'No description available'}</span>
              </button>
            ))}
          </div>
        )}
        {open && !loading && digits.length >= 6 && options.length === 0 && (
          <div className='ih-hts-empty'>No current USHTS codes found.</div>
        )}
      </div>
      <p className='ih-edit-help'>
        Type the first six digits, then select a current ten-digit code.
      </p>
    </div>
  );
}

function HtsRecommendationsPanel({
  issue,
  onSelect,
  onLoad,
  loading,
  error,
  requested,
}: {
  issue: {
    reason: string;
    recommendations: HtsRecommendation[];
    status?: ImportHtsClassification['status'];
    changedDate?: string | null;
  };
  onSelect: (rec: HtsRecommendation) => void;
  onLoad?: () => void;
  loading?: boolean;
  error?: string | null;
  requested?: boolean;
}) {
  const readOnly = useImportHubReadOnly();
  const [selected, setSelected] = useState<string | null>(
    issue.recommendations.find((r) => r.recommended)?.code ??
      issue.recommendations[0]?.code ??
      null,
  );
  useEffect(() => {
    if (
      selected &&
      issue.recommendations.some(
        (recommendation) => recommendation.code === selected,
      )
    ) {
      return;
    }
    setSelected(
      issue.recommendations.find((r) => r.recommended)?.code ??
        issue.recommendations[0]?.code ??
        null,
    );
  }, [issue.recommendations, selected]);
  const chosen = issue.recommendations.find((r) => r.code === selected) ?? null;
  const statusLabel =
    issue.status === 'expired'
      ? 'Expired HTS code'
      : issue.status === 'expiring_soon'
      ? 'HTS code expiring soon'
      : issue.status === 'not_yet_effective'
      ? 'HTS code not yet effective'
      : issue.status === 'ambiguous'
      ? 'HTS classification needs review'
      : 'HTS code needs review';
  return (
    <div className='mt-4 rounded-md border border-[var(--edge-divider)] bg-[var(--bg-surface)] p-3 space-y-3'>
      <div className='flex items-center gap-2'>
        <Sparkles className='size-3.5 text-[var(--action-color)]' />
        <span className='text-[11px] uppercase tracking-wider font-semibold text-muted-foreground'>
          Recommended HTS codes
        </span>
        <span className='ml-auto text-[10px] text-muted-foreground'>
          Review a suggestion, then save it manually
        </span>
      </div>
      <div className='flex items-start gap-2 rounded-md border border-[var(--edge-divider)] bg-[var(--bg-secondary)] p-2.5'>
        <AlertTriangle className='mt-0.5 size-3.5 shrink-0 text-[var(--warning)]' />
        <div className='min-w-0'>
          <div className='text-xs font-semibold'>{statusLabel}</div>
          <div className='mt-0.5 text-[11px] leading-relaxed text-muted-foreground'>
            {issue.reason}
          </div>
          {issue.changedDate && (
            <div className='mt-1 text-[10px] text-muted-foreground'>
              Schedule date: {formatDate(issue.changedDate)}
            </div>
          )}
        </div>
      </div>
      {loading ? (
        <div className='ih-hts-recommendation-status'>
          <RotateCw className='size-3.5 animate-spin' />
          Verifying and ranking current USHTS codes…
        </div>
      ) : error ? (
        <div className='ih-hts-recommendation-status is-error'>
          <AlertCircle className='size-3.5 shrink-0' />
          <span>{error}</span>
          {onLoad && (
            <button
              type='button'
              onClick={readOnly ? undefined : onLoad}
              disabled={readOnly}
              title={readOnly ? 'Requires a Business license' : undefined}
            >
              {readOnly && <Lock className='size-3.5' />} Try again
            </button>
          )}
        </div>
      ) : issue.recommendations.length === 0 ? (
        <div className='ih-hts-recommendation-empty'>
          <p>
            {requested
              ? 'No responsible replacement candidates were found in the current USHTS family. Manual classification review is required.'
              : 'Generate recommendations from current, effective USHTS records in the same code family.'}
          </p>
          {onLoad && (
            <button
              type='button'
              onClick={readOnly ? undefined : onLoad}
              disabled={readOnly}
              title={readOnly ? 'Requires a Business license' : undefined}
            >
              {readOnly ? (
                <Lock className='size-3.5' />
              ) : (
                <Sparkles className='size-3.5' />
              )}
              {requested ? 'Check again' : 'Get HTS recommendations'}
            </button>
          )}
        </div>
      ) : (
        <div className='space-y-2'>
          {issue.recommendations.map((r) => {
            const isSel = r.code === selected;
            const tone: Severity =
              r.confidence >= 85
                ? 'success'
                : r.confidence >= 60
                ? 'warning'
                : 'info';
            return (
              <label
                key={r.code}
                className={cn(
                  'flex items-start gap-2.5 rounded-md border p-2.5 cursor-pointer transition-colors',
                  isSel
                    ? 'border-primary bg-[color-mix(in_oklch,var(--action-color)_6%,transparent)]'
                    : 'border-[var(--edge-divider)] hover:bg-accent/40',
                )}
              >
                <input
                  type='radio'
                  name='hts-rec'
                  value={r.code}
                  checked={isSel}
                  onChange={() => setSelected(r.code)}
                  className='mt-1 accent-primary'
                />
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <span className='font-mono text-xs font-semibold'>
                      {formatHtsPretty(r.code)}
                    </span>
                    <span
                      className={cn(
                        'inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5',
                        SEV_BADGE[tone],
                      )}
                    >
                      {r.confidence}% match
                    </span>
                    {r.recommended && (
                      <span className='inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5 badge-info'>
                        <Award className='size-2.5' /> Recommended
                      </span>
                    )}
                  </div>
                  <div className='text-xs mt-1 leading-snug'>
                    {r.description}
                  </div>
                  {r.rationale && (
                    <div className='text-[11px] text-muted-foreground mt-1'>
                      {r.rationale}
                    </div>
                  )}
                </div>
              </label>
            );
          })}
          <p className='m-0 pt-1 text-[10px] leading-relaxed text-muted-foreground'>
            Match percentages are advisory relevance scores, not legal
            classification determinations.
          </p>
        </div>
      )}
      {issue.recommendations.length > 0 && !loading && !error && (
        <div className='flex items-center justify-end gap-2 pt-1'>
          <button
            type='button'
            disabled={!chosen || readOnly}
            title={readOnly ? 'Requires a Business license' : undefined}
            onClick={() => chosen && !readOnly && onSelect(chosen)}
            className='inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium'
          >
            {readOnly ? (
              <Lock className='size-3.5' />
            ) : (
              <PencilLine className='size-3.5' />
            )}{' '}
            Use selected code
          </button>
        </div>
      )}
    </div>
  );
}

function EditableField({
  editing,
  label,
  value,
  onChange,
  mono,
  multiline,
  displayValue,
  inputMode,
}: {
  editing: boolean;
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
  multiline?: boolean;
  displayValue?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}) {
  if (!editing)
    return <Field label={label} value={displayValue ?? value} mono={mono} />;
  return (
    <div className='space-y-1'>
      <label
        style={{ fontFamily: 'Inter, sans-serif', fontSize: 11 }}
        className='block text-muted-foreground uppercase tracking-wider font-medium'
      >
        {label}
      </label>
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className={cn(
            'w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs',
            mono && 'font-mono',
          )}
        />
      ) : (
        <input
          value={value}
          inputMode={inputMode}
          onChange={(e) => onChange(e.target.value)}
          className={cn(EDIT_INPUT_CLASS, mono && 'font-mono')}
        />
      )}
    </div>
  );
}

/* ─── Flags tabs (PGA / Forced Labor / AD/CVD / EU Imports / Other) ─── */

function FlagsTabsSection({
  detail,
  product,
  onOpenRecord,
  onCreateFromHit,
  appliedCodes,
  loading,
}: {
  detail: ProductDetail;
  product: ImportProduct;
  onOpenRecord: (r: Record<string, string | number>) => void;
  onCreateFromHit?: (hit: PgaHitOption) => void;
  appliedCodes?: PgaCode[];
  loading?: boolean;
}) {
  const [tab, setTab] = useState<
    'pga' | 'fl' | 'adcvd' | 'eu-imports' | 'other'
  >('pga');
  const pgaCount = flattenMatches(detail.biteLensGroups).filter(
    (m) => m.lensCode === 'PGA',
  ).length;
  const flCount = flattenMatches(detail.forcedLabor.groups ?? []).length;
  const adcvdCount = flattenMatches(detail.adcvd.groups ?? []).length;
  const euImportsCount = flattenMatches(detail.euImports).length;
  const otherCount = flattenMatches(detail.other).length;
  const tabs: { id: typeof tab; label: string; count: number }[] = [
    { id: 'pga', label: 'PGA', count: pgaCount },
    { id: 'fl', label: 'Forced Labor', count: flCount },
    { id: 'adcvd', label: 'AD/CVD', count: adcvdCount },
    { id: 'eu-imports', label: 'EU Imports', count: euImportsCount },
    { id: 'other', label: 'Other', count: otherCount },
  ];
  return (
    <div className='space-y-3'>
      <div className='flex items-center gap-1 border-b border-[var(--edge-divider)]'>
        {tabs.map((t) => (
          <button
            key={t.id}
            type='button'
            onClick={() => setTab(t.id)}
            className={cn(
              'px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors',
              tab === t.id
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
            <span className='ml-1.5 text-[10px] tabular-nums text-muted-foreground'>
              {t.count}
            </span>
          </button>
        ))}
        <span className='ml-auto flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground pr-1'>
          {loading && (
            <span
              className='ih-spinner'
              style={{ width: 11, height: 11, borderWidth: 2 }}
            />
          )}
          {tab === 'pga' ? 'PGA flag codes' : 'Review only'}
        </span>
      </div>

      {tab === 'pga' && (
        <ScreeningTable
          detail={detail}
          product={product}
          onOpenRecord={onOpenRecord}
          onCreateFromHit={onCreateFromHit}
          appliedCodes={appliedCodes}
        />
      )}
      {tab === 'fl' && (
        <ForcedLaborTable
          detail={detail}
          product={product}
          onOpenRecord={onOpenRecord}
        />
      )}
      {tab === 'adcvd' && (
        <AdCvdTable
          detail={detail}
          product={product}
          onOpenRecord={onOpenRecord}
        />
      )}
      {tab === 'eu-imports' && (
        <EuImportsTable
          detail={detail}
          product={product}
          onOpenRecord={onOpenRecord}
        />
      )}
      {tab === 'other' && (
        <OtherTable
          detail={detail}
          product={product}
          onOpenRecord={onOpenRecord}
        />
      )}
    </div>
  );
}

function ForcedLaborTable({
  detail,
  product,
  onOpenRecord,
}: {
  detail: ProductDetail;
  product: ImportProduct;
  onOpenRecord: (r: Record<string, string | number>) => void;
}) {
  return (
    <OtherTable
      detail={detail}
      product={product}
      onOpenRecord={onOpenRecord}
      groups={detail.forcedLabor.groups ?? []}
      emptyText='No forced-labor hits — this product is clear against UFLPA/WRO lists.'
      searchPlaceholder='Search product, HS, list, agency, lens…'
    />
  );
}

function AdCvdTable({
  detail,
  product,
  onOpenRecord,
}: {
  detail: ProductDetail;
  product: ImportProduct;
  onOpenRecord: (r: Record<string, string | number>) => void;
}) {
  return (
    <OtherTable
      detail={detail}
      product={product}
      onOpenRecord={onOpenRecord}
      groups={detail.adcvd.groups ?? []}
      emptyText="No AD/CVD cases in scope for this product's HTS and country of origin."
      searchPlaceholder='Search product, HS, list, agency, lens…'
    />
  );
}

function EuImportsTable({
  detail,
  product,
  onOpenRecord,
}: {
  detail: ProductDetail;
  product: ImportProduct;
  onOpenRecord: (r: Record<string, string | number>) => void;
}) {
  return (
    <OtherTable
      detail={detail}
      product={product}
      onOpenRecord={onOpenRecord}
      groups={detail.euImports}
      emptyText="No EU import-list matches for this product's HTS code."
      searchPlaceholder='Search product, HS, list, agency, lens…'
    />
  );
}

/* ─── Risk evaluation (user-triggered) ─── */

/* ─── Parent/Child + Linked Entities panels ─── */

type EntityLinkRole =
  | 'Supplier'
  | 'Manufacturer'
  | 'Consignee'
  | 'Broker'
  | 'Freight forwarder';
const ENTITY_ROLES: EntityLinkRole[] = [
  'Supplier',
  'Manufacturer',
  'Consignee',
  'Broker',
  'Freight forwarder',
];

function ROLE_ICON(role: EntityLinkRole) {
  return role === 'Supplier'
    ? Building2
    : role === 'Manufacturer'
    ? Factory
    : role === 'Consignee'
    ? Package
    : role === 'Broker'
    ? FileText
    : Truck;
}

const formatTariffHistoryCurrency = (value: number | null): string =>
  value === null
    ? '—'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);

const formatTariffHistoryRate = (value: string | number | null): string => {
  if (value === null || value === '') return '—';
  if (typeof value === 'number') return `${Number(value.toFixed(4))}%`;
  return /%|free/i.test(value) ? value : `${value}%`;
};

const formatCalendarDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC', // Lock to UTC so the calendar day never shifts
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

function DutyTariffChangelog({
  product,
  databaseProductId,
  refreshVersion,
}: {
  product: ImportProduct;
  databaseProductId?: string;
  refreshVersion: number;
}) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [entries, setEntries] = useState<ImportTariffHistoryEntry[]>([]);
  const [loading, setLoading] = useState(Boolean(databaseProductId));
  const [error, setError] = useState<string | null>(null);
  const [retryVersion, setRetryVersion] = useState(0);

  useEffect(() => {
    setExpandedIds(new Set());
    setEntries([]);
    setError(null);
    if (!databaseProductId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchImportProductTariffHistory(databaseProductId).then((result) => {
      if (cancelled) return;
      setLoading(false);
      if (result.success) {
        setEntries(
          [...result.history].sort(
            (left, right) =>
              new Date(right.changedAt).getTime() -
              new Date(left.changedAt).getTime(),
          ),
        );
      } else {
        setError(result.error ?? 'Failed to load tariff history');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [databaseProductId, product.id, refreshVersion, retryVersion]);

  const toggleBreakdown = (id: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <aside className='elms-card ih-tariff-changelog'>
      <div className='ih-tariff-changelog-header'>
        <h3 className='ih-tariff-changelog-title'>
          <History className='size-4' /> Duty &amp; Tariff Changelog
        </h3>
        <span className='ih-tariff-changelog-hts'>
          HTS {formatHtsPretty(product.htsCode) || '—'}
        </span>
      </div>
      {/* Tracking starts when the product is added to this database: that first
          tariff calculation is the baseline (product-screening.repository.ts
          persistProductTariffResult), so only later changes are listed, and the
          service keeps 20 snapshots per database product -- the baseline
          included -- so at most 19 changes ever show. */}
      <p className='ih-tariff-changelog-description'>
        Duty, tariff, and program changes for this product, tracked from when
        it was added to this database — earlier history isn&apos;t included.
        Only the most recent changes are kept. Effective dates come from the
        tariff schedule and can be earlier than when the product was added.
      </p>

      {loading ? (
        <div className='ih-tariff-changelog-empty'>
          <span
            className='ih-spinner'
            style={{ width: 20, height: 20, borderWidth: 2 }}
          />
          <strong>Loading tariff changes</strong>
          <span>Retrieving this product&apos;s archived cost snapshots.</span>
        </div>
      ) : error ? (
        <div className='ih-tariff-changelog-empty'>
          <AlertCircle className='size-5 text-destructive' />
          <strong>Unable to load tariff history</strong>
          <span>{error}</span>
          <button
            type='button'
            className='inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-input bg-background hover:bg-accent text-xs'
            onClick={() => setRetryVersion((version) => version + 1)}
          >
            <RefreshCw className='size-3' /> Try again
          </button>
        </div>
      ) : entries.length === 0 ? (
        <div className='ih-tariff-changelog-empty'>
          <History className='size-5' />
          <strong>No duty or tariff changes recorded</strong>
          <span>
            Historical cost snapshots will appear here when this product
            changes.
          </span>
        </div>
      ) : (
        <div className='ih-tariff-changelog-list'>
          {entries.map((entry) => {
            const expanded = expandedIds.has(entry.id);
            const panelId = `${entry.id}-breakdown`;
            return (
              <article key={entry.id} className='ih-tariff-change-card'>
                <div className='ih-tariff-change-heading'>{entry.heading}</div>
                <div className='ih-tariff-change-values'>
                  <span className='ih-tariff-change-previous'>
                    {entry.previousValue}
                  </span>
                  <span aria-hidden='true'>→</span>
                  <span className='ih-tariff-change-current'>
                    {entry.currentValue}
                  </span>
                </div>
                {entry.effectiveAt ? (
                  <div className='ih-tariff-change-date'>
                    <Clock className='size-3.5' />
                    <span>Effective:</span>
                    <strong>{formatCalendarDate(entry.effectiveAt)}</strong>
                  </div>
                ) : (
                  <div className='ih-tariff-change-date'>
                    <Clock className='size-3.5' />
                    <span>Changed Date:</span>
                    <strong>{formatDate(entry.changedAt)}</strong>
                  </div>
                )}

                <div className='ih-tariff-change-divider' />
                <div className='ih-tariff-breakdown-heading'>
                  Previous Breakdown
                </div>
                <TariffSnapshotRow
                  label='Base duty cost'
                  value={formatTariffHistoryCurrency(
                    entry.snapshot.baseDutyCost,
                  )}
                />
                <TariffSnapshotRow
                  label='Total duty cost'
                  value={formatTariffHistoryCurrency(
                    entry.snapshot.totalDutyCost,
                  )}
                />
                <TariffSnapshotRow
                  label='Total cost'
                  value={formatTariffHistoryCurrency(
                    feeFreeTotalCost(entry.snapshot) ?? null,
                  )}
                  emphasize
                />

                <button
                  type='button'
                  className='ih-tariff-breakdown-toggle'
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  onClick={() => toggleBreakdown(entry.id)}
                >
                  <ChevronDown
                    className={cn(
                      'size-3.5',
                      expanded && 'ih-tariff-chevron-expanded',
                    )}
                  />
                  {expanded ? 'Hide full breakdown' : 'Show full breakdown'}
                </button>

                {expanded && (
                  <div id={panelId} className='ih-tariff-breakdown-details'>
                    <TariffSnapshotRow
                      label='Base duty rate'
                      value={formatTariffHistoryRate(
                        entry.snapshot.baseDutyRate,
                      )}
                    />
                    <TariffSnapshotRow
                      label='Additional duty rate'
                      value={formatTariffHistoryRate(
                        entry.snapshot.additionalDutyRate,
                      )}
                    />
                    <TariffSnapshotRow
                      label='Additional duty cost'
                      value={formatTariffHistoryCurrency(
                        entry.snapshot.additionalDutyCost,
                      )}
                    />
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </aside>
  );
}

function TariffSnapshotRow({
  label,
  value,
  emphasize = false,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <div className='ih-tariff-snapshot-row'>
      <span>{label}</span>
      <strong className={cn(emphasize && 'is-emphasized')}>{value}</strong>
    </div>
  );
}

function ParentChildPanel({
  product,
  detail,
}: {
  product: ImportProduct;
  detail: ProductDetail;
}) {
  const readOnly = useImportHubReadOnly();
  type Row = {
    id: string;
    kind: 'parent' | 'child';
    name: string;
    hts: string;
    origin: string;
    percent?: number;
  };
  const [rows, setRows] = useState<Row[]>(() => [
    {
      id: `self-${product.id}`,
      kind: 'parent',
      name: product.name,
      hts: product.htsCode || '—',
      origin: product.countryOfOrigin || '—',
    },
    ...detail.childComponents.map((c, i) => ({
      id: `child-${i}`,
      kind: 'child' as const,
      name: c.name,
      hts: c.hts,
      origin: c.origin,
      percent: c.percent,
    })),
  ]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmUnlinkId, setConfirmUnlinkId] = useState<string | null>(null);

  const available = useMemo(
    () =>
      IMPORT_PRODUCTS.filter(
        (p) =>
          p.importDatabaseId === product.importDatabaseId &&
          p.id !== product.id &&
          !rows.some((r) => r.id === p.id),
      ),
    [product.id, product.importDatabaseId, rows],
  );

  const handleAdd = (
    p: ImportProduct,
    kind: 'parent' | 'child',
    qty?: number,
  ) => {
    setRows((prev) => [
      ...prev,
      {
        id: p.id,
        kind,
        name: p.name,
        hts: p.htsCode || '—',
        origin: p.countryOfOrigin || '—',
        percent: kind === 'child' ? qty ?? 100 : undefined,
      },
    ]);
    setPickerOpen(false);
  };

  const unlink = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    setConfirmUnlinkId(null);
  };

  const hasChildren = rows.some((r) => r.kind === 'child');

  return (
    <div className='elms-card'>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-sm font-semibold flex items-center gap-1.5'>
          <Layers className='size-3.5' /> Parent / Child Products
        </h3>
        <button
          type='button'
          onClick={readOnly ? undefined : () => setPickerOpen(true)}
          disabled={readOnly}
          title={readOnly ? 'Requires a Business license' : undefined}
          className='inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-input bg-background hover:bg-accent text-xs disabled:opacity-60 disabled:cursor-not-allowed'
        >
          {readOnly ? <Lock className='size-3' /> : <Plus className='size-3' />}{' '}
          Link Product
        </button>
      </div>
      {!hasChildren && rows.length <= 1 ? (
        <div className='text-xs text-muted-foreground italic space-y-2'>
          <p>No child components — this product is a finished good.</p>
          <button
            type='button'
            onClick={readOnly ? undefined : () => setPickerOpen(true)}
            disabled={readOnly}
            title={readOnly ? 'Requires a Business license' : undefined}
            className='inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-input bg-background hover:bg-accent text-xs not-italic text-foreground disabled:opacity-60 disabled:cursor-not-allowed'
          >
            {readOnly ? (
              <Lock className='size-3' />
            ) : (
              <Plus className='size-3' />
            )}{' '}
            Link a parent or child product
          </button>
        </div>
      ) : (
        <ul className='space-y-2'>
          {rows.map((r) => {
            const isSelf = r.id === `self-${product.id}`;
            return (
              <li
                key={r.id}
                className='group rounded-md bg-[var(--bg-surface)] p-2'
              >
                <div className='flex items-center justify-between gap-2'>
                  <div className='text-[10px] uppercase tracking-wider text-muted-foreground'>
                    {r.kind}
                  </div>
                  <div className='flex items-center gap-2'>
                    {typeof r.percent === 'number' && (
                      <span className='text-[10px] tabular-nums text-muted-foreground'>
                        {r.percent}%
                      </span>
                    )}
                    {!isSelf && (
                      <button
                        type='button'
                        onClick={
                          readOnly ? undefined : () => setConfirmUnlinkId(r.id)
                        }
                        disabled={readOnly}
                        className='opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive disabled:cursor-not-allowed'
                        title={
                          readOnly ? 'Requires a Business license' : 'Unlink'
                        }
                      >
                        {readOnly ? (
                          <Lock className='size-3.5' />
                        ) : (
                          <X className='size-3.5' />
                        )}
                      </button>
                    )}
                  </div>
                </div>
                <div className='text-xs font-medium mt-0.5'>{r.name}</div>
                <div className='text-[10px] text-muted-foreground font-mono'>
                  {r.hts} · {r.origin}
                </div>
                {confirmUnlinkId === r.id && (
                  <div className='mt-2 flex items-center justify-between gap-2 rounded bg-[var(--row-hover)] p-1.5'>
                    <span className='text-[11px] text-muted-foreground'>
                      Unlink this product?
                    </span>
                    <div className='flex items-center gap-1'>
                      <button
                        type='button'
                        onClick={() => setConfirmUnlinkId(null)}
                        className='h-6 px-2 text-[11px] rounded border border-input bg-background hover:bg-accent'
                      >
                        Cancel
                      </button>
                      <button
                        type='button'
                        onClick={() => unlink(r.id)}
                        disabled={readOnly}
                        title={
                          readOnly ? 'Requires a Business license' : undefined
                        }
                        className='h-6 px-2 text-[11px] rounded bg-destructive text-destructive-foreground hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed'
                      >
                        Unlink
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {pickerOpen && (
        <ProductPickerDialog
          available={available}
          onClose={() => setPickerOpen(false)}
          onSelect={handleAdd}
        />
      )}
    </div>
  );
}

function ProductPickerDialog({
  available,
  onClose,
  onSelect,
}: {
  available: ImportProduct[];
  onClose: () => void;
  onSelect: (p: ImportProduct, kind: 'parent' | 'child', qty?: number) => void;
}) {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<'parent' | 'child'>('child');
  const [qty, setQty] = useState<string>('100');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const filtered = available.filter(
    (p) =>
      !q ||
      p.name.toLowerCase().includes(q.toLowerCase()) ||
      p.sku.toLowerCase().includes(q.toLowerCase()),
  );
  const selected = filtered.find((p) => p.id === selectedId) ?? null;
  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center p-4'
      onClick={onClose}
    >
      <div className='absolute inset-0 bg-black/50' />
      <div
        className='relative w-full max-w-lg bg-card border border-[var(--edge-separator)] rounded-lg shadow-lg overflow-hidden'
        onClick={(e) => e.stopPropagation()}
      >
        <header className='flex items-center justify-between px-4 py-3 border-b border-[var(--edge-separator)]'>
          <h3 className='text-sm font-semibold'>Link product</h3>
          <button onClick={onClose} className='p-1 rounded hover:bg-accent'>
            <X className='size-4' />
          </button>
        </header>
        <div className='p-4 space-y-3'>
          <div className='flex items-center gap-2 text-xs'>
            <span className='text-muted-foreground'>Relationship:</span>
            {(['parent', 'child'] as const).map((k) => (
              <button
                key={k}
                type='button'
                onClick={() => setKind(k)}
                className={cn(
                  'h-7 px-2.5 rounded-md border text-xs capitalize',
                  kind === k
                    ? 'border-primary bg-primary/10 text-foreground'
                    : 'border-input bg-background hover:bg-accent',
                )}
              >
                {k}
              </button>
            ))}
            {kind === 'child' && (
              <label className='ml-auto inline-flex items-center gap-1.5 text-[11px] text-muted-foreground'>
                Qty %
                <input
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  className='w-14 h-7 px-2 rounded-md border border-input bg-background text-xs tabular-nums'
                />
              </label>
            )}
          </div>
          <div className='relative'>
            <Search className='absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground' />
            <input
              autoFocus
              placeholder='Search products in this database…'
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className='w-full h-8 pl-7 pr-2 rounded-md border border-input bg-background text-xs'
            />
          </div>
          <ul className='max-h-64 overflow-auto divide-y divide-[var(--edge-separator)] rounded-md border border-[var(--edge-divider)]'>
            {filtered.length === 0 && (
              <li className='p-3 text-xs text-muted-foreground italic'>
                No matching products.
              </li>
            )}
            {filtered.map((p) => (
              <li key={p.id}>
                <button
                  type='button'
                  onClick={() => setSelectedId(p.id)}
                  className={cn(
                    'w-full text-left px-3 py-2 hover:bg-[var(--row-hover)] text-xs',
                    selectedId === p.id && 'bg-[var(--row-hover)]',
                  )}
                >
                  <div className='font-medium'>{p.name}</div>
                  <div className='text-[11px] text-muted-foreground font-mono'>
                    {p.sku} · {p.htsCode || 'no HTS'} · {p.countryOfOrigin}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <footer className='flex items-center justify-end gap-2 px-4 py-3 border-t border-[var(--edge-separator)]'>
          <button
            onClick={onClose}
            className='h-8 px-3 rounded-md border border-input bg-background hover:bg-accent text-xs'
          >
            Cancel
          </button>
          <button
            type='button'
            disabled={!selected}
            onClick={() =>
              selected &&
              onSelect(
                selected,
                kind,
                kind === 'child' ? Number(qty) || undefined : undefined,
              )
            }
            className='h-8 px-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-medium disabled:opacity-50'
          >
            Link
          </button>
        </footer>
      </div>
    </div>
  );
}

function LinkedEntitiesPanel({
  initial,
}: {
  initial: { entity: ImportEntity; role: EntityLinkRole }[];
}) {
  const readOnly = useImportHubReadOnly();
  const [links, setLinks] = useState(initial);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const available = useMemo(
    () =>
      IMPORT_ENTITIES.filter((e) => !links.some((l) => l.entity.id === e.id)),
    [links],
  );

  const add = (entities: ImportEntity[], role: EntityLinkRole) => {
    setLinks((prev) => [
      ...prev,
      ...entities.map((e) => ({ entity: e, role })),
    ]);
    setPickerOpen(false);
  };
  const remove = (id: string) => {
    setLinks((prev) => prev.filter((l) => l.entity.id !== id));
    setConfirmId(null);
  };
  const setRole = (id: string, role: EntityLinkRole) => {
    setLinks((prev) =>
      prev.map((l) => (l.entity.id === id ? { ...l, role } : l)),
    );
  };

  return (
    <div className='elms-card'>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-sm font-semibold'>Linked entities</h3>
        <button
          type='button'
          onClick={readOnly ? undefined : () => setPickerOpen(true)}
          disabled={readOnly}
          title={readOnly ? 'Requires a Business license' : undefined}
          className='inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-input bg-background hover:bg-accent text-xs disabled:opacity-60 disabled:cursor-not-allowed'
        >
          {readOnly ? <Lock className='size-3' /> : <Plus className='size-3' />}{' '}
          Link Entity
        </button>
      </div>
      {links.length === 0 ? (
        <div className='text-xs text-muted-foreground italic space-y-2'>
          <p>No entities linked.</p>
          <button
            type='button'
            onClick={readOnly ? undefined : () => setPickerOpen(true)}
            disabled={readOnly}
            title={readOnly ? 'Requires a Business license' : undefined}
            className='inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-input bg-background hover:bg-accent text-xs not-italic text-foreground disabled:opacity-60 disabled:cursor-not-allowed'
          >
            {readOnly ? (
              <Lock className='size-3' />
            ) : (
              <Plus className='size-3' />
            )}{' '}
            Link an entity
          </button>
        </div>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-2'>
          {links.map(({ entity, role }) => {
            const Icon = ROLE_ICON(role);
            const tierTone =
              entity.riskTier === 'high'
                ? 'badge-danger'
                : entity.riskTier === 'medium'
                ? 'badge-warning'
                : 'badge-success';
            return (
              <div
                key={entity.id}
                className='group rounded-md bg-[var(--bg-surface)] p-2 relative'
              >
                <div className='flex items-center justify-between gap-2'>
                  <div className='flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground'>
                    <Icon className='size-3' />
                    <select
                      value={role}
                      disabled={readOnly}
                      onChange={(e) =>
                        setRole(entity.id, e.target.value as EntityLinkRole)
                      }
                      className='bg-transparent border-0 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground cursor-pointer focus:outline-none disabled:cursor-not-allowed'
                    >
                      {ENTITY_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className='flex items-center gap-1.5'>
                    <span
                      className={cn(
                        'inline-flex items-center text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-0.5',
                        tierTone,
                      )}
                    >
                      {entity.riskTier}
                    </span>
                    <button
                      type='button'
                      onClick={
                        readOnly ? undefined : () => setConfirmId(entity.id)
                      }
                      disabled={readOnly}
                      className='opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive disabled:cursor-not-allowed'
                      title={
                        readOnly ? 'Requires a Business license' : 'Unlink'
                      }
                    >
                      {readOnly ? (
                        <Lock className='size-3.5' />
                      ) : (
                        <X className='size-3.5' />
                      )}
                    </button>
                  </div>
                </div>
                <div className='mt-1 text-xs font-medium'>{entity.name}</div>
                <div className='text-[10px] text-muted-foreground'>
                  {entity.country}
                </div>
                {confirmId === entity.id && (
                  <div className='mt-2 flex items-center justify-between gap-2 rounded bg-[var(--row-hover)] p-1.5'>
                    <span className='text-[11px] text-muted-foreground'>
                      Unlink?
                    </span>
                    <div className='flex items-center gap-1'>
                      <button
                        type='button'
                        onClick={() => setConfirmId(null)}
                        className='h-6 px-2 text-[11px] rounded border border-input bg-background hover:bg-accent'
                      >
                        Cancel
                      </button>
                      <button
                        type='button'
                        onClick={() => remove(entity.id)}
                        disabled={readOnly}
                        title={
                          readOnly ? 'Requires a Business license' : undefined
                        }
                        className='h-6 px-2 text-[11px] rounded bg-destructive text-destructive-foreground hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed'
                      >
                        Unlink
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {pickerOpen && (
        <EntityPickerDialog
          available={available}
          onClose={() => setPickerOpen(false)}
          onLink={add}
        />
      )}
    </div>
  );
}

function EntityPickerDialog({
  available,
  onClose,
  onLink,
}: {
  available: ImportEntity[];
  onClose: () => void;
  onLink: (entities: ImportEntity[], role: EntityLinkRole) => void;
}) {
  const [q, setQ] = useState('');
  const [role, setRole] = useState<EntityLinkRole>('Supplier');
  const [filterRole, setFilterRole] = useState<'all' | ImportEntity['role']>(
    'all',
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const filtered = available.filter((e) => {
    if (filterRole !== 'all' && e.role !== filterRole) return false;
    if (
      q &&
      !e.name.toLowerCase().includes(q.toLowerCase()) &&
      !e.country.toLowerCase().includes(q.toLowerCase())
    )
      return false;
    return true;
  });
  const toggle = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center p-4'
      onClick={onClose}
    >
      <div className='absolute inset-0 bg-black/50' />
      <div
        className='relative w-full max-w-lg bg-card border border-[var(--edge-separator)] rounded-lg shadow-lg overflow-hidden'
        onClick={(e) => e.stopPropagation()}
      >
        <header className='flex items-center justify-between px-4 py-3 border-b border-[var(--edge-separator)]'>
          <h3 className='text-sm font-semibold'>Link entity</h3>
          <button onClick={onClose} className='p-1 rounded hover:bg-accent'>
            <X className='size-4' />
          </button>
        </header>
        <div className='p-4 space-y-3'>
          <div className='flex items-center gap-2 text-xs flex-wrap'>
            <span className='text-muted-foreground'>Role:</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as EntityLinkRole)}
              className='h-7 rounded-md border border-input bg-background px-2 text-xs'
            >
              {ENTITY_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <span className='text-muted-foreground ml-auto'>Filter:</span>
            <select
              value={filterRole}
              onChange={(e) =>
                setFilterRole(e.target.value as typeof filterRole)
              }
              className='h-7 rounded-md border border-input bg-background px-2 text-xs'
            >
              <option value='all'>All types</option>
              <option value='supplier'>Suppliers</option>
              <option value='manufacturer'>Manufacturers</option>
              <option value='consignee'>Consignees</option>
            </select>
          </div>
          <div className='relative'>
            <Search className='absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground' />
            <input
              autoFocus
              placeholder='Search by name or country…'
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className='w-full h-8 pl-7 pr-2 rounded-md border border-input bg-background text-xs'
            />
          </div>
          <ul className='max-h-64 overflow-auto divide-y divide-[var(--edge-separator)] rounded-md border border-[var(--edge-divider)]'>
            {filtered.length === 0 && (
              <li className='p-3 text-xs text-muted-foreground italic'>
                No matching entities.
              </li>
            )}
            {filtered.map((e) => {
              const checked = selectedIds.includes(e.id);
              return (
                <li key={e.id}>
                  <label
                    className={cn(
                      'flex items-center gap-2 px-3 py-2 hover:bg-[var(--row-hover)] cursor-pointer text-xs',
                      checked && 'bg-[var(--row-hover)]',
                    )}
                  >
                    <input
                      type='checkbox'
                      checked={checked}
                      onChange={() => toggle(e.id)}
                      className='size-3.5'
                    />
                    <div className='flex-1 min-w-0'>
                      <div className='font-medium'>{e.name}</div>
                      <div className='text-[11px] text-muted-foreground capitalize'>
                        {e.role} · {e.country}
                      </div>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
        <footer className='flex items-center justify-end gap-2 px-4 py-3 border-t border-[var(--edge-separator)]'>
          <button
            onClick={onClose}
            className='h-8 px-3 rounded-md border border-input bg-background hover:bg-accent text-xs'
          >
            Cancel
          </button>
          <button
            type='button'
            disabled={selectedIds.length === 0}
            onClick={() =>
              onLink(
                available.filter((e) => selectedIds.includes(e.id)),
                role,
              )
            }
            className='h-8 px-3 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-medium disabled:opacity-50'
          >
            Link {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
          </button>
        </footer>
      </div>
    </div>
  );
}
